import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import * as XLSX from "xlsx";
import { Search, RefreshCw, Package2, Factory, Boxes, X, Upload } from "lucide-react";
import { addDays, differenceInCalendarDays, format, parseISO } from "date-fns";
import Select from "react-select";
import "./planning-workspace.tailwind.css";
import "./planning-workspace-view.css";
import { ensureSpvpAspScript } from "./load-spvp-asp";
import {
  fillAspTemplatePreservingStyles,
  uint8ArrayToBase64,
} from "./asp-template-fill-exceljs";
import { PlannerService } from "../../service/api/planner-service";
import { SpvpAspService } from "../../service/api/spvp-asp-service";
import { CustomerService } from "../../service/api/customer-service";

const HORIZON_DAYS = 365;
/** Max pallet positions per truck; shipment qty (each) above Pallet Qty × this triggers warning. */
const SHIPMENT_TRUCK_PALLET_SLOTS = 52;
const SHIPMENT_TRUCK_OVERFLOW_MESSAGE = "This quantity will not fit into the truck. Do you want to proceed?";
const ASP_MAPPING_STORAGE_KEY = "SPVP_ASP_COLUMN_MAPPING_PROFILES";
const CUSTOM_FIELD_PREFIX = "custom:";
/** Master template in OneDrive ASP_Forms_Raintels — never overwrite this file. */
const COMMON_ASP_TEMPLATE_FILE_NAME = "COMMON_ASP_Form.xlsx";
/** OneDrive folder for filled copies only. */
const LOADED_ASP_FORMS_FOLDER = "Loaded_ASP_Forms_Raintels";
const LOW_STOCK = 120;
const CRITICAL_STOCK = 40;

/** react-select: match other planner filters (height, radius) and keep menu above nested scroll areas */
const PLANNER_CUSTOMER_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    fontSize: "0.8125rem",
    borderRadius: "0.85rem",
    borderColor: state.isFocused ? "#64748b" : "#e2e8f0",
    boxShadow: state.isFocused ? "0 0 0 1px #94a3b8" : "none",
    backgroundColor: state.isDisabled ? "#f8fafc" : "#fff",
    "&:hover": { borderColor: "#cbd5e1" },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 10050 }),
  menu: (base) => ({ ...base, zIndex: 10050 }),
};

/** Shown on Load Board “Shipment Form” until supplier profile comes from configuration/API. */
const DEFAULT_SUPPLIER_SHIP_FROM = {
  supplierName: "Chemplast, Inc.",
  supplierNumber: "79",
  addressLineOne: "1002 Texas Parkway",
  addressLineTwo: "",
  addressLineThree: "",
  city: "Stafford",
  state: "TX",
  zipCode: "77477",
  countryOfOrigin: "USA",
  phone: "",
  phoneExtension: "",
  freightTerms: "",
};

const ASP_FORM_EMAIL = "logistics@chemplastinc.com";

const DOCK_APPOINTMENT_NOTE =
  "NOTE: A Dock Appointment is required for all Shipments. Drivers must park their trailers in a receiving dock door and are not authorized to park on the streets adjacent to our facilities. If a dock door is not immediately available upon your arrival, you will be directed to an approved waiting location until your door becomes available.";

/**
 * Default 3-column shipment form (Load Board modal + Excel block).
 * Per-customer overrides: store JSON in dbo.Customer (`AspLoadBoardConfigJson`) with optional `sections` (shipment form),
 * optional `columns` / `headerRows` (same shape as load board `schemaJson`), plus `supplierDefaults`, etc.
 * Schema from planner/loadboard-schema wins when it defines `columns`; otherwise customer JSON `columns` are used.
 *
 * Field `key` values must match keys returned by computeLoadBoardShipmentFormPropsForIndex.
 * Special field types: dockNote | shipToLines | aspEmail
 */
const DEFAULT_SHIPMENT_FORM_SECTIONS = [
  {
    id: "shipFrom",
    eyebrow: "Ship from location",
    title: "To be filled in by Supplier",
    excelSectionHeader: "SHIP FROM LOCATION (to be filled in by Supplier)",
    fields: [
      { key: "creationDate", label: "Creation Date" },
      { key: "createdBy", label: "Created By", highlight: true },
      { key: "shipmentReadyDate", label: "Shipment Ready Date" },
      { key: "shipmentReadyTime", label: "Shipment Ready Time" },
      { key: "shippingDockHours", label: "Shipping Dock Hours", highlight: true },
      { key: "planDeliveryDate", label: "Plan Delivery Date" },
      { key: "planDeliveryTime", label: "Plan Delivery Time" },
      { key: "supplierName", label: "Supplier Name" },
      { key: "supplierNumber", label: "Supplier Number", highlight: true },
      { key: "addressLineOne", label: "Address Line One" },
      { key: "addressLineTwo", label: "Address Line Two" },
      { key: "addressLineThree", label: "Address Line Three" },
      { key: "supplierCity", label: "City" },
      { key: "supplierState", label: "State (eg TX)" },
      { key: "supplierZip", label: "Zip Code" },
      { key: "countryOfOrigin", label: "Country of Origin (eg USA)" },
      { key: "phone", label: "Phone" },
      { key: "phoneExtension", label: "Phone Extension" },
      { key: "freightTerms", label: "Freight Terms" },
    ],
  },
  {
    id: "shippingInstructions",
    eyebrow: "Shipping instructions",
    title: "To be filled in by Customer",
    excelSectionHeader: "SHIPPING INSTRUCTIONS (to be filled in by Customer)",
    fields: [
      { key: "shipmentNumber", label: "Shipment #", highlight: true },
      { key: "rev", label: "Rev #" },
      { key: "carrierName", label: "Carrier Name" },
      { key: "carrierContact", label: "Carrier Contact" },
      { key: "carrierPhone", label: "Carrier Phone" },
      { key: "totalBoxes", label: "Total Boxes" },
      { key: "totalPallets", label: "Total Pallets" },
      { key: "totalWeight", label: "Total Weight" },
      { type: "dockNote" },
    ],
  },
  {
    id: "destination",
    eyebrow: "Destination",
    title: "Ship To Information",
    excelSectionHeader: "SHIP TO INFORMATION",
    fields: [{ type: "shipToLines", label: "Ship To:" }, { type: "aspEmail" }],
  },
];

/** When false, skip GET planner/customer-asp-loadboard-config and use DEFAULT_SHIPMENT_FORM_SECTIONS in the Load board header. */
const LOAD_BOARD_USE_CUSTOMER_ASP_CONFIG_API = false;

function getDefaultLoadBoardShipmentFormLayoutRaw() {
  return {
    sections: DEFAULT_SHIPMENT_FORM_SECTIONS,
    supplierDefaults: DEFAULT_SUPPLIER_SHIP_FROM,
    aspFormEmail: ASP_FORM_EMAIL,
    dockAppointmentNote: DOCK_APPOINTMENT_NOTE,
  };
}

/**
 * Merges API `shipmentForm` / Customer `AspLoadBoardConfigJson` only.
 * Returns null if there is no usable layout (UI must not fall back to a hardcoded Chemplast default form).
 */
function normalizeShipmentFormLayoutFromApi(raw) {
  if (!raw || typeof raw !== "object") return null;
  const sections = Array.isArray(raw.sections) && raw.sections.length > 0 ? raw.sections : null;
  if (!sections) return null;
  return {
    supplierDefaults: { ...DEFAULT_SUPPLIER_SHIP_FROM, ...(raw.supplierDefaults || {}) },
    aspFormEmail: raw.aspFormEmail != null && String(raw.aspFormEmail).trim() !== "" ? String(raw.aspFormEmail).trim() : ASP_FORM_EMAIL,
    dockAppointmentNote:
      raw.dockAppointmentNote != null && String(raw.dockAppointmentNote).trim() !== ""
        ? String(raw.dockAppointmentNote)
        : DOCK_APPOINTMENT_NOTE,
    sections,
  };
}

/** Mirrors spvp-asp-shared.js — keep keys/labels aligned for workbook fill compatibility. */
const SYSTEM_FIELDS_FOR_MAPPING = [
  { key: "lineNumber", label: "Line #", scope: "line", group: "Line items (per part)" },
  { key: "partNumber", label: "Part Number", scope: "line", group: "Line items (per part)" },
  { key: "partDescription", label: "Description", scope: "line", group: "Line items (per part)" },
  { key: "poNumber", label: "PO Number", scope: "line", group: "Line items (per part)" },
  { key: "numberOfBoxes", label: "Number of Boxes", scope: "line", group: "Line items (per part)" },
  { key: "qtyPerBox", label: "Qty Per Box", scope: "line", group: "Line items (per part)" },
  { key: "palletsQty", label: "Pallet(s) Qnty", scope: "line", group: "Line items (per part)" },
  { key: "plannedShipQuantity", label: "Planned Ship Quantity", scope: "line", group: "Line items (per part)" },
  { key: "uom", label: "UOM", scope: "line", group: "Line items (per part)" },
  { key: "mixedPalletYn", label: "Mixed Pallet Y / N", scope: "line", group: "Line items (per part)" },
  { key: "dimensionsLWH", label: "Dimensions L x W x H", scope: "line", group: "Line items (per part)" },
  { key: "totalShipWeight", label: "Total Ship Weight", scope: "line", group: "Line items (per part)" },
  { key: "totalCube", label: "Total Cube", scope: "line", group: "Line items (per part)" },
  { key: "deleteYn", label: "Delete (Y/N)", scope: "line", group: "Line items (per part)" },
  { key: "creationDate", label: "CREATION DATE", scope: "shipment", group: "Scheduling & supplier" },
  { key: "createdBy", label: "CREATED BY", scope: "shipment", group: "Scheduling & supplier" },
  { key: "shipmentReadyDate", label: "SHIPMENT READY DATE", scope: "shipment", group: "Scheduling & supplier" },
  { key: "shipmentReadyTime", label: "SHIPMENT READY TIME", scope: "shipment", group: "Scheduling & supplier" },
  { key: "shippingDockHours", label: "Shipping Dock Hours", scope: "shipment", group: "Scheduling & supplier" },
  { key: "planDeliveryDate", label: "PLAN DELIVERY DATE", scope: "shipment", group: "Scheduling & supplier" },
  { key: "planDeliveryTime", label: "PLAN DELIVERY TIME", scope: "shipment", group: "Scheduling & supplier" },
  { key: "supplierName", label: "Supplier Name", scope: "shipment", group: "Scheduling & supplier" },
  { key: "supplierNumber", label: "Supplier Number", scope: "shipment", group: "Scheduling & supplier" },
  { key: "addressLineOne", label: "Address Line One", scope: "shipment", group: "Scheduling & supplier" },
  { key: "addressLineTwo", label: "Address Line Two", scope: "shipment", group: "Scheduling & supplier" },
  { key: "addressLineThree", label: "Address Line Three", scope: "shipment", group: "Scheduling & supplier" },
  { key: "city", label: "City", scope: "shipment", group: "Scheduling & supplier" },
  { key: "state", label: "State (eg TX)", scope: "shipment", group: "Scheduling & supplier" },
  { key: "zipCode", label: "Zip Code", scope: "shipment", group: "Scheduling & supplier" },
  { key: "countryOfOrigin", label: "Country of Origin (eg USA)", scope: "shipment", group: "Scheduling & supplier" },
  { key: "phone", label: "Phone", scope: "shipment", group: "Scheduling & supplier" },
  { key: "phoneExtension", label: "Phone Extension", scope: "shipment", group: "Scheduling & supplier" },
  { key: "freightTerms", label: "Freight Terms", scope: "shipment", group: "Scheduling & supplier" },
  { key: "shipmentNumber", label: "Shipment #", scope: "shipment", group: "Carrier & totals" },
  { key: "carrierName", label: "Carrier Name", scope: "shipment", group: "Carrier & totals" },
  { key: "carrierContact", label: "Carrier Contact", scope: "shipment", group: "Carrier & totals" },
  { key: "carrierPhone", label: "Carrier Phone", scope: "shipment", group: "Carrier & totals" },
  { key: "totalBoxes", label: "Total Boxes", scope: "shipment", group: "Carrier & totals" },
  { key: "totalPallets", label: "Total Pallets", scope: "shipment", group: "Carrier & totals" },
  { key: "totalWeight", label: "Total Weight", scope: "shipment", group: "Carrier & totals" },
  { key: "shipToLine1", label: "Ship-To line 1", scope: "shipment", group: "Ship-To" },
  { key: "shipToLine2", label: "Ship-To line 2", scope: "shipment", group: "Ship-To" },
  { key: "shipToLine3", label: "Ship-To line 3", scope: "shipment", group: "Ship-To" },
  { key: "shipmentReleaseId", label: "Shipment ReleaseId", scope: "shipment", group: "Identifiers" },
  { key: "customerNumber", label: "Customer #", scope: "shipment", group: "Identifiers" },
  { key: "dueDate", label: "Due date", scope: "line", group: "Other" },
  { key: "shipDate", label: "Shipment date", scope: "shipment", group: "Other" },
  { key: "destination", label: "Destination", scope: "line", group: "Other" },
  { key: "source", label: "Source", scope: "line", group: "Other" },
  { key: "shipTo", label: "Ship-To", scope: "shipment", group: "Other" },
  { key: "quantity", label: "Quantity (generic)", scope: "line", group: "Other" },
  { key: "notes", label: "Notes / comments", scope: "line", group: "Other" },
];

/** Default columns for Load Load Board when API has no saved schema yet (matches typical ASP line grid). */
const DEFAULT_SUPPLIER_SHIP_PLAN_LOAD_BOARD_COLUMNS = [
  { group: "SUPPLIER SHIP PLAN REQUEST", label: "Line #", fieldKey: "lineNumber", align: "center" },
  { group: "SUPPLIER SHIP PLAN REQUEST", label: "Part Number", fieldKey: "partNumber", align: "left" },
  { group: "SUPPLIER SHIP PLAN REQUEST", label: "Description", fieldKey: "partDescription", align: "left" },
  { group: "SUPPLIER SHIP PLAN REQUEST", label: "PO Number", fieldKey: "poNumber", align: "left" },
  { group: "SUPPLIER SHIP PLAN REQUEST", label: "Number of Boxes", fieldKey: "numberOfBoxes", align: "right" },
  { group: "SUPPLIER SHIP PLAN REQUEST", label: "Qty Per Box", fieldKey: "qtyPerBox", align: "right" },
  { group: "SUPPLIER SHIP PLAN REQUEST", label: "Pallet(s) Qnty", fieldKey: "palletsQty", align: "right" },
  { group: "SUPPLIER SHIP PLAN REQUEST", label: "Planned Ship Quantity", fieldKey: "plannedShipQuantity", align: "right" },
  { group: "SUPPLIER SHIP PLAN REQUEST", label: "UOM", fieldKey: "uom", align: "center" },
  { group: "SUPPLIER SHIP PLAN REQUEST", label: "Mixed Pallet Y / N", fieldKey: "mixedPalletYn", align: "center" },
  { group: "SUPPLIER SHIP PLAN REQUEST", label: "Dimensions L x W x H", fieldKey: "dimensionsLWH", align: "left" },
  { group: "SUPPLIER SHIP PLAN REQUEST", label: "Total Ship Weight", fieldKey: "totalShipWeight", align: "right" },
  { group: "SUPPLIER SHIP PLAN REQUEST", label: "Total Cube", fieldKey: "totalCube", align: "right" },
  { group: "SUPPLIER SHIP PLAN REQUEST", label: "Delete (Y/N)", fieldKey: "deleteYn", align: "center" },
  { group: "APPROVAL", label: "Approved Ship Quantity", fieldKey: "approvedShipQuantity", align: "right" },
  { group: "APPROVAL", label: "Approved Delivery Date", fieldKey: "approvedDeliveryDate", align: "center" },
  { group: "APPROVAL", label: "Approved Delivery Time", fieldKey: "approvedDeliveryTime", align: "center" },
  { group: "APPROVAL", label: "Planner Signature", fieldKey: "plannerSignature", align: "left" },
  { group: "APPROVAL", label: "Shipment Released", fieldKey: "shipmentReleased", align: "center" },
];

const LOAD_BOARD_HIDDEN_COLUMN_KEYS = new Set(["totalCube", "deleteYn"]);
const LOAD_BOARD_READONLY_FIELD_KEYS = new Set([
  "lineNumber",
  "partNumber",
  "partDescription",
  "poNumber",
  "totalShipWeight",
  "shipmentReleased",
]);

function filterLoadBoardTableColumns(columns) {
  const list = Array.isArray(columns) ? columns : [];
  return list.filter((col) => {
    const key = String(col?.fieldKey || col?.key || "").trim();
    return key && !LOAD_BOARD_HIDDEN_COLUMN_KEYS.has(key);
  });
}

function createBaseDates() {
  const start = new Date();
  start.setDate(start.getDate() - start.getDay() + 1);
  start.setHours(12, 0, 0, 0);
  return Array.from({ length: HORIZON_DAYS }, (_, i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

const baseDates = createBaseDates();

function padSeries(arr, len = HORIZON_DAYS) {
  const safe = Array.isArray(arr) ? [...arr] : [];
  while (safe.length < len) safe.push(0);
  return safe.slice(0, len);
}

function pad(n) {
  return n < 10 ? `0${n}` : String(n);
}

function fmtDate(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fmtHeader(d) {
  const wd = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d.getDay()];
  return `${wd} ${pad(d.getMonth() + 1)}/${pad(d.getDate())}`;
}

/** True if projected on-hand dips below zero along the demand matrix date columns (same order as the grid). */
function demandPartHasShortageInWindow(part, datesOrdered) {
  if (!part || !Array.isArray(datesOrdered) || !datesOrdered.length) return false;
  const onHand = part.onHand;
  if (!Number.isFinite(onHand)) return false;
  if (onHand < 0) return true;
  let cumulativeEach = 0;
  for (let i = 0; i < datesOrdered.length; i += 1) {
    const iso = fmtDate(datesOrdered[i]);
    cumulativeEach += Number(part.qtyByIso?.[iso] || 0);
    if (onHand - cumulativeEach < 0) return true;
  }
  return false;
}

function fmtDueLabel(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtUsDateFromIso(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(String(iso))) return "—";
  try {
    return format(parseISO(iso), "MM-dd-yyyy");
  } catch {
    return "—";
  }
}

function fmtMaterialNumber(value) {
  if (value === "" || value == null) return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return String(Math.round(n));
}

function demandDateToYmd(raw) {
  if (raw == null || raw === "") return "";
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return fmtDate(raw);
  const s = String(raw).trim();
  const ymd = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "" : fmtDate(d);
}

/**
 * Demand / shipping API due date → yyyy-MM-dd for matrix keys and slicers.
 * Handles ISO strings, .NET /Date(ms)/, Date objects, and other strings via demandDateToYmd.
 */
function parseDemandDueDateToIsoYmd(raw) {
  if (raw == null || raw === "") return "";
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return fmtDate(raw);
  if (typeof raw === "number" && Number.isFinite(raw)) {
    if (raw > 1e11) {
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? "" : fmtDate(d);
    }
    return "";
  }
  const s = String(raw).trim();
  const head = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(head)) return head;
  const msDotNet = /^\/Date\((-?\d+)\)\/$/.exec(s);
  if (msDotNet) {
    const d = new Date(Number(msDotNet[1]));
    return Number.isNaN(d.getTime()) ? "" : fmtDate(d);
  }
  return demandDateToYmd(s);
}

function pickDemandDueDateRaw(row) {
  return pickDemandRow(
    row,
    "dueDate",
    "DueDate",
    "due_date",
    "Due_Date",
    "Due Date",
    "needDate",
    "NeedDate",
    "requiredDate",
    "RequiredDate",
  );
}

/** Earliest / latest yyyy-MM-dd due dates in API rows that have positive quantity (for Demand planner date range). */
function getDemandDueDateRangeFromApiRows(rows) {
  const isos = new Set();
  for (const row of Array.isArray(rows) ? rows : []) {
    const dueIso = parseDemandDueDateToIsoYmd(pickDemandDueDateRaw(row));
    if (!dueIso) continue;
    const qty = Number(pickDemandRow(row, "quantity", "Quantity"));
    if (!Number.isFinite(qty) || qty === 0) continue;
    isos.add(dueIso);
  }
  if (!isos.size) return null;
  const sorted = [...isos].sort();
  return { fromIso: sorted[0], toIso: sorted[sorted.length - 1] };
}

function firstNonEmpty(...vals) {
  for (const v of vals) {
    const s = v == null ? "" : String(v).trim();
    if (s) return s;
  }
  return "";
}

/** Display name for load-board "Created By" — same keys as login (`AuthCallback.jsx`). */
function getPlannerLoggedInDisplayName() {
  if (typeof localStorage === "undefined") return "";
  return String(
    localStorage.getItem("UserName") ||
      localStorage.getItem("userName") ||
      localStorage.getItem("UserId") ||
      "",
  ).trim();
}

const customers = {
  "": { name: "— Select customer —", shipTos: [""], parts: [] },
  ACME: {
    name: "Acme Manufacturing Co.",
    shipTos: ["", "Dallas DC", "Chicago Hub", "Phoenix Plant"],
    parts: [
      { sku: "PN-44021", desc: "Hydraulic manifold block", status: "Released", onHand: 850, demand: padSeries([0, 120, 0, 200, 150, 0, 80, 100, 0, 60, 90, 0, 40, 0]) },
      { sku: "PN-44088", desc: "Seal kit, high temp", status: "Active", onHand: 35, demand: padSeries([50, 40, 30, 25, 20, 45, 0, 30, 55, 20, 0, 25, 30, 15]) },
      { sku: "PN-55102", desc: "Drive sprocket assembly", status: "Released", onHand: 2100, demand: padSeries([400, 0, 350, 0, 400, 350, 0, 300, 0, 250, 200, 0, 180, 220]) },
      { sku: "PN-55117", desc: "Bearing housing, cast", status: "Planned", onHand: 95, demand: padSeries([0, 0, 60, 60, 0, 0, 70, 0, 50, 0, 40, 40, 0, 30]) },
      { sku: "PN-66200", desc: "Control arm bushing set", status: "Firm", onHand: 12, demand: padSeries([25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25, 25]) },
      { sku: "PN-77014", desc: "Filter cartridge, primary", status: "Hold", onHand: 480, demand: padSeries([0, 90, 0, 0, 120, 0, 85, 0, 0, 95, 0, 110, 0, 0]) },
      { sku: "PN-0161", desc: "Bracket, mounting", status: "Active", onHand: 140, demand: padSeries([10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10]) },
    ],
  },
  GLOBAL: {
    name: "Global Parts Inc.",
    shipTos: ["", "Memphis WH", "Toronto", "Monterrey"],
    parts: [
      { sku: "GP-1001", desc: "Gear reducer, 15:1", status: "Released", onHand: 2000, demand: padSeries([100, 100, 100, 100, 150, 150, 100, 100, 100, 100, 100, 100, 100, 100]) },
      { sku: "GP-1002", desc: "Coupling insert", status: "Planned", onHand: 88, demand: padSeries([0, 0, 120, 0, 0, 130, 0, 0, 140, 0, 0, 100, 0, 0]) },
      { sku: "GP-2007", desc: "Shaft collar kit", status: "Active", onHand: 520, demand: padSeries([40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40, 40]) },
      { sku: "GP-3011", desc: "Pulley, timing belt", status: "Firm", onHand: 0, demand: padSeries([75, 75, 75, 75, 75, 75, 75, 75, 75, 75, 75, 75, 75, 75]) },
    ],
  },
  NORTH: {
    name: "Northwind Supply LLC",
    shipTos: ["", "Seattle", "Portland", "Boise"],
    parts: [
      { sku: "NW-A12", desc: "Linear rail, 1.2m", status: "Released", onHand: 340, demand: padSeries([20, 20, 20, 20, 20, 20, 20, 20, 20, 20, 20, 20, 20, 20]) },
      { sku: "NW-B04", desc: "Ball screw, precision", status: "Hold", onHand: 55, demand: padSeries([0, 80, 0, 80, 0, 80, 0, 80, 0, 80, 0, 80, 0, 80]) },
      { sku: "NW-C99", desc: "Servo mount plate", status: "Active", onHand: 1500, demand: padSeries([200, 200, 200, 200, 200, 200, 200, 200, 200, 200, 200, 200, 200, 200]) },
    ],
  },
};

const linkedPartColumns = [
  { key: "partNumber", label: "PartNumber" },
  { key: "shotWeight", label: "ShotWeight" },
  { key: "status", label: "Status" },
  { key: "wtNumber", label: "WTNumber" },
  { key: "cycleTime", label: "CycleTime" },
  { key: "plannedQty", label: "PlannedQTY" },
  { key: "totalMaterialRequired", label: "TotalMaterialRequired" },
  { key: "plannedStartDate", label: "PlannedStartDate" },
  { key: "plannedEndDate", label: "PlannedEndDate" },
];

const materialPartDetailColumns = [
  { keys: ["partNumber", "PartNumber"], label: "Part Number" },
  { keys: ["partDescription", "PartDescription"], label: "Part Description" },
  { keys: ["bomName", "BomName"], label: "BOM Name" },
  { keys: ["standardId", "StandardId"], label: "Standard ID", align: "right" },
  { keys: ["componentType", "ComponentType"], label: "Component Type" },
  { keys: ["opClass", "OpClass"], label: "Op Class" },
  { keys: ["sortSeq", "SortSeq"], label: "Sort Seq", align: "right" },
  { keys: ["componentItemNo", "ComponentItemNo"], label: "Component Item No" },
  { keys: ["componentDescription", "ComponentDescription"], label: "Component Description" },
  { keys: ["ptsPer", "PtsPer"], label: "PtsPer", align: "right" },
  { keys: ["materialPerPiece", "MaterialPerPiece"], label: "Material Per Piece", align: "right" },
  { keys: ["totalMaterialPiece", "TotalMaterialPiece"], label: "Total Material Piece", align: "right" },
  { keys: ["isPallet", "IsPallet"], label: "Is Pallet" },
  { keys: ["palletDims", "PalletDims"], label: "Pallet Dims" },
];

function shipToLabelFromDemandRow(row) {
  const attn = String(pickDemandRow(row, "attn", "ATTN") || "").trim();
  const city = String(pickDemandRow(row, "city", "CITY") || "").trim();
  const state = String(pickDemandRow(row, "state", "STATE") || "").trim();
  const addr1 = String(pickDemandRow(row, "addr1", "ADDR1") || "").trim();
  const parts = [attn || addr1, [city, state].filter(Boolean).join(", ")].filter(Boolean);
  const label = parts.join(" · ").trim();
  return label || "—";
}

function shipToAddressFieldsFromDemandRow(row) {
  return {
    attn: String(pickDemandRow(row, "attn", "ATTN") || "").trim(),
    addr1: String(pickDemandRow(row, "addr1", "ADDR1") || "").trim(),
    addr2: String(pickDemandRow(row, "addr2", "ADDR2") || "").trim(),
    addr3: String(pickDemandRow(row, "addr3", "ADDR3") || "").trim(),
    city: String(pickDemandRow(row, "city", "CITY") || "").trim(),
    state: String(pickDemandRow(row, "state", "STATE") || "").trim(),
    zip: String(pickDemandRow(row, "zip", "ZIP") || "").trim(),
    country: String(pickDemandRow(row, "country", "COUNTRY") || "").trim(),
  };
}

function mergePreferNonEmpty(target, patch) {
  Object.keys(patch || {}).forEach((k) => {
    const v = patch[k];
    if (v == null || v === "") return;
    if (target[k] == null || target[k] === "") target[k] = v;
  });
}

/** `Customer/getall` row → display name for Demand / Shipping planner customer dropdowns. */
function pickCustomerMasterName(row) {
  if (!row || typeof row !== "object") return "";
  return String(
    row.customerName ??
      row.CustomerName ??
      row.name ??
      row.Name ??
      row.companyName ??
      row.CompanyName ??
      "",
  ).trim();
}

/** Prefer ERP / external id for planner APIs; falls back to numeric id fields. */
function pickCustomerMasterCode(row) {
  if (!row || typeof row !== "object") return "";
  const fromPick = String(
    pickDemandRow(
      row,
      "customerIdExt",
      "CustomerIdExt",
      "customerNumber",
      "CustomerNumber",
      "arcustoId",
      "Arcusto_ID",
      "ArcustoId",
    ) || "",
  ).trim();
  if (fromPick) return fromPick;
  return String(row.customerCode ?? row.CustomerCode ?? row.id ?? row.Id ?? row.customerId ?? row.CustomerId ?? "").trim();
}

/**
 * Builds sorted customer name list (Demand) and `{ name, value }[]` for Shipping `<select>`
 * (`value` is master code when available, else `__name:{name}` for compatibility with existing planner logic).
 */
function buildPlannerCustomerListsFromGetAllResponse(res) {
  const raw = res?.response ?? res?.Response ?? (Array.isArray(res) ? res : []);
  const rows = Array.isArray(raw) ? raw : [];
  const byName = new Map();
  rows.forEach((row) => {
    const name = pickCustomerMasterName(row);
    if (!name) return;
    if (!byName.has(name)) byName.set(name, row);
  });
  const demandNames = [...byName.keys()].sort((a, b) => a.localeCompare(b));
  const shippingOptions = demandNames.map((name) => {
    const row = byName.get(name);
    const code = pickCustomerMasterCode(row);
    return { name, value: code || `__name:${name}` };
  });
  return { demandNames, shippingOptions };
}

function normalizeMaterialPlannerComponentTypesResponse(res) {
  const raw = res?.response ?? res?.Response ?? (Array.isArray(res) ? res : []);
  const rows = Array.isArray(raw) ? raw : [];
  const seen = new Set();
  rows.forEach((row) => {
    const value =
      typeof row === "string"
        ? row
        : pickDemandRow(row, "componentType", "ComponentType", "component_type", "Component_Type");
    const label = String(value || "").trim();
    if (label) seen.add(label);
  });
  return [...seen].sort((a, b) => a.localeCompare(b));
}

function normalizeMaterialPlannerWorkspaceRowsResponse(res) {
  const raw = res?.response ?? res?.Response ?? (Array.isArray(res) ? res : []);
  return Array.isArray(raw) ? raw : [];
}

function filterRawShippingDemandRows(apiRows, filters) {
  const customerFilter = filters?.customerName ? String(filters.customerName).trim() : "";
  const shipToFilter = filters?.shipTo && filters.shipTo !== "all" ? String(filters.shipTo).trim().toLowerCase() : "";

  let rows = Array.isArray(apiRows) ? [...apiRows] : [];
  if (customerFilter) {
    rows = rows.filter((r) => String(pickDemandRow(r, "customerName", "CustomerName") || "").trim() === customerFilter);
  }
  if (shipToFilter) {
    rows = rows.filter((r) => shipToLabelFromDemandRow(r).toLowerCase().includes(shipToFilter));
  }
  return rows;
}

function buildShippingRowsFromDemandDetail(apiRows, filters) {
  const rows = filterRawShippingDemandRows(apiRows, filters);

  /** @type {Map<string, any>} */
  const map = new Map();

  rows.forEach((r) => {
    const customerName = String(pickDemandRow(r, "customerName", "CustomerName") || "").trim();
    const partNumber = pickDemandPartNumber(r);
    if (!partNumber) return;

    const desc = String(pickDemandRow(r, "description", "Description") || "").trim();
    const po = String(
      pickDemandRow(r, "po", "PO", "poNumber", "PONumber", "purchaseOrder", "PurchaseOrder", "poNo", "PONo") || "",
    ).trim();
    const releaseId = String(
      pickDemandRow(r, "releaseId", "ReleaseId", "release", "Release", "releaseNo", "ReleaseNo", "shipmentReleaseId", "ShipmentReleaseId") || "",
    ).trim();

    const shipToLabel = shipToLabelFromDemandRow(r);
    const source = String(pickDemandRow(r, "source", "Source", "plant", "Plant", "mfg", "MFG") || "").trim() || "—";
    const destination = String(pickDemandRow(r, "destination", "Destination", "route", "Route") || "").trim() || shipToLabel;

    let inventoryOnHandRaw = pickDemandRow(r, "inventoryOnHand", "InventoryOnHand");
    if (inventoryOnHandRaw === "" || inventoryOnHandRaw == null) {
      inventoryOnHandRaw = pickDemandRow(r, "onHand", "OnHand");
    }
    const inventoryOnHand = inventoryOnHandRaw === "" || inventoryOnHandRaw == null ? "" : Number(inventoryOnHandRaw);

    const palletInvRaw = pickDemandRow(
      r,
      "onHand",
      "OnHand",
      "inventoryOnHand",
      "InventoryOnHand",
      "partsOnAPallet",
      "PartsOnAPallet",
      "partsonaPallet",
      "PartsonaPallet",
      "palletInv",
      "PalletInv",
    );
    const palletInv = palletInvRaw === "" || palletInvRaw == null ? "" : Number(palletInvRaw);
    const boxQtyRaw = pickDemandRow(
      r,
      "boxQty",
      "BoxQty",
      "qtyPerBox",
      "QtyPerBox",
      "piecesPerBox",
      "PiecesPerBox",
      "partsInBox",
      "PartsInBox",
      "partsInABox",
      "PartsInABox",
      "partsinaBox",
      "PartsinaBox",
    );
    const boxQty = boxQtyRaw === "" || boxQtyRaw == null ? "" : Number(boxQtyRaw);

    const partsonaPalletRaw = pickDemandRow(
      r,
      "partsonaPallet",
      "PartsonaPallet",
      "partsOnPallet",
      "PartsOnPallet",
      "partsOnAPallet",
      "PartsOnAPallet",
    );
    const partsonaPalletNum =
      partsonaPalletRaw === "" || partsonaPalletRaw == null ? "" : Number(partsonaPalletRaw);

    const palletDimensionsInit = String(pickDemandRow(r, "palletDimensions", "PalletDimensions") || "").trim();
    const partWeightInitRaw = pickDemandRow(r, "partWeight", "PartWeight");
    const partWeightInitNum =
      partWeightInitRaw === "" || partWeightInitRaw == null ? "" : Number(partWeightInitRaw);

    const rowKey = [customerName, partNumber, po, releaseId, shipToLabel, source, destination].join("||");

    if (!map.has(rowKey)) {
      map.set(rowKey, {
        rowKey,
        customerName,
        sku: partNumber,
        desc,
        po: po || "—",
        inventoryOnHand: Number.isFinite(inventoryOnHand) ? inventoryOnHand : "",
        palletInv: Number.isFinite(palletInv) ? palletInv : "",
        partsonaPallet: Number.isFinite(partsonaPalletNum) ? partsonaPalletNum : "",
        boxQty: Number.isFinite(boxQty) ? boxQty : "",
        palletDimensions: palletDimensionsInit,
        partWeight: Number.isFinite(partWeightInitNum) ? partWeightInitNum : "",
        releaseId: releaseId || "—",
        dueDate: "",
        grandTotal: 0,
        loads: [],
        qtyByDueDate: {},
        remainingByDueDate: {},
        /** Cus_Demand_Detail.Id rows per due date (for load-to-shipment demand balance). */
        demandDetailsByDueDate: {},
        source,
        destination,
        ...shipToAddressFieldsFromDemandRow(r),
      });
    }

    const agg = map.get(rowKey);
    mergePreferNonEmpty(agg, shipToAddressFieldsFromDemandRow(r));

    const pdNext = String(pickDemandRow(r, "palletDimensions", "PalletDimensions") || "").trim();
    if (pdNext && !String(agg.palletDimensions || "").trim()) agg.palletDimensions = pdNext;
    const pwNextRaw = pickDemandRow(r, "partWeight", "PartWeight");
    const pwNext = pwNextRaw === "" || pwNextRaw == null ? "" : Number(pwNextRaw);
    if (Number.isFinite(pwNext) && (agg.partWeight === "" || !Number.isFinite(agg.partWeight))) agg.partWeight = pwNext;
    const boxQtyNextRaw = pickDemandRow(
      r,
      "boxQty",
      "BoxQty",
      "qtyPerBox",
      "QtyPerBox",
      "partsInBox",
      "PartsInBox",
      "partsInABox",
      "PartsInABox",
      "partsinaBox",
      "PartsinaBox",
    );
    const boxQtyNext = boxQtyNextRaw === "" || boxQtyNextRaw == null ? "" : Number(boxQtyNextRaw);
    if (Number.isFinite(boxQtyNext) && (agg.boxQty === "" || !Number.isFinite(agg.boxQty))) agg.boxQty = boxQtyNext;
    const palletQtyNextRaw = pickDemandRow(
      r,
      "partsonaPallet",
      "PartsonaPallet",
      "partsOnPallet",
      "PartsOnPallet",
      "partsOnAPallet",
      "PartsOnAPallet",
    );
    const palletQtyNext = palletQtyNextRaw === "" || palletQtyNextRaw == null ? "" : Number(palletQtyNextRaw);
    if (Number.isFinite(palletQtyNext) && (agg.partsonaPallet === "" || !Number.isFinite(agg.partsonaPallet))) {
      agg.partsonaPallet = palletQtyNext;
    }
    const dueIso = parseDemandDueDateToIsoYmd(pickDemandDueDateRaw(r));
    if (!dueIso) return;

    const qty = Number(pickDemandRow(r, "quantity", "Quantity"));
    if (!Number.isFinite(qty) || qty === 0) return;

    agg.qtyByDueDate[dueIso] = (agg.qtyByDueDate[dueIso] || 0) + qty;
    agg.grandTotal += qty;

    const demandDetailId = pickDemandDetailIdFromRow(r);
    if (!agg.demandDetailsByDueDate[dueIso]) agg.demandDetailsByDueDate[dueIso] = [];
    agg.demandDetailsByDueDate[dueIso].push({
      id: demandDetailId,
      qty,
      releaseId: releaseId || "",
    });

    let invOnHand = Number(pickDemandRow(r, "inventoryOnHand", "InventoryOnHand"));
    if (!Number.isFinite(invOnHand)) invOnHand = Number(pickDemandRow(r, "onHand", "OnHand"));
    if (Number.isFinite(invOnHand)) {
      if (agg.inventoryOnHand === "" || !Number.isFinite(agg.inventoryOnHand)) agg.inventoryOnHand = invOnHand;
      else agg.inventoryOnHand = Math.max(agg.inventoryOnHand, invOnHand);
    }

    const inv = Number(
      pickDemandRow(
        r,
        "onHand",
        "OnHand",
        "inventoryOnHand",
        "InventoryOnHand",
        "partsOnAPallet",
        "PartsOnAPallet",
        "partsonaPallet",
        "PartsonaPallet",
        "palletInv",
        "PalletInv",
      ),
    );
    if (Number.isFinite(inv)) {
      if (agg.palletInv === "" || !Number.isFinite(agg.palletInv)) agg.palletInv = inv;
      else agg.palletInv = Math.max(agg.palletInv, inv);
    }

    const qb = Number(
      pickDemandRow(
        r,
        "boxQty",
        "BoxQty",
        "qtyPerBox",
        "QtyPerBox",
        "piecesPerBox",
        "PiecesPerBox",
        "partsInABox",
        "PartsInABox",
        "partsinaBox",
        "PartsinaBox",
      ),
    );
    if (Number.isFinite(qb) && qb > 0) {
      if (agg.boxQty === "" || !Number.isFinite(agg.boxQty)) agg.boxQty = qb;
    }

    const perPal = Number(
      pickDemandRow(r, "partsonaPallet", "PartsonaPallet", "partsOnAPallet", "PartsOnAPallet"),
    );
    if (Number.isFinite(perPal) && perPal > 0) {
      if (agg.partsonaPallet === "" || !Number.isFinite(agg.partsonaPallet)) agg.partsonaPallet = perPal;
      else agg.partsonaPallet = Math.max(agg.partsonaPallet, perPal);
    }

    if (!agg.desc && desc) agg.desc = desc;
  });

  const out = [...map.values()].map((row) => {
    const dates = Object.keys(row.qtyByDueDate || {}).sort();
    let maxIso = "";
    let maxQ = -1;
    dates.forEach((iso) => {
      const q = row.qtyByDueDate[iso] || 0;
      if (q > maxQ) {
        maxQ = q;
        maxIso = iso;
      }
    });
    return {
      ...row,
      dueDate: maxIso || dates[0] || "",
      remainingByDueDate: { ...row.qtyByDueDate },
    };
  });

  out.sort((a, b) => {
    const c = String(a.customerName || "").localeCompare(String(b.customerName || ""));
    if (c !== 0) return c;
    const p = String(a.sku || "").localeCompare(String(b.sku || ""));
    if (p !== 0) return p;
    return String(a.rowKey || "").localeCompare(String(b.rowKey || ""));
  });

  return out;
}

function getDateRangeIndices(fromValue, toValue) {
  const from = fromValue ? new Date(`${fromValue}T12:00:00`) : null;
  const to = toValue ? new Date(`${toValue}T12:00:00`) : null;
  let fromIdx = 0;
  let toIdx = baseDates.length - 1;

  if (from) {
    const i = baseDates.findIndex((d) => d.getTime() >= from.getTime());
    fromIdx = i === -1 ? baseDates.length - 1 : Math.max(0, i);
  }

  if (to) {
    let j = -1;
    for (let k = baseDates.length - 1; k >= 0; k -= 1) {
      if (baseDates[k].getTime() <= to.getTime()) {
        j = k;
        break;
      }
    }
    toIdx = j === -1 ? 0 : j;
  }

  if (fromIdx > toIdx) {
    return { fromIdx: toIdx, toIdx: fromIdx };
  }

  return { fromIdx, toIdx };
}

function materialDateIndicesWithData(materials, fromIdx, toIdx) {
  const indices = [];
  for (let i = fromIdx; i <= toIdx; i += 1) {
    if (materials.some((mat) => (mat.req[i] || 0) !== 0 || (mat.incoming[i] || 0) !== 0)) indices.push(i);
  }
  return indices;
}

function getPartStatusClass(status) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "hold") return "text-red-700";
  if (normalized === "planned") return "text-slate-500";
  return "text-slate-800";
}

function sumForDates(row, dates, source = "qtyByDueDate") {
  return dates.reduce((sum, due) => sum + (row[source]?.[due] || 0), 0);
}

function sanitizeFilenamePart(s) {
  return String(s || "export")
    .replace(/[\\/:*?"<>|]+/g, "_")
    .replace(/\s+/g, "_")
    .slice(0, 120);
}

/** Excel cell text — empty shows as em dash like the on-screen form. */
function xlsxCellDash(v) {
  if (v == null || v === "") return "—";
  return String(v);
}

function appendShipmentFormFieldToExcelColumn(rows, fieldDef, formProps, layoutNorm) {
  const f = fieldDef || {};
  if (f.type === "dockNote") {
    rows.push(["Note Text", xlsxCellDash(layoutNorm.dockAppointmentNote)]);
    return;
  }
  if (f.type === "shipToLines") {
    rows.push(["Ship To", xlsxCellDash(formProps.shipToLine1)]);
    rows.push(["", xlsxCellDash(formProps.shipToLine2)]);
    rows.push(["", xlsxCellDash(formProps.shipToLine3)]);
    return;
  }
  if (f.type === "aspEmail") {
    rows.push(["Instruction", "EMAIL ASP FORM to the following:"]);
    rows.push(["Email Address", layoutNorm.aspFormEmail]);
    return;
  }
  const label = f.label != null ? String(f.label) : String(f.key || "");
  const v = f.key ? formProps[f.key] : "";
  rows.push([label, xlsxCellDash(v)]);
}

/**
 * Shipment form block for Excel — only when API provided a layout (`sections`). Otherwise a short notice row.
 */
function buildLoadBoardShipmentFormExcelAoa(form, layoutRaw) {
  const f = form || {};
  const L = normalizeShipmentFormLayoutFromApi(layoutRaw);
  if (!L) {
    return [
      ["Shipment Form", "", "", "", "", ""],
      [
        "No customer-specific ASP form is configured for this customer. Add AspLoadBoardConfigJson on the Customer record or shipmentForm in load board schema.",
        "",
        "",
        "",
        "",
        "",
      ],
    ];
  }
  const sections = Array.isArray(L.sections) ? L.sections.slice(0, 3) : [];
  while (sections.length < 3) {
    sections.push({ id: `pad-${sections.length}`, eyebrow: "", title: "", fields: [] });
  }
  const colRows = [[], [], []];
  sections.forEach((sec, si) => {
    const target = colRows[Math.min(si, 2)] || colRows[0];
    (sec.fields || []).forEach((fld) => appendShipmentFormFieldToExcelColumn(target, fld, f, L));
  });
  const n = Math.max(colRows[0].length, colRows[1].length, colRows[2].length);
  const pad2 = (rows) => {
    const out = rows.map((r) => [...r]);
    while (out.length < n) out.push(["", ""]);
    return out;
  };
  const [left, mid, right] = [pad2(colRows[0]), pad2(colRows[1]), pad2(colRows[2])];
  const body = Array.from({ length: n }, (_, i) => [...left[i], ...mid[i], ...right[i]]);
  const hdr = sections.map((sec) =>
    String(sec.excelSectionHeader || `${sec.eyebrow || ""} (${sec.title || ""})`).trim() || "—",
  );
  while (hdr.length < 3) hdr.push("");
  return [
    ["Shipment Form", "", "", "", "", ""],
    [hdr[0], "", hdr[1], "", hdr[2], ""],
    ...body,
  ];
}

function aoaColumnWidths(aoa, cap = 48) {
  const maxC = aoa.reduce((m, row) => Math.max(m, Array.isArray(row) ? row.length : 0), 0);
  const widths = [];
  for (let c = 0; c < maxC; c += 1) {
    let wch = 10;
    aoa.forEach((row) => {
      if (!Array.isArray(row)) return;
      const cell = row[c];
      if (cell == null || cell === "") return;
      wch = Math.max(wch, Math.min(cap, String(cell).length + 2));
    });
    widths.push({ wch });
  }
  return widths;
}

/**
 * Load-board .xlsx: metadata + planner grid, optional second sheet with ASP-style line columns
 * (Supplier Ship Plan style — flat table; not merged-cell customer template).
 */
function buildLoadBoardTableWorkbook({ metaRows, headerRow, dataRows, extraSheets }) {
  const wb = XLSX.utils.book_new();
  const mainAoa = [...metaRows, [""], headerRow, ...dataRows];
  const ws = XLSX.utils.aoa_to_sheet(mainAoa);
  ws["!cols"] = aoaColumnWidths(mainAoa);
  XLSX.utils.book_append_sheet(wb, ws, "Load board");
  (extraSheets || []).forEach(({ name, aoa }) => {
    const sheetName = String(name || "Sheet").slice(0, 31);
    const ws2 = XLSX.utils.aoa_to_sheet(aoa);
    ws2["!cols"] = aoaColumnWidths(aoa);
    XLSX.utils.book_append_sheet(wb, ws2, sheetName);
  });
  return wb;
}

function loadBoardWorkbookToXlsxBlob(wb) {
  const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  return new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error("Failed to read Excel as base64."));
    reader.readAsDataURL(blob);
  });
}

function base64ToUint8Array(base64) {
  const binary = atob(String(base64 || "").replace(/\s/g, ""));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Build a new output file name for Loaded_ASP_Forms_Raintels.
 * Never returns COMMON_ASP_Form.xlsx (master template must stay untouched).
 */
function buildLoadedAspFormFileName({ customerCode, customerName, shipmentNumber, shipDate, loadIndex }) {
  const stamp =
    shipDate && /^\d{4}-\d{2}-\d{2}$/.test(shipDate)
      ? shipDate.replace(/-/g, "")
      : new Date().toISOString().slice(0, 10).replace(/-/g, "");
  const unique = `${new Date().toISOString().replace(/[-:TZ.]/g, "").slice(0, 14)}_${(loadIndex || 0) + 1}`;
  let fileName = `ASP_${sanitizeFilenamePart(customerCode || customerName || "CUSTOMER")}_${sanitizeFilenamePart(
    shipmentNumber || `S${(loadIndex || 0) + 1}`,
  )}_${stamp}_${unique}.xlsx`;

  const commonName = COMMON_ASP_TEMPLATE_FILE_NAME.toLowerCase();
  if (!fileName.toLowerCase().endsWith(".xlsx")) fileName = `${fileName}.xlsx`;
  if (fileName.toLowerCase() === commonName || fileName.toLowerCase().includes("common_asp_form")) {
    fileName = `ASP_LOADED_${unique}.xlsx`;
  }
  return fileName;
}

/** Shipment-scope values for ASP template fill (COMMON ASP template). */
function buildAspShipmentOnlyFromFormProps(formProps, extras = {}) {
  const p = formProps || {};
  return {
    creationDate: p.creationDate,
    createdBy: p.createdBy,
    shipmentReadyDate: p.shipmentReadyDate,
    shipmentReadyTime: p.shipmentReadyTime,
    shippingDockHours: p.shippingDockHours,
    planDeliveryDate: p.planDeliveryDate,
    planDeliveryTime: p.planDeliveryTime,
    supplierName: p.supplierName,
    supplierNumber: p.supplierNumber,
    addressLineOne: p.addressLineOne,
    addressLineTwo: p.addressLineTwo,
    addressLineThree: p.addressLineThree,
    city: p.supplierCity,
    state: p.supplierState,
    zipCode: p.supplierZip,
    countryOfOrigin: p.countryOfOrigin,
    phone: p.phone,
    phoneExtension: p.phoneExtension,
    freightTerms: p.freightTerms,
    shipmentNumber: p.shipmentNumber,
    rev: p.rev,
    carrierName: p.carrierName,
    carrierContact: p.carrierContact,
    carrierPhone: p.carrierPhone,
    totalBoxes: p.totalBoxes,
    totalPallets: p.totalPallets,
    totalWeight: p.totalWeight,
    shipToLine1: p.shipToLine1,
    shipToLine2: p.shipToLine2,
    shipToLine3: p.shipToLine3,
    shipTo: [p.shipToLine1, p.shipToLine2, p.shipToLine3].filter(Boolean).join(", "),
    shipDate: extras.shipDate || "",
    customerNumber: extras.customerCode || "",
    notes: DOCK_APPOINTMENT_NOTE,
  };
}

/** Line-scope rows for fillWorkbookFromMapping. */
function buildAspLineRowsForFill({
  loadIdx,
  filteredShippingRows,
  dueColumns,
  palletMode,
  cellOverrides,
}) {
  const rows = shippingRowsWithQtyOnLoad(filteredShippingRows, loadIdx);
  const lineKeys = [
    "lineNumber",
    "partNumber",
    "partDescription",
    "poNumber",
    "numberOfBoxes",
    "qtyPerBox",
    "palletsQty",
    "plannedShipQuantity",
    "uom",
    "mixedPalletYn",
    "dimensionsLWH",
    "totalShipWeight",
    "totalCube",
    "deleteYn",
    "shipmentReleaseId",
    "dueDate",
    "destination",
    "source",
    "quantity",
    "notes",
  ];
  return rows.map((row, rowIndex) => {
    const line = {};
    lineKeys.forEach((key) => {
      line[key] = resolveLoadBoardCellValue({
        loadIdx,
        row,
        rowIndex,
        fieldKey: key,
        dueColumns,
        palletMode,
        cellOverrides,
      });
    });
    if (line.quantity === "" || line.quantity == null) {
      line.quantity = row.loads?.[loadIdx] || 0;
    }
    return line;
  });
}

function classNames(...items) {
  return items.filter(Boolean).join(" ");
}

function loadAspStore() {
  if (typeof window === "undefined") return { version: 1, profiles: [] };
  try {
    const raw = window.localStorage.getItem(ASP_MAPPING_STORAGE_KEY);
    if (!raw) return { version: 1, profiles: [] };
    const o = JSON.parse(raw);
    if (!o || !Array.isArray(o.profiles)) return { version: 1, profiles: [] };
    return o;
  } catch {
    return { version: 1, profiles: [] };
  }
}

function saveAspStore(store) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(ASP_MAPPING_STORAGE_KEY, JSON.stringify(store));
}

function loadAspMappings() {
  return loadAspStore().profiles;
}

function newAspMappingId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;
}

function normalizeAspCellRef(s) {
  return String(s || "").trim().replace(/\$/g, "").toUpperCase();
}

function parseExcelCellRef(str) {
  const t = normalizeAspCellRef(str);
  if (!t) return null;
  const m = t.match(/^([A-Z]+)(\d+)$/);
  if (!m) return null;
  const letters = m[1];
  const row1 = parseInt(m[2], 10);
  if (row1 < 1 || row1 > 1048576) return null;
  let col = 0;
  for (let i = 0; i < letters.length; i++) {
    col = col * 26 + (letters.charCodeAt(i) - 64);
  }
  const col0 = col - 1;
  if (col0 < 0 || col0 > 16383) return null;
  return { row1, col0 };
}

function col0ToLetters(col0) {
  let c = col0 + 1;
  let s = "";
  while (c > 0) {
    const mod = (c - 1) % 26;
    s = String.fromCharCode(65 + mod) + s;
    c = Math.floor((c - mod) / 26);
  }
  return s;
}

function formatExcelCellRef(row1, col0) {
  return col0ToLetters(col0) + row1;
}

function parseHeaderRowFromSheet(sheet, row0) {
  if (!sheet || !sheet["!ref"]) return [];
  const range = XLSX.utils.decode_range(sheet["!ref"]);
  const headers = [];
  const r = row0;
  if (range.s.c > range.e.c) return [];
  for (let C = range.s.c; C <= range.e.c; C++) {
    const addr = XLSX.utils.encode_cell({ r, c: C });
    const cell = sheet[addr];
    const v = cell && cell.v != null ? String(cell.v).trim() : "";
    headers.push(v || `Column ${col0ToLetters(C)}`);
  }
  return headers;
}

function normalizeColumnMapEntry(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "string") {
    return { header: raw.trim(), cellRef: "" };
  }
  if (typeof raw === "object") {
    return {
      header: raw.header != null ? String(raw.header).trim() : "",
      cellRef: raw.cellRef != null ? String(raw.cellRef).trim().replace(/\$/g, "").toUpperCase() : "",
    };
  }
  return null;
}

function migrateColumnMap(obj) {
  const out = {};
  Object.keys(obj || {}).forEach((k) => {
    const e = normalizeColumnMapEntry(obj[k]);
    if (e && (e.header || e.cellRef)) out[k] = e;
  });
  return out;
}

function getBaseScopeForKey(key, profile) {
  const p = profile || {};
  if (String(key).indexOf(CUSTOM_FIELD_PREFIX) === 0) {
    const cid = String(key).slice(CUSTOM_FIELD_PREFIX.length);
    const list = p.customFields ? p.customFields : [];
    for (let j = 0; j < list.length; j++) {
      if (list[j].id === cid) return list[j].scope === "shipment" ? "shipment" : "line";
    }
    return "line";
  }
  for (let i = 0; i < SYSTEM_FIELDS_FOR_MAPPING.length; i++) {
    if (SYSTEM_FIELDS_FOR_MAPPING[i].key === key) {
      return SYSTEM_FIELDS_FOR_MAPPING[i].scope === "shipment" ? "shipment" : "line";
    }
  }
  return "line";
}

function getScopeForKey(key, profile) {
  const p = profile || {};
  const ovr = p.fieldScopeOverrides;
  if (ovr && Object.prototype.hasOwnProperty.call(ovr, key)) {
    const o = ovr[key];
    if (o === "shipment" || o === "line") return o;
  }
  return getBaseScopeForKey(key, p);
}

function findSystemKeyForExcelHeader(cm, headerName) {
  let found = "";
  Object.keys(cm || {}).forEach((k) => {
    const e = normalizeColumnMapEntry(cm[k]);
    if (e && e.header === headerName) found = k;
  });
  return found;
}

function cloneDefaultLoadBoardColumns() {
  return DEFAULT_SUPPLIER_SHIP_PLAN_LOAD_BOARD_COLUMNS.map((c) => ({ ...c }));
}

function loadBoardSchemaStorageKey(plannerCustomerKey, shipTo) {
  const st = shipTo && shipTo !== "all" ? String(shipTo) : "all";
  return `SPVP_LOAD_BOARD_SCHEMA_V1:${String(plannerCustomerKey || "none")}|${st}`;
}

function parseLoadBoardSchemaEnvelope(raw) {
  if (!raw || typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw);
    const columns = Array.isArray(parsed?.columns) ? parsed.columns : null;
    if (!columns?.length) return null;
    const headerRows = Array.isArray(parsed?.headerRows) ? parsed.headerRows : null;
    const shipmentForm = parsed?.shipmentForm ?? parsed?.ShipmentForm ?? null;
    return { columns, headerRows, shipmentForm };
  } catch {
    return null;
  }
}

/** dbo.Customer.AspLoadBoardConfigJson from GET planner/customer-asp-loadboard-config/{customerCode} (full envelope: sections, columns, …). */
function parseShipmentFormFromCustomerAspApiResponse(res) {
  const inner = res?.response ?? res?.Response ?? res;
  if (!inner || typeof inner !== "object") return null;
  const raw = inner.aspLoadBoardConfigJson ?? inner.AspLoadBoardConfigJson;
  if (raw == null || raw === "") return null;
  if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) return raw;
  try {
    const o = JSON.parse(String(raw));
    return typeof o === "object" && o !== null && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
}

function loadLoadBoardSchemaFromLocalStorage(plannerCustomerKey, shipTo) {
  if (typeof window === "undefined" || !plannerCustomerKey) return null;
  const raw = window.localStorage.getItem(loadBoardSchemaStorageKey(plannerCustomerKey, shipTo));
  return parseLoadBoardSchemaEnvelope(raw);
}

function saveLoadBoardSchemaToLocalStorage(plannerCustomerKey, shipTo, schemaObj) {
  if (typeof window === "undefined" || !plannerCustomerKey) return;
  window.localStorage.setItem(loadBoardSchemaStorageKey(plannerCustomerKey, shipTo), JSON.stringify(schemaObj));
}

function loadBoardGroupAndAlignForFieldKey(fieldKey) {
  const def = DEFAULT_SUPPLIER_SHIP_PLAN_LOAD_BOARD_COLUMNS.find((c) => c.fieldKey === fieldKey);
  if (def) return { group: def.group, align: def.align || "left" };
  const sf = SYSTEM_FIELDS_FOR_MAPPING.find((x) => x.key === fieldKey);
  const g = String(sf?.group || "");
  if (g.toUpperCase().includes("APPROVAL") || String(fieldKey).toLowerCase().startsWith("approved")) {
    return { group: "APPROVAL", align: "center" };
  }
  return { group: "SUPPLIER SHIP PLAN REQUEST", align: "left" };
}

/** Build Load Load Board column defs from ASP column map + Excel header order (same workbook as mapping). */
function buildLoadBoardColumnsFromColumnMap(mappingCurrentHeaders, columnMap, customFields) {
  const headers = Array.isArray(mappingCurrentHeaders) ? mappingCurrentHeaders : [];
  const entries = [];
  Object.keys(columnMap || {}).forEach((fieldKey) => {
    const e = normalizeColumnMapEntry(columnMap[fieldKey]);
    if (!e || !e.header) return;
    const header = String(e.header).trim();
    let idx = headers.findIndex((h) => String(h).trim() === header);
    if (idx < 0) idx = 9999;
    const validKey =
      SYSTEM_FIELDS_FOR_MAPPING.some((s) => s.key === fieldKey) ||
      (String(fieldKey).startsWith(CUSTOM_FIELD_PREFIX) &&
        (customFields || []).some((c) => `${CUSTOM_FIELD_PREFIX}${c.id}` === fieldKey));
    if (!validKey) return;
    entries.push({ idx, fieldKey, label: header });
  });
  entries.sort((a, b) => a.idx - b.idx || a.fieldKey.localeCompare(b.fieldKey));
  return entries.map(({ fieldKey, label }) => {
    const { group, align } = loadBoardGroupAndAlignForFieldKey(fieldKey);
    return { group, label: label || fieldKey, fieldKey, align };
  });
}

function parseHeaderAnchorFromCellInput(raw) {
  let p = parseExcelCellRef(normalizeAspCellRef(raw));
  if (!p && /^\d+$/.test(String(raw).trim())) {
    p = parseExcelCellRef(`A${String(raw).trim()}`);
  }
  return p;
}

function normalizeFieldScopeOverridesFromProfile(p, customFields) {
  const out = {};
  const prof = { customFields };
  const raw = (p && p.fieldScopeOverrides) || {};
  Object.keys(raw).forEach((k) => {
    const v = raw[k];
    if (v === "shipment" || v === "line") {
      if (v !== getBaseScopeForKey(k, prof)) out[k] = v;
    }
  });
  return out;
}

function buildAllSystemFieldRows(customFieldsDraft) {
  const out = [];
  SYSTEM_FIELDS_FOR_MAPPING.forEach((sf) => out.push(sf));
  (customFieldsDraft || []).forEach((cf) => {
    out.push({
      key: `${CUSTOM_FIELD_PREFIX}${cf.id}`,
      label: cf.label,
      group: "Custom fields",
      scope: cf.scope,
      isCustom: true,
    });
  });
  return out;
}

function AspScopeGroupSelect({ fieldKey, customFields, valueOverrides, onChange, disabled }) {
  const base = getBaseScopeForKey(fieldKey, { customFields });
  const baseLabel = base === "shipment" ? "Group 1 — once per sheet" : "Group 2 — per part line";
  const stored = valueOverrides[fieldKey];
  const hasOverride = (stored === "shipment" || stored === "line") && stored !== base;
  const selVal = hasOverride ? stored : "";
  return (
    <select
      disabled={disabled}
      value={selVal}
      onChange={(e) => onChange(fieldKey, e.target.value)}
      title="Group 1 = one value for the whole ASP. Group 2 = one value per part row."
      className="h-8 max-w-[240px] rounded-lg border border-slate-200 bg-white px-2 text-[11px] disabled:opacity-40"
    >
      <option value="">{`Default — ${baseLabel}`}</option>
      <option value="shipment">Group 1 — once per sheet</option>
      <option value="line">Group 2 — per part line</option>
    </select>
  );
}

function AspSettingsModal({ open, onClose, onStoreChanged, toast, plannerCustomerKey = "", plannerShipTo = "all" }) {
  const excelInputRef = useRef(null);
  const importInputRef = useRef(null);
  const selectedProfileIdRef = useRef("");
  const [savedProfiles, setSavedProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState("");
  const [profileName, setProfileName] = useState("");
  const [headerCell, setHeaderCell] = useState("A1");
  const [firstLineCell, setFirstLineCell] = useState("A2");
  const [layoutMode, setLayoutMode] = useState("system_fields");
  const [mappingWb, setMappingWb] = useState(null);
  const [mappingFileName, setMappingFileName] = useState("");
  const [mappingCurrentSheet, setMappingCurrentSheet] = useState("");
  const [mappingCurrentHeaders, setMappingCurrentHeaders] = useState([]);
  const [columnMap, setColumnMap] = useState({});
  const [fieldScopeOverrides, setFieldScopeOverrides] = useState({});
  const [customFields, setCustomFields] = useState([]);
  const [fileMeta, setFileMeta] = useState("No file loaded — upload to read column headers.");
  const [customFieldName, setCustomFieldName] = useState("");
  const [customFieldScope, setCustomFieldScope] = useState("line");
  const [lbColumns, setLbColumns] = useState([]);
  const [lbLoading, setLbLoading] = useState(false);
  const [lbSaving, setLbSaving] = useState(false);

  const sheetNames = mappingWb?.SheetNames || [];

  useEffect(() => {
    selectedProfileIdRef.current = selectedProfileId;
  }, [selectedProfileId]);

  const refreshProfiles = useCallback(() => {
    setSavedProfiles(
      [...loadAspStore().profiles].sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")))
    );
  }, []);

  const resetDraft = useCallback(() => {
    setSelectedProfileId("");
    setProfileName("");
    setHeaderCell("A1");
    setFirstLineCell("A2");
    setLayoutMode("system_fields");
    setMappingWb(null);
    setMappingFileName("");
    setMappingCurrentSheet("");
    setMappingCurrentHeaders([]);
    setColumnMap({});
    setFieldScopeOverrides({});
    setCustomFields([]);
    setFileMeta("No file loaded — upload to read column headers.");
  }, []);

  const loadProfileIntoDraft = useCallback((profileId) => {
    const p = loadAspStore().profiles.find((x) => x.id === profileId);
    if (!p) return;
    setProfileName(p.name || "");
    setCustomFields(
      Array.isArray(p.customFields)
        ? p.customFields.map((x) => ({
            id: x.id,
            label: String(x.label || ""),
            scope: x.scope === "shipment" ? "shipment" : "line",
          }))
        : []
    );
    if (p.headerCellRef) setHeaderCell(normalizeAspCellRef(p.headerCellRef));
    else if (p.columnHeaderRow != null && p.columnHeaderRow !== "") setHeaderCell(`A${p.columnHeaderRow}`);
    else setHeaderCell("A1");
    if (p.firstLineCellRef) setFirstLineCell(normalizeAspCellRef(p.firstLineCellRef));
    else if (p.firstLineItemRow != null && p.firstLineItemRow !== "") setFirstLineCell(`A${p.firstLineItemRow}`);
    else setFirstLineCell("A2");
    setMappingCurrentHeaders(Array.isArray(p.headers) ? p.headers.slice() : []);
    setMappingFileName(p.sourceFileName || "");
    setMappingCurrentSheet(p.sheetName || "");
    setMappingWb(null);
    setLayoutMode(p.mappingLayoutMode === "excel_columns" ? "excel_columns" : "system_fields");
    setColumnMap(migrateColumnMap(p.columnMap || {}));
    const cfs = Array.isArray(p.customFields)
      ? p.customFields.map((x) => ({
          id: x.id,
          label: String(x.label || ""),
          scope: x.scope === "shipment" ? "shipment" : "line",
        }))
      : [];
    setFieldScopeOverrides(normalizeFieldScopeOverridesFromProfile(p, cfs));
    const hp = parseHeaderAnchorFromCellInput(p.headerCellRef || (p.columnHeaderRow != null ? `A${p.columnHeaderRow}` : "A1"));
    setFileMeta(
      p.sourceFileName
        ? `Saved mapping — template was: ${p.sourceFileName} (${(Array.isArray(p.headers) ? p.headers.length : "?")} columns, header at ${hp ? formatExcelCellRef(hp.row1, hp.col0) : "?"}).`
        : "Saved mapping (no file reattached — upload Excel again to refresh headers)."
    );
  }, []);

  useEffect(() => {
    if (!open) return;
    refreshProfiles();
    resetDraft();
  }, [open, refreshProfiles, resetDraft]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      if (!plannerCustomerKey) {
        setLbLoading(false);
        setLbColumns(cloneDefaultLoadBoardColumns());
        return;
      }
      setLbLoading(true);
      try {
        const code = String(plannerCustomerKey).startsWith("__name:") ? "" : plannerCustomerKey;
        if (code) {
          try {
            const res = await SpvpAspService.getLoadBoardSchema({
              customerCode: code,
              shipToCode: plannerShipTo && plannerShipTo !== "all" ? plannerShipTo : null,
            });
            const schemaJson = res?.response?.schemaJson ?? res?.response?.SchemaJson ?? res?.schemaJson ?? res?.SchemaJson ?? "";
            if (schemaJson) {
              const parsed = JSON.parse(schemaJson);
              const columns = Array.isArray(parsed?.columns) ? parsed.columns : [];
              if (!cancelled && columns.length) {
                setLbColumns(columns.map((c) => ({ ...c })));
                return;
              }
            }
          } catch {
            /* fall through */
          }
        } else {
          const local = loadLoadBoardSchemaFromLocalStorage(plannerCustomerKey, plannerShipTo);
          if (!cancelled && local?.columns?.length) {
            setLbColumns(local.columns.map((c) => ({ ...c })));
            return;
          }
        }
        if (!cancelled) setLbColumns(cloneDefaultLoadBoardColumns());
      } finally {
        if (!cancelled) setLbLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, plannerCustomerKey, plannerShipTo]);

  useEffect(() => {
    if (!mappingWb || !mappingCurrentSheet) return;
    const sheet = mappingWb.Sheets[mappingCurrentSheet];
    if (!sheet) return;
    const hp = parseHeaderAnchorFromCellInput(headerCell);
    if (!hp) {
      setFileMeta("Invalid header cell — use column letter and row (e.g. A9).");
      return;
    }
    const row0 = hp.row1 - 1;
    const hdrs = parseHeaderRowFromSheet(sheet, row0);
    setMappingCurrentHeaders(hdrs);
    setFileMeta(
      `Loaded: ${mappingFileName || "workbook"} · sheet "${mappingCurrentSheet}" · ${hdrs.length} column(s) across the used range, labels from row at ${formatExcelCellRef(hp.row1, hp.col0)} (blank cells use Column A, B, …).`
    );
  }, [mappingWb, mappingCurrentSheet, headerCell, mappingFileName]);

  const ensureFirstLineBelowHeader = useCallback(() => {
    const h = parseHeaderAnchorFromCellInput(headerCell);
    const f = parseHeaderAnchorFromCellInput(firstLineCell);
    if (!h || !f) return;
    if (f.row1 < h.row1 || (f.row1 === h.row1 && f.col0 === h.col0)) {
      setFirstLineCell(formatExcelCellRef(h.row1 + 1, f.col0));
    }
  }, [headerCell, firstLineCell]);

  const onScopeOverrideChange = (key, val) => {
    const base = getBaseScopeForKey(key, { customFields });
    setFieldScopeOverrides((prev) => {
      const next = { ...prev };
      if (!val || val === base) delete next[key];
      else next[key] = val;
      return next;
    });
  };

  const updateSystemColumnMap = (key, header, cellRef) => {
    setColumnMap((prev) => {
      const next = { ...prev };
      if (!header && !cellRef) {
        delete next[key];
        return migrateColumnMap(next);
      }
      next[key] = normalizeColumnMapEntry({ header, cellRef });
      return migrateColumnMap(next);
    });
  };

  const updateExcelColumnAssignment = (excelHeader, newSysKey, cellRef) => {
    setColumnMap((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        const e = normalizeColumnMapEntry(next[k]);
        if (e && e.header === excelHeader) delete next[k];
      });
      if (newSysKey) {
        next[newSysKey] = normalizeColumnMapEntry({ header: excelHeader, cellRef: cellRef || "" });
      }
      return migrateColumnMap(next);
    });
  };

  const handleExcelFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const data = new Uint8Array(reader.result);
        const wb = XLSX.read(data, { type: "array" });
        setMappingWb(wb);
        setMappingFileName(file.name);
        const names = wb.SheetNames || [];
        const useSheet = names[0] || "";
        setMappingCurrentSheet(useSheet);
        const pid = selectedProfileIdRef.current;
        if (pid) {
          const p = loadAspStore().profiles.find((x) => x.id === pid);
          if (p) {
            setColumnMap(migrateColumnMap(p.columnMap || {}));
            const cfs = Array.isArray(p.customFields) ? p.customFields : [];
            setFieldScopeOverrides(normalizeFieldScopeOverridesFromProfile(p, cfs));
          }
        }
      } catch (e) {
        toast(`Could not read Excel: ${e.message || e}`, "error");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleReadColumnsClick = () => {
    if (layoutMode !== "excel_columns") return;
    if (!mappingWb || !mappingCurrentSheet) {
      toast("Upload an Excel file first.", "error");
      return;
    }
    ensureFirstLineBelowHeader();
    const hp = parseHeaderAnchorFromCellInput(headerCell);
    if (!hp) {
      toast("Enter a valid header cell (e.g. A9) to read columns.", "error");
      return;
    }
    const sheet = mappingWb.Sheets[mappingCurrentSheet];
    const row0 = hp.row1 - 1;
    const hdrs = parseHeaderRowFromSheet(sheet, row0);
    setMappingCurrentHeaders(hdrs);
    const n = hdrs.length;
    if (!n) toast("No columns in this sheet's used range — try another sheet or a file that contains cells.", "warn");
    else toast(`Populated ${n} column(s) from the sheet.`);
  };

  const handleSave = () => {
    const name = profileName.trim();
    if (!name) {
      toast("Enter a mapping name.", "error");
      return;
    }
    if (!mappingCurrentHeaders.length) {
      toast("Upload an Excel file first so column headers are available (or open a saved mapping and upload again).", "error");
      return;
    }
    const hRef = normalizeAspCellRef(headerCell);
    const fRef = normalizeAspCellRef(firstLineCell);
    const hP = parseExcelCellRef(hRef);
    const fP = parseExcelCellRef(fRef);
    if (!hP || !fP) {
      toast("Enter valid Excel cells with column letters and row (e.g. A9 and A10).", "error");
      return;
    }
    if (fP.row1 < hP.row1) {
      toast("First line-item cell must be on a row below the header cell.", "error");
      return;
    }
    const store = loadAspStore();
    const id = selectedProfileId || newAspMappingId();
    const now = new Date().toISOString();
    const existing = store.profiles.findIndex((x) => x.id === id);
    const customIds = new Set(customFields.map((c) => c.id));
    const cleanedMap = { ...columnMap };
    Object.keys(cleanedMap).forEach((k) => {
      if (k.indexOf(CUSTOM_FIELD_PREFIX) === 0) {
        const cid = k.slice(CUSTOM_FIELD_PREFIX.length);
        if (!customIds.has(cid)) delete cleanedMap[k];
      }
    });
    const row = {
      id,
      name,
      updatedAt: now,
      sourceFileName: mappingFileName || "(unspecified)",
      sheetName: mappingCurrentSheet || "",
      headers: mappingCurrentHeaders.slice(),
      columnMap: migrateColumnMap(cleanedMap),
      fieldScopeOverrides: { ...fieldScopeOverrides },
      mappingLayoutMode: layoutMode,
      headerCellRef: hRef,
      firstLineCellRef: fRef,
      columnHeaderRow: hP.row1,
      firstLineItemRow: fP.row1,
      customFields: customFields.map((x) => ({ id: x.id, label: x.label, scope: x.scope })),
    };
    if (existing >= 0) {
      row.createdAt = store.profiles[existing].createdAt || now;
      store.profiles[existing] = row;
    } else {
      row.createdAt = now;
      store.profiles.push(row);
    }
    saveAspStore(store);
    refreshProfiles();
    setSelectedProfileId(id);
    toast(`Mapping "${name}" saved.`);
    onStoreChanged();
  };

  const handleDelete = () => {
    if (!selectedProfileId) {
      toast("Select a mapping to delete.", "error");
      return;
    }
    if (!window.confirm("Delete this saved mapping? This cannot be undone.")) return;
    const store = loadAspStore();
    store.profiles = store.profiles.filter((p) => p.id !== selectedProfileId);
    saveAspStore(store);
    refreshProfiles();
    resetDraft();
    toast("Mapping removed.");
    onStoreChanged();
  };

  const handleExport = () => {
    const store = loadAspStore();
    const blob = new Blob([JSON.stringify(store, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "spvp-asp-column-mappings-backup.json";
    a.click();
    URL.revokeObjectURL(a.href);
    toast("Backup downloaded.");
  };

  const handleImport = (file) => {
    if (!file) return;
    const r = new FileReader();
    r.onload = () => {
      try {
        const data = JSON.parse(String(r.result || ""));
        if (!data || !Array.isArray(data.profiles)) throw new Error("Invalid file");
        const store = loadAspStore();
        const byId = {};
        store.profiles.forEach((p) => {
          byId[p.id] = p;
        });
        data.profiles.forEach((p) => {
          if (p && p.id) byId[p.id] = p;
        });
        store.profiles = Object.keys(byId).map((k) => byId[k]);
        saveAspStore(store);
        refreshProfiles();
        setSelectedProfileId("");
        resetDraft();
        toast(`Imported ${data.profiles.length} mapping(s) — merged by id.`);
        onStoreChanged();
      } catch (e) {
        toast(`Import failed: ${e.message || e}`, "error");
      }
    };
    r.readAsText(file);
  };

  const addCustomField = () => {
    const name = customFieldName.trim();
    if (!name) {
      toast("Enter a name for the custom field.", "error");
      return;
    }
    const scope = customFieldScope === "shipment" ? "shipment" : "line";
    setCustomFields((prev) => [...prev, { id: newAspMappingId(), label: name, scope }]);
    setCustomFieldName("");
  };

  const removeCustomField = (cid) => {
    setCustomFields((prev) => prev.filter((x) => x.id !== cid));
    setColumnMap((prev) => {
      const next = { ...prev };
      delete next[`${CUSTOM_FIELD_PREFIX}${cid}`];
      return next;
    });
    setFieldScopeOverrides((prev) => {
      const next = { ...prev };
      delete next[`${CUSTOM_FIELD_PREFIX}${cid}`];
      return next;
    });
  };

  const handleLbImportFromMapping = () => {
    const built = buildLoadBoardColumnsFromColumnMap(mappingCurrentHeaders, columnMap, customFields);
    if (!built.length) {
      toast("Map at least one Excel column to a system field (Excel columns layout), then try again.", "warn");
      return;
    }
    setLbColumns(built);
    toast(`Load board preview: ${built.length} column(s) from your mapping.`);
  };

  const handleLbResetDefault = () => {
    setLbColumns(cloneDefaultLoadBoardColumns());
    toast("Reset to default supplier ship plan columns.");
  };

  const handleLbSaveSchema = async () => {
    if (!plannerCustomerKey) {
      toast("Select a specific customer on Shipping Planner (not All customers) to save a load board format.", "error");
      return;
    }
    if (!lbColumns.length) {
      toast("Add at least one column.", "error");
      return;
    }
    setLbSaving(true);
    try {
      const schemaObj = { columns: lbColumns };
      const json = JSON.stringify(schemaObj);
      const code = String(plannerCustomerKey).startsWith("__name:") ? "" : plannerCustomerKey;
      if (code) {
        await SpvpAspService.saveLoadBoardSchema({
          customerCode: code,
          shipToCode: plannerShipTo && plannerShipTo !== "all" ? plannerShipTo : null,
          schemaVersion: 1,
          schemaJson: json,
        });
        toast("Load board column layout saved for this customer on the server.");
      } else {
        saveLoadBoardSchemaToLocalStorage(plannerCustomerKey, plannerShipTo || "all", schemaObj);
        toast("Saved in this browser. For server storage, pick a customer code in the planner dropdown (not name-only).");
      }
    } catch (e) {
      toast(e?.message || "Could not save load board schema.", "error");
    } finally {
      setLbSaving(false);
    }
  };

  const updateLbRow = (i, patch) => {
    setLbColumns((prev) => prev.map((row, j) => (j === i ? { ...row, ...patch } : row)));
  };

  const moveLbRow = (i, dir) => {
    setLbColumns((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const removeLbRow = (i) => setLbColumns((prev) => prev.filter((_, j) => j !== i));

  const addLbRow = () =>
    setLbColumns((prev) => [
      ...prev,
      { group: "SUPPLIER SHIP PLAN REQUEST", label: "Column", fieldKey: "partNumber", align: "left" },
    ]);

  const fieldRows = useMemo(() => buildAllSystemFieldRows(customFields), [customFields]);

  const readColumnsDisabled = layoutMode !== "excel_columns" || !mappingWb || !mappingCurrentSheet;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">ASP Settings</h2>
            <p className="mt-1 max-w-3xl text-sm text-slate-500">
              Define how planning data maps into customer ASP (shipment approval) Excel templates. Named mappings are stored in this browser until you change or remove them.
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 hover:text-slate-900">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto p-5">
          <details className="mb-4 rounded-2xl border border-slate-200 bg-slate-50/80 p-4" open>
            <summary className="cursor-pointer text-sm font-semibold text-slate-800">What is an ASP form?</summary>
            <div className="mt-3 space-y-2 text-sm leading-relaxed text-slate-700">
              <p>
                An <strong>ASP form</strong> is a shipment form filled with shipment details. It is sometimes sent to the customer for approval. When approval is{" "}
                <strong>not</strong> required, shipment details can be populated directly onto the load board.
              </p>
              <p className="font-medium text-slate-800">On this page you can:</p>
              <ul className="list-disc space-y-1 pl-5">
                <li>Upload an Excel template and map system fields to its column headers.</li>
                <li>
                  Give each mapping a <strong>name</strong> and select it from <strong>Saved mapping</strong> when loading ASP forms elsewhere in the app.
                </li>
                <li>Keep mappings until you edit, delete, or replace them (stored locally in this browser; use export/import for backup).</li>
              </ul>
            </div>
          </details>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="text-base font-semibold text-slate-900">Map ASP form columns</h3>
            <p className="mt-1 max-w-4xl text-xs leading-relaxed text-slate-500">
              Upload your ASP / shipment Excel template. Set the <strong>header cell</strong> (e.g. <code className="rounded bg-slate-100 px-1">H100</code>) — the row is where column titles are read from; all used columns in the sheet are listed for mapping. Set the{" "}
              <strong>first line-item cell</strong> (e.g. <code className="rounded bg-slate-100 px-1">A101</code>) — row where the first part line is written; each additional part fills the next row. Map system fields to Excel headers, then save.
            </p>

            <div className="mt-4 flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Saved mapping
                <select
                  value={selectedProfileId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedProfileId(id);
                    if (!id) resetDraft();
                    else loadProfileIntoDraft(id);
                  }}
                  className="input-field min-w-[220px]"
                >
                  <option value="">— Select a mapping to edit —</option>
                  {savedProfiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name || p.id}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Mapping name
                <input
                  type="text"
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  placeholder="e.g. Customer A — approval template"
                  className="input-field min-w-[220px]"
                  autoComplete="off"
                />
              </label>
              <label className="flex flex-col gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Excel template
                <span className="flex flex-wrap items-center gap-2">
                  <input
                    ref={excelInputRef}
                    type="file"
                    accept=".xlsx,.xls,.xlsm"
                    className="max-w-[min(100%,280px)] text-sm"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      handleExcelFile(f);
                    }}
                  />
                  {layoutMode === "excel_columns" && (
                    <button type="button" disabled={readColumnsDisabled} onClick={handleReadColumnsClick} className="secondary-btn whitespace-nowrap disabled:opacity-40">
                      Read &amp; populate columns
                    </button>
                  )}
                </span>
                <span className="mt-1 block max-w-xl text-[11px] text-slate-500">{fileMeta}</span>
              </label>
              {sheetNames.length > 0 && (
                <label className="flex flex-col gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Sheet
                  <select
                    value={mappingCurrentSheet}
                    onChange={(e) => {
                      setMappingCurrentSheet(e.target.value);
                    }}
                    className="input-field min-w-[180px]"
                  >
                    {sheetNames.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label className="flex flex-col gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Header cell
                <input
                  type="text"
                  value={headerCell}
                  onChange={(e) => setHeaderCell(e.target.value)}
                  onBlur={() => {
                    ensureFirstLineBelowHeader();
                  }}
                  className="input-field w-[88px] font-mono uppercase"
                  spellCheck={false}
                  title="Row where header labels are read"
                />
              </label>
              <label className="flex flex-col gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                First line-item cell
                <input
                  type="text"
                  value={firstLineCell}
                  onChange={(e) => setFirstLineCell(e.target.value)}
                  onBlur={(ev) => {
                    const h = parseHeaderAnchorFromCellInput(headerCell);
                    const f = parseHeaderAnchorFromCellInput(ev.target.value);
                    if (h && f && (f.row1 < h.row1 || (f.row1 === h.row1 && f.col0 === h.col0))) {
                      setFirstLineCell(formatExcelCellRef(h.row1 + 1, f.col0));
                      toast("First line-item must be below the header — adjusted.", "warn");
                    }
                  }}
                  className="input-field w-[88px] font-mono uppercase"
                  spellCheck={false}
                />
              </label>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3" role="radiogroup" aria-label="Mapping layout">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Mapping layout</span>
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                <input type="radio" name="aspLayout" checked={layoutMode === "excel_columns"} onChange={() => setLayoutMode("excel_columns")} />
                Excel columns (from file)
              </label>
              <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                <input type="radio" name="aspLayout" checked={layoutMode === "system_fields"} onChange={() => setLayoutMode("system_fields")} />
                System fields
              </label>
            </div>
            <p className="mt-2 max-w-4xl text-xs text-slate-500">
              Choose <strong>Excel columns</strong> to map each detected column header to a system field, or <strong>System fields</strong> to walk every planner field. Use <strong>Group</strong> for Group 1 (once per sheet) vs Group 2 (per part line). To lock placement, type an{" "}
              <strong>exact cell</strong> (e.g. <code className="rounded bg-slate-100 px-1">A9</code>).
            </p>

            <div className="mt-4 rounded-2xl border border-slate-200 p-4">
              <h4 className="text-sm font-semibold text-slate-900">Custom fields</h4>
              <p className="mt-1 max-w-2xl text-xs text-slate-500">
                Add named fields and map them to Excel like built-in fields. Choose Line vs Shipment as the default; you can still override Group per mapping in the table.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  value={customFieldName}
                  onChange={(e) => setCustomFieldName(e.target.value)}
                  placeholder="Field name"
                  maxLength={120}
                  className="input-field min-w-[180px]"
                />
                <select value={customFieldScope} onChange={(e) => setCustomFieldScope(e.target.value)} className="input-field min-w-[160px]">
                  <option value="line">Line (per part)</option>
                  <option value="shipment">Shipment (once)</option>
                </select>
                <button type="button" onClick={addCustomField} className="primary-btn">
                  Add field
                </button>
              </div>
              <ul className="mt-3 flex flex-wrap gap-2">
                {customFields.map((cf) => (
                  <li key={cf.id} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs">
                    <span className="text-[10px] font-bold uppercase text-slate-500">{cf.scope === "shipment" ? "Shipment" : "Line"}</span>
                    <span>{cf.label}</span>
                    <button type="button" onClick={() => removeCustomField(cf.id)} className="text-red-600 hover:underline">
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-4 max-h-[360px] overflow-auto rounded-xl border border-slate-200">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="sticky top-0 z-[1] bg-slate-100">
                    <th className="border-b border-slate-200 px-3 py-2 font-semibold">
                      {layoutMode === "excel_columns" ? "Excel column (from sheet)" : "System field"}
                    </th>
                    <th className="border-b border-slate-200 px-3 py-2 font-semibold">
                      {layoutMode === "excel_columns" ? "Map to system field" : "Excel column (header)"}
                    </th>
                    <th className="border-b border-slate-200 px-3 py-2 font-semibold">Group</th>
                    <th className="border-b border-slate-200 px-3 py-2 font-semibold">Exact cell</th>
                  </tr>
                </thead>
                <tbody>
                  {layoutMode === "excel_columns" &&
                    (!mappingCurrentHeaders.length ? (
                      <tr>
                        <td colSpan={4} className="px-4 py-8 text-center text-slate-500">
                          Upload an Excel file and set the header cell so column names appear here. Then assign each column to a system field.
                        </td>
                      </tr>
                    ) : (
                      mappingCurrentHeaders.map((h, colIdx) => {
                        const sysKey = findSystemKeyForExcelHeader(columnMap, h);
                        const e = sysKey ? normalizeColumnMapEntry(columnMap[sysKey]) : null;
                        return (
                          <tr key={`excel-col-${colIdx}`} className="border-b border-slate-100">
                            <td className="px-3 py-2 align-middle">{h}</td>
                            <td className="px-3 py-2 align-middle">
                              <select
                                value={sysKey}
                                onChange={(ev) => {
                                  updateExcelColumnAssignment(h, ev.target.value, e?.cellRef || "");
                                }}
                                className="h-8 max-w-[280px] rounded-lg border border-slate-200 bg-white px-2 text-xs"
                              >
                                <option value="">— Do not assign —</option>
                                {SYSTEM_FIELDS_FOR_MAPPING.map((sf) => (
                                  <option key={sf.key} value={sf.key}>
                                    {sf.label}
                                  </option>
                                ))}
                                {customFields.map((cf) => (
                                  <option key={cf.id} value={`${CUSTOM_FIELD_PREFIX}${cf.id}`}>
                                    {cf.label} (custom)
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-3 py-2 align-middle">
                              <AspScopeGroupSelect
                                fieldKey={sysKey}
                                customFields={customFields}
                                valueOverrides={fieldScopeOverrides}
                                onChange={onScopeOverrideChange}
                                disabled={!sysKey}
                              />
                            </td>
                            <td className="px-3 py-2 align-middle">
                              <input
                                type="text"
                                value={e?.cellRef || ""}
                                onChange={(ev) => {
                                  if (sysKey) updateExcelColumnAssignment(h, sysKey, ev.target.value);
                                }}
                                placeholder="e.g. A9"
                                disabled={!sysKey}
                                className="input-field h-8 w-24 font-mono text-xs uppercase disabled:opacity-40"
                              />
                            </td>
                          </tr>
                        );
                      })
                    ))}
                  {layoutMode === "system_fields" &&
                    (() => {
                      let lastGroup = "";
                      return fieldRows.map((sf) => {
                        const rows = [];
                        if (sf.group && sf.group !== lastGroup) {
                          lastGroup = sf.group;
                          rows.push(
                            <tr key={`g-${sf.group}`} className="bg-slate-100">
                              <td colSpan={4} className="px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                                {sf.group}
                              </td>
                            </tr>
                          );
                        }
                        const entry = normalizeColumnMapEntry(columnMap[sf.key]);
                        const picked = entry?.header || "";
                        rows.push(
                          <tr key={sf.key} className="border-b border-slate-100">
                            <td className="px-3 py-2 align-middle">
                              {sf.label}
                              {sf.isCustom ? " · custom" : ""}
                            </td>
                            <td className="px-3 py-2 align-middle">
                              <select
                                value={picked}
                                onChange={(ev) => updateSystemColumnMap(sf.key, ev.target.value, entry?.cellRef || "")}
                                className="h-8 max-w-[280px] rounded-lg border border-slate-200 bg-white px-2 text-xs"
                              >
                                <option value="">— No header column —</option>
                                {picked && !mappingCurrentHeaders.includes(picked) ? (
                                  <option value={picked}>{picked} (saved)</option>
                                ) : null}
                                {mappingCurrentHeaders.map((hh) => (
                                  <option key={hh} value={hh}>
                                    {hh}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="px-3 py-2 align-middle">
                              <AspScopeGroupSelect fieldKey={sf.key} customFields={customFields} valueOverrides={fieldScopeOverrides} onChange={onScopeOverrideChange} />
                            </td>
                            <td className="px-3 py-2 align-middle">
                              <input
                                type="text"
                                value={entry?.cellRef || ""}
                                onChange={(ev) => updateSystemColumnMap(sf.key, picked, ev.target.value)}
                                placeholder="e.g. A9"
                                className="input-field h-8 w-24 font-mono text-xs uppercase"
                              />
                            </td>
                          </tr>
                        );
                        return rows;
                      }).flat();
                    })()}
                </tbody>
              </table>
            </div>

            <p className="mt-3 text-[11px] text-slate-500">
              Mappings are stored in this browser (local storage) until you change or delete them. Export a JSON backup to share or move to another machine.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" onClick={handleSave} className="primary-btn">
                Save mapping
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedProfileId("");
                  resetDraft();
                }}
                className="secondary-btn"
              >
                New mapping
              </button>
              <button type="button" onClick={handleDelete} className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-xs font-semibold text-red-700 hover:bg-red-100">
                Delete selected
              </button>
              <button type="button" onClick={handleExport} className="secondary-btn">
                Export JSON backup
              </button>
              <label className="secondary-btn inline-flex cursor-pointer items-center">
                Import JSON
                <input
                  ref={importInputRef}
                  type="file"
                  accept="application/json,.json"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    handleImport(f);
                  }}
                />
              </label>
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="text-base font-semibold text-slate-900">Load board column layout (per customer)</h3>
            <p className="mt-1 max-w-4xl text-xs leading-relaxed text-slate-500">
              Choose the customer and ship-to on <strong>Shipping Planner</strong> before opening ASP Settings. Upload a template and map columns above, then use{" "}
              <strong>Build columns from Excel mapping</strong> to mirror those headers on the Load Load Board grid. Edit groups, titles, and data fields below, then save — the board uses the server schema when the customer has a code; name-only customers store the layout in this browser.
            </p>
            <p className="mt-2 text-xs text-slate-600">
              <span className="font-semibold text-slate-800">Current planner scope:</span>{" "}
              {plannerCustomerKey
                ? `${plannerCustomerKey}${plannerShipTo && plannerShipTo !== "all" ? ` · Ship-To: ${plannerShipTo}` : ""}`
                : "All customers — select a customer to save a layout."}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" onClick={handleLbImportFromMapping} className="secondary-btn text-xs">
                Build columns from Excel mapping
              </button>
              <button type="button" onClick={handleLbResetDefault} className="secondary-btn text-xs">
                Reset to default grid
              </button>
              <button type="button" disabled={lbSaving || lbLoading} onClick={() => handleLbSaveSchema()} className="primary-btn text-xs disabled:opacity-50">
                {lbSaving ? "Saving…" : "Save load board format"}
              </button>
            </div>
            {lbLoading ? (
              <p className="mt-3 text-sm text-slate-500">Loading saved layout…</p>
            ) : (
              <div className="mt-3 max-h-[280px] overflow-auto rounded-xl border border-slate-200">
                <table className="w-full border-collapse text-left text-xs">
                  <thead>
                    <tr className="sticky top-0 z-[1] bg-slate-100">
                      <th className="border-b border-slate-200 px-2 py-2 font-semibold">Group</th>
                      <th className="border-b border-slate-200 px-2 py-2 font-semibold">Column title</th>
                      <th className="border-b border-slate-200 px-2 py-2 font-semibold">Data field</th>
                      <th className="border-b border-slate-200 px-2 py-2 font-semibold">Align</th>
                      <th className="border-b border-slate-200 px-2 py-2 font-semibold"> </th>
                    </tr>
                  </thead>
                  <tbody>
                    {lbColumns.map((col, i) => (
                      <tr key={`lb-col-${i}-${col.fieldKey}`} className="border-b border-slate-100">
                        <td className="px-2 py-1 align-middle">
                          <input
                            type="text"
                            value={col.group || ""}
                            onChange={(e) => updateLbRow(i, { group: e.target.value })}
                            className="input-field h-8 w-[min(100%,160px)] text-xs"
                          />
                        </td>
                        <td className="px-2 py-1 align-middle">
                          <input
                            type="text"
                            value={col.label || ""}
                            onChange={(e) => updateLbRow(i, { label: e.target.value })}
                            className="input-field h-8 w-[min(100%,180px)] text-xs"
                          />
                        </td>
                        <td className="px-2 py-1 align-middle">
                          <select
                            value={col.fieldKey || ""}
                            onChange={(e) => {
                              const fk = e.target.value;
                              const { group, align } = loadBoardGroupAndAlignForFieldKey(fk);
                              updateLbRow(i, { fieldKey: fk, group, align });
                            }}
                            className="h-8 max-w-[220px] rounded-lg border border-slate-200 bg-white px-2 text-xs"
                          >
                            <option value="">— data field —</option>
                            {SYSTEM_FIELDS_FOR_MAPPING.map((sf) => (
                              <option key={sf.key} value={sf.key}>
                                {sf.label}
                              </option>
                            ))}
                            {customFields.map((cf) => (
                              <option key={cf.id} value={`${CUSTOM_FIELD_PREFIX}${cf.id}`}>
                                {cf.label} (custom)
                              </option>
                            ))}
                            {col.fieldKey &&
                            !SYSTEM_FIELDS_FOR_MAPPING.some((s) => s.key === col.fieldKey) &&
                            !customFields.some((cf) => `${CUSTOM_FIELD_PREFIX}${cf.id}` === col.fieldKey) ? (
                              <option value={col.fieldKey}>{String(col.fieldKey)} (saved)</option>
                            ) : null}
                          </select>
                        </td>
                        <td className="px-2 py-1 align-middle">
                          <select
                            value={col.align || "left"}
                            onChange={(e) => updateLbRow(i, { align: e.target.value })}
                            className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs"
                          >
                            <option value="left">left</option>
                            <option value="center">center</option>
                            <option value="right">right</option>
                          </select>
                        </td>
                        <td className="px-2 py-1 align-middle whitespace-nowrap">
                          <button type="button" className="mr-1 text-slate-500 hover:text-slate-900" onClick={() => moveLbRow(i, -1)} title="Move up">
                            ↑
                          </button>
                          <button type="button" className="mr-1 text-slate-500 hover:text-slate-900" onClick={() => moveLbRow(i, 1)} title="Move down">
                            ↓
                          </button>
                          <button type="button" className="text-red-600 hover:underline" onClick={() => removeLbRow(i)}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <button type="button" onClick={addLbRow} className="mt-2 text-xs font-semibold text-blue-700 hover:underline">
              + Add column
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlannerTabButton({ active, label, onClick, icon: Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition",
        active ? "bg-blue-700 text-white shadow" : "bg-slate-50 text-slate-600 hover:bg-blue-50 hover:text-slate-900"
      )}
    >
      <Icon className="h-4 w-4" />
      {label}
    </button>
  );
}

function PanelCard({ children }) {
  return <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">{children}</div>;
}

function StickyCell({ className = "", style, children, alternateRow = false }) {
  return (
    <td
      className={classNames("sticky z-[1]", alternateRow ? "bg-slate-200" : "bg-white", className)}
      style={style}
    >
      {children}
    </td>
  );
}

function toastVariantClass(variant) {
  if (variant === "error") return "bg-red-700";
  if (variant === "warn") return "bg-amber-800";
  if (variant === "success") return "bg-green-700";
  return "bg-slate-900";
}

function ToastStack({ toasts, onDismiss }) {
  return (
    <div className="fixed bottom-5 right-5 z-50 flex max-w-sm flex-col gap-2">
      {toasts.map((toast) => (
        <motion.button
          key={toast.id}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 6 }}
          type="button"
          onClick={() => onDismiss(toast.id)}
          className={classNames(
            "rounded-xl px-4 py-3 text-left text-sm font-medium text-white shadow-lg",
            toastVariantClass(toast.variant),
          )}
        >
          {toast.message}
        </motion.button>
      ))}
    </div>
  );
}

function pickDemandRow(row, ...keys) {
  if (!row || typeof row !== "object") return "";
  for (const key of keys) {
    if (key in row && row[key] != null && row[key] !== "") return row[key];
  }
  return "";
}

/** Like pickDemandRow but also matches property names case-insensitively. */
function pickDemandRowField(row, aliases = []) {
  const direct = pickDemandRow(row, ...aliases);
  if (direct !== "" && direct != null) return direct;
  if (!row || typeof row !== "object") return "";
  const lowerMap = {};
  Object.entries(row).forEach(([k, v]) => {
    if (v != null && v !== "") lowerMap[String(k).toLowerCase()] = v;
  });
  for (const alias of aliases) {
    const hit = lowerMap[String(alias).toLowerCase()];
    if (hit != null && hit !== "") return hit;
  }
  return "";
}

/** Cus_Demand_Detail.Id from a demand API row (several server property names). */
function pickDemandDetailIdFromRow(row) {
  const raw = pickDemandRowField(row, [
    "id",
    "Id",
    "cusDemandDetailId",
    "CusDemandDetailId",
    "demandDetailId",
    "DemandDetailId",
  ]);
  const id = Number(raw);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** ReleaseId as stored / displayed string (do not convert to number). */
function releaseIdAsString(releaseId) {
  const s = String(releaseId ?? "").trim();
  return s && s !== "—" ? s : "";
}

/** Part # for grouping/display: prefer Part join fields, then strip trailing " - …" from composite SQL labels. */
function pickDemandPartNumber(row) {
  const raw = pickDemandRow(
    row,
    "partPartNumber",
    "PartPartNumber",
    "partNumber",
    "PartNumber",
    "Part Number",
  );
  let s = String(raw == null ? "" : raw).trim();
  if (!s) return "";
  const cut = s.indexOf(" - ");
  if (cut > 0) s = s.slice(0, cut).trim();
  return s;
}

/**
 * Shipping planner `<select>` uses demo keys (e.g. ACME) or `__name:{displayName}` when the customer
 * only comes from the demand API. Load-board schema APIs may need ERP / external codes (CustomerIdExt).
 */
function resolvePlannerCustomerCodeForApi(sCustomer, shippingApiRows) {
  const raw = String(sCustomer || "").trim();
  if (!raw) return "";
  if (!raw.startsWith("__name:")) return raw;
  const name = raw.slice("__name:".length).trim();
  const rows = Array.isArray(shippingApiRows) ? shippingApiRows : [];
  for (const r of rows) {
    const cn = String(pickDemandRow(r, "customerName", "CustomerName") || "").trim();
    if (cn !== name) continue;
    const code = String(
      pickDemandRow(
        r,
        "arcustoId",
        "Arcusto_ID",
        "ArcustoId",
        "customerIdExt",
        "CustomerIdExt",
        "customerId",
        "CustomerId",
        "customerNumber",
        "CustomerNumber",
      ) || "",
    ).trim();
    if (code) return code;
  }
  return "";
}

/** dbo.Customer.Id from shipping demand detail — used for upsertShipmentsAndItems (not CustomerIdExt). */
function resolvePlannerCustomerIdForUpsert(sCustomer, shippingSelectedCustomerName, shippingApiRows) {
  const rows = Array.isArray(shippingApiRows) ? shippingApiRows : [];
  const nameFilter = String(shippingSelectedCustomerName || "").trim();
  const candidates = nameFilter
    ? rows.filter((r) => String(pickDemandRow(r, "customerName", "CustomerName") || "").trim() === nameFilter)
    : rows;
  const pool = candidates.length ? candidates : rows;
  for (const r of pool) {
    const id = String(pickDemandRow(r, "customerId", "CustomerId") || "").trim();
    if (id) return id;
  }
  const raw = String(sCustomer || "").trim();
  if (raw && !raw.startsWith("__name:") && /^\d+$/.test(raw)) return raw;
  return "";
}

function resolvePlannerCustomerNameForUpsert(sCustomer, shippingSelectedCustomerName, shippingApiRows) {
  const fromSelect = String(shippingSelectedCustomerName || "").trim();
  if (fromSelect) return fromSelect;
  const raw = String(sCustomer || "").trim();
  if (raw.startsWith("__name:")) return raw.slice("__name:".length).trim();
  const rows = Array.isArray(shippingApiRows) ? shippingApiRows : [];
  for (const r of rows) {
    const cn = String(pickDemandRow(r, "customerName", "CustomerName") || "").trim();
    if (cn) return cn;
  }
  return "";
}

function formatShippingReleaseIdDisplay(releaseId) {
  const s = String(releaseId ?? "").trim();
  return s && s !== "—" ? s : "";
}

/**
 * Shipment form header fields for a specific load column (used in Load Board modal and per-shipment Excel export).
 */
function computeLoadBoardShipmentFormPropsForIndex(
  loadIdx,
  {
    filteredShippingRows,
    shippingApiRows,
    shippingSelectedCustomerName,
    sShipTo,
    sShipDate,
    sCustomer,
  },
  options = {},
) {
  const sup = { ...DEFAULT_SUPPLIER_SHIP_FROM, ...(options.supplierDefaults || {}) };
  const nowIso = fmtDate(new Date());
  const anchorBoardRow =
    filteredShippingRows.find((r) => (r.loads?.[loadIdx] || 0) > 0) || filteredShippingRows[0] || null;

  const demandRows = filterRawShippingDemandRows(shippingApiRows, {
    customerName: shippingSelectedCustomerName,
    shipTo: sShipTo || "all",
  });

  const scanDemand = (...keys) => {
    for (const r of demandRows) {
      const v = pickDemandRow(r, ...keys);
      if (v !== "" && v != null) return v;
    }
    return "";
  };

  const shipToLine1 = firstNonEmpty(
    anchorBoardRow?.attn,
    scanDemand("attn", "ATTN", "shipToName", "ShipToName", "shipTo", "ShipTo"),
    sShipTo !== "all" ? sShipTo : "",
  );

  const shipToLine2 = firstNonEmpty(
    anchorBoardRow?.addr1,
    scanDemand("addr1", "ADDR1", "shipToAddress1", "ShipToAddress1", "address1", "Address1"),
  );

  const cityStateZip = firstNonEmpty(
    [anchorBoardRow?.city, [anchorBoardRow?.state, anchorBoardRow?.zip].filter(Boolean).join(" ")].filter(Boolean).join(", "),
    [
      scanDemand("city", "CITY"),
      [scanDemand("state", "STATE"), scanDemand("zip", "ZIP", "postalCode", "PostalCode")].filter(Boolean).join(" "),
    ]
      .filter(Boolean)
      .join(", "),
  );
  const shipToLine3 = cityStateZip;

  const savedAspNum = String(options.savedAspByLoadIndex?.[loadIdx] || "").trim();
  const shipmentNumber = firstNonEmpty(
    savedAspNum,
    scanDemand(
      "shipmentNumber",
      "ShipmentNumber",
      "shipmentNo",
      "ShipmentNo",
      "shipmentId",
      "ShipmentId",
      "asn",
      "ASN",
    ),
    anchorBoardRow?.releaseId && anchorBoardRow.releaseId !== "—" ? String(anchorBoardRow.releaseId) : "",
    sCustomer ? `${sCustomer}-${sShipDate || "ship"}-S${loadIdx + 1}` : `SHIP-${sShipDate || "date"}-S${loadIdx + 1}`,
  );

  const rev = firstNonEmpty(scanDemand("rev", "Rev", "revision", "Revision", "shipmentRev", "ShipmentRev"), "0");

  let totalBoxes = 0;
  let countedBoxLines = false;
  filteredShippingRows.forEach((row) => {
    const shipmentQty = row.loads?.[loadIdx] || 0;
    if (!shipmentQty) return;
    if (row.boxQty > 0) {
      totalBoxes += Math.ceil(shipmentQty / row.boxQty);
      countedBoxLines = true;
    }
  });

  const createdBy = firstNonEmpty(
    scanDemand("createdBy", "CreatedBy", "createdByName", "CreatedByName", "planner", "Planner"),
    getPlannerLoggedInDisplayName(),
  );

  const carrierName = firstNonEmpty(scanDemand("carrierName", "CarrierName", "carrier", "Carrier"), "");
  const carrierContact = firstNonEmpty(scanDemand("carrierContact", "CarrierContact"), "");
  const carrierPhone = firstNonEmpty(scanDemand("carrierPhone", "CarrierPhone", "carrierTel", "CarrierTel"), "");

  const readyRaw = firstNonEmpty(scanDemand("shipmentReadyDate", "ShipmentReadyDate"), sShipDate);
  const planRaw = firstNonEmpty(scanDemand("planDeliveryDate", "PlanDeliveryDate", "deliveryDate", "DeliveryDate"), sShipDate);
  const readyYmd = demandDateToYmd(readyRaw);
  const planYmd = demandDateToYmd(planRaw);

  return {
    creationDate: fmtUsDateFromIso(nowIso),
    createdBy: createdBy || "—",
    shipmentReadyDate: fmtUsDateFromIso(readyYmd),
    shipmentReadyTime: firstNonEmpty(scanDemand("shipmentReadyTime", "ShipmentReadyTime"), "11:30:00"),
    shippingDockHours: firstNonEmpty(scanDemand("shippingDockHours", "ShippingDockHours"), "00:00:00"),
    planDeliveryDate: fmtUsDateFromIso(planYmd),
    planDeliveryTime: firstNonEmpty(scanDemand("planDeliveryTime", "PlanDeliveryTime"), "12:30:00"),
    supplierName: sup.supplierName,
    supplierNumber: sup.supplierNumber,
    addressLineOne: sup.addressLineOne,
    addressLineTwo: sup.addressLineTwo,
    addressLineThree: sup.addressLineThree,
    supplierCity: sup.city,
    supplierState: sup.state,
    supplierZip: sup.zipCode,
    countryOfOrigin: sup.countryOfOrigin,
    phone: sup.phone,
    phoneExtension: sup.phoneExtension,
    freightTerms: sup.freightTerms,
    shipmentNumber,
    rev,
    carrierName,
    carrierContact,
    carrierPhone,
    totalBoxes: countedBoxLines ? String(totalBoxes) : "",
    totalPallets: "",
    totalWeight: "",
    shipToLine1,
    shipToLine2,
    shipToLine3,
  };
}

/** First row/object returned by `planner/upsertShipmentsAndItems` (SP result set). */
function pickUpsertShipmentResult(response) {
  const raw =
    response?.response ??
    response?.Response ??
    response?.data ??
    response?.Data ??
    response;
  if (Array.isArray(raw) && raw.length) return raw[0];
  if (
    raw &&
    typeof raw === "object" &&
    (raw.aspNum != null ||
      raw.ASPNum != null ||
      raw.AspNum != null ||
      raw.shipId != null ||
      raw.ShipId != null)
  ) {
    return raw;
  }
  return null;
}

function pickAspNumFromUpsertResult(result) {
  if (!result || typeof result !== "object") return "";
  return String(result.aspNum ?? result.ASPNum ?? result.AspNum ?? "").trim();
}

/** Converts MM-dd-yyyy (Load Board form) to yyyy-MM-dd for API / SQL DATE fields. */
function usMmDdYyyyToYmd(us) {
  const m = String(us || "")
    .trim()
    .match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!m) return "";
  return `${m[3]}-${m[1]}-${m[2]}`;
}

/**
 * Single shipment object for `planner/upsertShipmentsAndItems` `shippingJson` string.
 * Property names use PascalCase to align with SQL OPENJSON paths on the server.
 */
function buildPlannerUpsertShippingHeaderObject(formProps, { customerNum, customerName, shipDateYmdFallback, aspNumForUpsert }) {
  const userId = typeof localStorage !== "undefined" ? String(localStorage.getItem("UserId") || "").trim() : "";
  const shipReadyYmd = usMmDdYyyyToYmd(formProps.shipmentReadyDate) || shipDateYmdFallback || "";
  const planYmd = usMmDdYyyyToYmd(formProps.planDeliveryDate) || shipDateYmdFallback || "";
  const creationYmd = usMmDdYyyyToYmd(formProps.creationDate) || fmtDate(new Date());
  // ASPNum is assigned in SQL for new rows (CP####). Only send an existing CP number for updates.
  const aspNum =
    aspNumForUpsert != null
      ? String(aspNumForUpsert).trim()
      : String(formProps.shipmentNumber || "").trim();

  return {
    ASPNum: aspNum,
    PackingSlipNum: "",
    ShipReadyDate: shipReadyYmd,
    Status: "Open",
    CustomerNum: String(customerNum || "").trim(),
    CustomerName: String(customerName || "").trim(),
    UserId: userId,
    CreationDate: creationYmd,
    CreatedBy: String(formProps.createdBy || "").trim(),
    ShipReadyTime: String(formProps.shipmentReadyTime || "").trim(),
    ShipDockHrs: String(formProps.shippingDockHours || "").trim(),
    PlanDeliveryDate: planYmd,
    PlanDeliveryTime: String(formProps.planDeliveryTime || "").trim(),
    SupplierName: String(formProps.supplierName || "").trim(),
    AddressLineOne: String(formProps.addressLineOne || "").trim(),
    AddressLineTwo: String(formProps.addressLineTwo || "").trim(),
    AddressLineThree: String(formProps.addressLineThree || "").trim(),
    City: String(formProps.supplierCity || "").trim(),
    State: String(formProps.supplierState || "").trim(),
    ZipCode: String(formProps.supplierZip || "").trim(),
    CountryOrigin: String(formProps.countryOfOrigin || "").trim(),
    PhoneNum: String(formProps.phone || "").trim(),
    PhoneExt: String(formProps.phoneExtension || "").trim(),
    FreightTerms: String(formProps.freightTerms || "").trim(),
    CarrierName: String(formProps.carrierName || "").trim(),
    CarrierContact: String(formProps.carrierContact || "").trim(),
    CarrierPhone: String(formProps.carrierPhone || "").trim(),
    TotalBoxes: String(formProps.totalBoxes || "").trim(),
    TotalPallets: String(formProps.totalPallets || "").trim(),
    TotalWeight: String(formProps.totalWeight || "").trim(),
    ShipToAddrLineOne: String(formProps.shipToLine1 || "").trim(),
    ShipToAddrLineTwo: String(formProps.shipToLine2 || "").trim(),
    ShipToAddrLineThree: String(formProps.shipToLine3 || "").trim(),
    Expedited: "",
    Trailer: "",
    Driver: "",
    Carrier: "",
    Notes: "",
    Comments:
      formProps.rev != null && String(formProps.rev).trim() !== "" ? `Rev: ${String(formProps.rev).trim()}` : "",
    IsDeleted: false,
    PONumbers: "",
    IsDownloaded: false,
    FileUploadPath: "",
    CheckoutTime: "",
    photoPath: "",
    CarrierId: null,
  };
}

/** Planner / load board rows with a planned quantity on the given shipment column (0-based index). */
function shippingRowsWithQtyOnLoad(rows, loadIdx) {
  const i = Number(loadIdx);
  if (!Number.isFinite(i) || i < 0) return [];
  const list = Array.isArray(rows) ? rows : [];
  return list.filter((row) => (Number(row?.loads?.[i]) || 0) > 0);
}

/** Upsert line qty fields: API expects a positive value — default missing/null/zero to 1. */
function upsertQtyDefaultOne(value) {
  if (value === null || value === undefined || value === "") return 1;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 1;
  return n;
}

function loadBoardUpsertScalar(value) {
  if (value === "" || value == null || value === "—") return "";
  return value;
}

/** Load board Dimensions L×W×H: default when blank. */
function loadBoardDimensionsOrDefault(value) {
  const s = String(value ?? "").trim();
  return s || "48x48";
}

function parseLoadBoardUpsertNumber(value) {
  const raw = loadBoardUpsertScalar(value);
  if (raw === "") return 0;
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

/** Normalize load-board column keys so overrides match between UI columns and upsert field names. */
function canonicalLoadBoardFieldKey(fieldKey) {
  const k = String(fieldKey || "").trim();
  if (!k) return "";
  const lower = k.toLowerCase();
  if (lower === "numberofboxes" || lower === "numboxes") return "numberOfBoxes";
  if (lower === "qtyperbox") return "qtyPerBox";
  if (lower === "palletsqty" || lower === "palletqty" || lower === "palletsqnty" || lower === "palletqnty") {
    return "palletsQty";
  }
  if (lower === "plannedshipquantity" || lower === "plannedshipqty") return "plannedShipQuantity";
  return k;
}

/** User-edited cell value for a row (matches any alias of the canonical field key). */
function getLoadBoardCellOverride(cellOverrides, loadIdx, rowKey, fieldKey) {
  const canonical = canonicalLoadBoardFieldKey(fieldKey);
  if (!canonical) return undefined;
  const prefix = `${loadIdx}::${rowKey}::`;
  for (const ok of Object.keys(cellOverrides || {})) {
    if (!ok.startsWith(prefix)) continue;
    const fk = ok.slice(prefix.length);
    if (canonicalLoadBoardFieldKey(fk) === canonical) return cellOverrides[ok];
  }
  return undefined;
}

/**
 * Single load-board table cell (base + optional per-cell overrides).
 * Shared by the Load board UI and upsertShipmentsAndItems line build.
 */
function resolveLoadBoardCellValue({
  loadIdx,
  row,
  rowIndex,
  fieldKey,
  dueColumns = [],
  palletMode = false,
  cellOverrides = {},
}) {
  const key = String(fieldKey || "").trim();
  if (!key) return "";

  if (key !== "totalShipWeight") {
    const overridden = getLoadBoardCellOverride(cellOverrides, loadIdx, row.rowKey, key);
    if (overridden !== undefined) {
      if (key === "dimensionsLWH") return loadBoardDimensionsOrDefault(overridden);
      return overridden;
    }
  }

  const assignedSum = row.loads.reduce((sum, value) => sum + (value || 0), 0);
  const displayGrandTotal = dueColumns.length ? sumForDates(row, dueColumns, "qtyByDueDate") : row.grandTotal;
  const unassigned = displayGrandTotal - assignedSum;
  const shipmentQty = row.loads[loadIdx] || 0;

  if (key.startsWith("due:")) {
    const due = key.slice(4);
    const remaining = row.remainingByDueDate?.[due] ?? row.qtyByDueDate?.[due] ?? 0;
    return remaining > 0 ? remaining : "—";
  }

  const lineNo = String(rowIndex + 1).padStart(2, "0");
  const map = {
    lineNumber: lineNo,
    partNumber: row.sku,
    partDescription: row.desc,
    poNumber: row.po,
    palletsQty: formatShipmentUnitRatio(shipmentQty, row.partsonaPallet),
    palletInv: shippingPalletInvDisplay(row, palletMode),
    qtyPerBox: row.boxQty,
    numberOfBoxes: formatShipmentUnitRatio(shipmentQty, row.boxQty),
    shipmentReleaseId: formatShippingReleaseIdDisplay(row.releaseId),
    plannedShipQuantity: shipmentQty || "—",
    grandTotal: shippingLineQtyAsPalletsDisplay(displayGrandTotal, row, palletMode),
    unassigned: shippingLineQtyAsPalletsDisplay(unassigned, row, palletMode),
    source: row.source,
    destination: row.destination,
    uom: "EA",
    mixedPalletYn: "N",
    dimensionsLWH: loadBoardDimensionsOrDefault(row?.palletDimensions),
    totalShipWeight: computeLoadBoardTotalShipWeightEach(shipmentQty, row),
    totalCube: "",
    deleteYn: "",
    approvedShipQuantity: "",
    approvedDeliveryDate: "",
    approvedDeliveryTime: "",
    plannerSignature: "",
    shipmentReleased: formatShippingReleaseIdDisplay(row.releaseId),
  };

  if (Object.prototype.hasOwnProperty.call(map, key)) return map[key];
  return "";
}

/** Demand detail candidates for a board row (already scoped to part + PO + release, etc.). */
function listDemandDetailCandidates(row) {
  const out = [];
  const byDue = row?.demandDetailsByDueDate || {};
  Object.entries(byDue).forEach(([dueIso, details]) => {
    if (!Array.isArray(details)) return;
    details.forEach((d) => {
      const qty = Number(d?.qty) || 0;
      const id = Number(d?.id);
      if (qty > 0) {
        out.push({
          dueIso,
          cusDemandDetailId: Number.isFinite(id) && id > 0 ? id : null,
          qty,
          releaseId: d?.releaseId || "",
        });
      }
    });
  });
  if (!out.length) {
    Object.entries(row?.qtyByDueDate || {}).forEach(([dueIso, qty]) => {
      const q = Number(qty) || 0;
      if (q > 0) {
        out.push({
          dueIso,
          cusDemandDetailId: null,
          qty: q,
          releaseId: row?.releaseId || "",
        });
      }
    });
  }
  out.sort((a, b) => String(a.dueIso).localeCompare(String(b.dueIso)));
  return out;
}

/**
 * Pick one Cus_Demand_Detail row for upsert: same PO line, qty must cover planned ship qty.
 * When one due date column is active, only demand on that date is considered.
 */
function resolveUpsertDemandMatch(row, dueColumns, plannedQty) {
  const needQty = Number(plannedQty) || 0;
  if (needQty <= 0) return null;

  const cols = Array.isArray(dueColumns) ? dueColumns.filter(Boolean) : [];
  let candidates = listDemandDetailCandidates(row);
  const targetRelease = releaseIdAsString(row?.releaseId);
  if (targetRelease) {
    const releaseMatches = candidates.filter((c) => {
      const candRelease = releaseIdAsString(c.releaseId);
      return !candRelease || candRelease === targetRelease;
    });
    if (releaseMatches.length) candidates = releaseMatches;
  }
  if (cols.length === 1) {
    candidates = candidates.filter((c) => c.dueIso === cols[0]);
  } else if (cols.length > 1) {
    const allowed = new Set(cols);
    candidates = candidates.filter((c) => allowed.has(c.dueIso));
  }

  const fits = candidates.filter((c) => c.qty >= needQty);
  if (!fits.length) return null;

  fits.sort((a, b) => {
    const aHasId = a.cusDemandDetailId ? 0 : 1;
    const bHasId = b.cusDemandDetailId ? 0 : 1;
    if (aHasId !== bHasId) return aHasId - bHasId;
    const byDue = String(a.dueIso).localeCompare(String(b.dueIso));
    if (byDue !== 0) return byDue;
    return a.qty - b.qty;
  });

  const pick = fits[0];
  return {
    dueDateIso: pick.dueIso,
    cusDemandDetailId: pick.cusDemandDetailId,
    releaseId: releaseIdAsString(pick.releaseId) || targetRelease || "",
  };
}

/**
 * Look up Cus_Demand_Detail.Id from raw shipping demand API rows (Part + PO + DueDate + ReleaseId).
 */
function findCusDemandDetailIdFromApiRows(apiRows, { partNumber, poNumber, dueDateIso, releaseId }) {
  const part = String(partNumber || "").trim();
  const po = String(poNumber || "").trim();
  const due = String(dueDateIso || "").trim();
  const rel = releaseIdAsString(releaseId);
  if (!part || !Array.isArray(apiRows) || !apiRows.length) return null;

  for (const r of apiRows) {
    const rowPart = pickDemandPartNumber(r);
    if (rowPart !== part) continue;
    const rowPo = String(
      pickDemandRow(r, "po", "PO", "poNumber", "PONumber", "purchaseOrder", "PurchaseOrder") || "",
    ).trim();
    if (po && rowPo && rowPo !== po) continue;
    if (due) {
      const rowDue = parseDemandDueDateToIsoYmd(pickDemandDueDateRaw(r));
      if (rowDue && rowDue !== due) continue;
    }
    if (rel) {
      const rowRel = releaseIdAsString(
        pickDemandRow(r, "releaseId", "ReleaseId", "release", "Release", "shipmentReleaseId", "ShipmentReleaseId"),
      );
      if (rowRel && rowRel !== rel) continue;
    }
    const id = pickDemandDetailIdFromRow(r);
    if (id) return id;
  }
  return null;
}

/** Build upsert line items from the same values shown in the Load board table (incl. user edits). */
function buildPlannerUpsertShippingItemsFromLoadBoard({
  loadIdx,
  rows,
  cellOverrides = {},
  dueColumns = [],
  palletMode = false,
  shippingApiRows = [],
}) {
  const lines = shippingRowsWithQtyOnLoad(rows, loadIdx);
  const pickCell = (row, rowIndex, fieldKey) =>
    resolveLoadBoardCellValue({
      loadIdx,
      row,
      rowIndex,
      fieldKey,
      dueColumns,
      palletMode,
      cellOverrides,
    });

  return lines.map((row, rowIndex) => {
    const numBoxes = upsertQtyDefaultOne(parseLoadBoardUpsertNumber(pickCell(row, rowIndex, "numberOfBoxes")));
    const qtyPerBox = upsertQtyDefaultOne(parseLoadBoardUpsertNumber(pickCell(row, rowIndex, "qtyPerBox")));
    const numPallets = upsertQtyDefaultOne(parseLoadBoardUpsertNumber(pickCell(row, rowIndex, "palletsQty")));
    const plannedQty = parseLoadBoardUpsertNumber(pickCell(row, rowIndex, "plannedShipQuantity"));
    const totalShipWeight = upsertQtyDefaultOne(parseLoadBoardUpsertNumber(pickCell(row, rowIndex, "totalShipWeight")));
    const releaseIdRaw =
      loadBoardUpsertScalar(pickCell(row, rowIndex, "shipmentReleased")) ||
      loadBoardUpsertScalar(pickCell(row, rowIndex, "shipmentReleaseId")) ||
      row?.releaseId;
    const demandMatch = resolveUpsertDemandMatch(row, dueColumns, plannedQty);
    const releaseId = releaseIdAsString(demandMatch?.releaseId || releaseIdRaw);
    const poRaw = String(loadBoardUpsertScalar(pickCell(row, rowIndex, "poNumber")) || "").trim();
    const poNumber = poRaw && poRaw !== "—" ? poRaw : String(row?.po || "").trim().replace(/^—$/, "");
    const partNumber = String(loadBoardUpsertScalar(pickCell(row, rowIndex, "partNumber")) || "").trim();
    let cusDemandDetailId = demandMatch?.cusDemandDetailId ?? null;
    if (!cusDemandDetailId && demandMatch?.dueDateIso) {
      cusDemandDetailId = findCusDemandDetailIdFromApiRows(shippingApiRows, {
        partNumber,
        poNumber,
        dueDateIso: demandMatch.dueDateIso,
        releaseId,
      });
    }

    return {
      PartNumber: partNumber,
      Description: String(loadBoardUpsertScalar(pickCell(row, rowIndex, "partDescription")) || ""),
      PONumber: poNumber && poNumber !== "—" ? poNumber : "",
      NumBoxes: String(numBoxes),
      QtyPerBox: qtyPerBox,
      NumPallets: String(numPallets),
      PlannedShipQty: plannedQty > 0 ? plannedQty : upsertQtyDefaultOne(0),
      UOM: String(loadBoardUpsertScalar(pickCell(row, rowIndex, "uom")) || "EA"),
      MixedPallet: String(loadBoardUpsertScalar(pickCell(row, rowIndex, "mixedPalletYn")) || "N"),
      Dimensions: loadBoardDimensionsOrDefault(loadBoardUpsertScalar(pickCell(row, rowIndex, "dimensionsLWH"))),
      TotalShipWeight: String(totalShipWeight),
      TotalCube: "",
      Defer: "",
      ShipmentReleaseId: releaseId,
      DueDate: demandMatch?.dueDateIso || null,
      CusDemandDetailId: cusDemandDetailId,
      Comments: "",
      IsDeleted: false,
      PartId: null,
      _demandMatchError:
        plannedQty > 0 && !demandMatch
          ? `No demand for PO ${poNumber || "—"} with quantity at least ${plannedQty} for part ${partNumber || "line"}.`
          : null,
    };
  });
}

function stripLoadBoardUpsertItemMeta(item) {
  if (!item || typeof item !== "object") return item;
  const { _demandMatchError, ...rest } = item;
  return rest;
}

function formatApiDueDate(raw) {
  if (raw == null || raw === "") return "—";
  const s = String(raw);
  const ymd = s.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return fmtDueLabel(ymd);
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function displayDemandQty(row, uom) {
  const q = Number(pickDemandRow(row, "quantity", "Quantity"));
  if (!Number.isFinite(q)) return "—";
  if (uom !== "pallet") return q;
  const perPallet = Number(pickDemandRow(row, "partsonaPallet", "PartsonaPallet"));
  if (Number.isFinite(perPallet) && perPallet > 0) {
    const v = q / perPallet;
    return Number.isInteger(v) ? v : Number(v.toFixed(2));
  }
  return q;
}

/** Load board line: planned ship qty ÷ parts per box / per pallet (matches demand JSON partsinaBox / partsonaPallet). */
function formatShipmentUnitRatio(shipmentQty, perUnit) {
  const q = Number(shipmentQty);
  const p = Number(perUnit);
  if (!Number.isFinite(q) || q <= 0 || !Number.isFinite(p) || p <= 0) return "";
  const r = q / p;
  if (Math.abs(r - Math.round(r)) < 1e-9) return String(Math.round(r));
  return String(Number(r.toFixed(2)));
}

/** Fixed add-on (lbs or business constant) for total ship weight line on load board / ASP export. */
const LOAD_BOARD_TOTAL_SHIP_WEIGHT_ADDEND = 35;

/**
 * Total ship weight for load board / ASP line: (planned ship qty × part weight) + fixed add-on.
 */
function computeLoadBoardTotalShipWeightEach(shipmentQty, row) {
  const qty = Number(shipmentQty) || 0;
  const partWeight = Number(row?.partWeight);
  if (!Number.isFinite(partWeight)) return "";
  const total = qty * partWeight + LOAD_BOARD_TOTAL_SHIP_WEIGHT_ADDEND;
  if (!Number.isFinite(total)) return "";
  if (Math.abs(total - Math.round(total)) < 1e-9) return String(Math.round(total));
  return String(Number(total.toFixed(2)));
}

function shippingPalletInvDisplay(row, palletMode) {
  if (!palletMode) return row?.palletInv ?? "";
  let onHand = Number(row?.inventoryOnHand);
  if (!Number.isFinite(onHand)) onHand = Number(row?.palletInv);
  const perPallet = Number(row?.partsonaPallet);
  if (!Number.isFinite(onHand) || !Number.isFinite(perPallet) || perPallet <= 0) return "";
  const v = onHand / perPallet;
  return Number.isInteger(v) ? v : Number(v.toFixed(2));
}

/** Due-bucket ship qty (each) shown as pallets when palletMode — same divisor as Pallet Inv (`partsonaPallet`). */
function shippingDueShipQtyDisplay(remainingEach, row, palletMode) {
  const r = Number(remainingEach);
  if (!Number.isFinite(r) || r <= 0) return "—";
  if (!palletMode) return r;
  return formatShipmentUnitRatio(r, row?.partsonaPallet) || "—";
}

/** Sum of due remainings: pallets when palletMode (total each ÷ partsonaPallet). */
function shippingDueTotalRemainingDisplay(row, dueColumns, palletMode) {
  const total = sumForDates(row, dueColumns, "remainingByDueDate");
  const n = Number(total);
  if (!palletMode) return total;
  if (!Number.isFinite(n)) return "—";
  if (n === 0) return 0;
  return formatShipmentUnitRatio(total, row?.partsonaPallet) || "—";
}

/** Grand total / unassigned (each) → pallets for shipping board when pallet mode (supports negative unassigned). */
function shippingLineQtyAsPalletsDisplay(eachQty, row, palletMode) {
  if (!palletMode) return eachQty;
  const q = Number(eachQty);
  const p = Number(row?.partsonaPallet);
  if (!Number.isFinite(p) || p <= 0) return "—";
  if (!Number.isFinite(q)) return "—";
  if (q === 0) return 0;
  const r = q / p;
  if (Math.abs(r - Math.round(r)) < 1e-9) return Math.round(r);
  return Number(r.toFixed(2));
}

/** Shipment column input display: stored each qty → pallets when palletMode. */
function shippingShipmentLoadDisplay(eachQty, row, palletMode) {
  const q = Number(eachQty);
  if (!Number.isFinite(q) || q === 0) return "";
  if (!palletMode) return q;
  return formatShipmentUnitRatio(q, row?.partsonaPallet) || "";
}

/** Shipment column user input → each qty for internal storage. */
function shippingShipmentLoadInputToEach(rawValue, row, palletMode) {
  const n = Number(rawValue);
  if (!Number.isFinite(n)) return 0;
  const clamped = Math.max(0, n);
  if (!palletMode) return clamped;
  const perPallet = Number(row?.partsonaPallet);
  if (!Number.isFinite(perPallet) || perPallet <= 0) return 0;
  return clamped * perPallet;
}

/** Truck capacity in each units: Pallet Qty (`partsonaPallet`) × pallet positions per truck. */
function getShippingTruckQtyLimit(row) {
  const perPallet = Number(row?.partsonaPallet);
  if (!Number.isFinite(perPallet) || perPallet <= 0) return null;
  return perPallet * SHIPMENT_TRUCK_PALLET_SLOTS;
}

function shipmentQtyExceedsTruckLimit(row, shipmentQtyEach) {
  const limit = getShippingTruckQtyLimit(row);
  if (limit == null) return false;
  const q = Number(shipmentQtyEach);
  if (!Number.isFinite(q) || q <= 0) return false;
  return q > limit;
}

/**
 * Matrix "on hand" column by toggle (Demand planner):
 * - demandOnHand: API `onHand` / `OnHand`; if absent, fall back to `inventoryOnHand` for older payloads.
 * - iqmsOnHand: API `iqmsOnHand` / `IQMSOnHand` only.
 */
function getQTyNumeric(row, source) {
  if (source === "iqmsOnHand") {
    const raw = pickDemandRow(row, "iqmsOnHand", "IQMSOnHand");
    if (raw === "" || raw == null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  let raw = pickDemandRow(row, "onHand", "OnHand");
  if (raw === "" || raw == null) {
    raw = pickDemandRow(row, "inventoryOnHand", "InventoryOnHand");
  }
  if (raw === "" || raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Sticky column widths / left offsets for Demand matrix (Part, Description, Status, On-hand). */
const DM_COL_PART = 112;
const DM_COL_DESC = 152;
const DM_COL_STATUS = 100;
const DM_COL_ONHAND = 118;
const DM_LEFT_STATUS = DM_COL_PART + DM_COL_DESC;
const DM_LEFT_ONHAND = DM_LEFT_STATUS + DM_COL_STATUS;

function Modal({ open, title, subtitle, onClose, children, panelClassName }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/50 p-5">
      <div className={classNames("flex max-h-[85vh] w-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl", panelClassName || "max-w-6xl")}>
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
            <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 hover:text-slate-900">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-auto p-5">{children}</div>
      </div>
    </div>
  );
}

function ShipmentTruckOverflowDialog({ open, shipmentLabel, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[10060] flex items-center justify-center bg-slate-950/50 p-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="shipment-truck-overflow-title"
    >
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
        <h2 id="shipment-truck-overflow-title" className="text-lg font-semibold text-slate-900">
          Truck capacity
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-slate-600">{SHIPMENT_TRUCK_OVERFLOW_MESSAGE}</p>
        {shipmentLabel ? (
          <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{shipmentLabel}</p>
        ) : null}
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={onCancel} className="secondary-btn">
            Cancel
          </button>
          <button type="button" onClick={onConfirm} className="primary-btn">
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

function LoadBoardShipmentFormHeader({ formProps, layout }) {
  const L = normalizeShipmentFormLayoutFromApi(layout);
  if (!L) return null;
  const fp = formProps || {};

  const FieldRow = ({ label, value, valueClassName }) => (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-x-3 gap-y-1 border-b border-slate-200 py-1.5 text-[12px] leading-snug last:border-b-0">
      <div className="font-semibold uppercase tracking-wide text-slate-600">{label}</div>
      <div className={classNames("text-right font-medium text-slate-900", valueClassName)}>{value || "—"}</div>
    </div>
  );

  const Section = ({ eyebrow, title, children: sectionChildren }) => (
    <div className="rounded-xl border border-slate-200 bg-white">
      <div className="border-b border-slate-200 bg-slate-50 px-3 py-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{eyebrow}</div>
        <div className="text-sm font-semibold text-slate-900">{title}</div>
      </div>
      <div className="px-3 py-1">{sectionChildren}</div>
    </div>
  );

  return (
    <div className="w-full rounded-2xl border border-slate-200 bg-slate-50/80 p-3 shadow-sm">
      <div className="mb-2 border-b border-slate-200 pb-2 text-center text-sm font-bold uppercase tracking-wide text-slate-800">Shipment Form</div>
      <div className="grid gap-3 lg:grid-cols-3">
        {L.sections.map((sec) => (
          <Section key={sec.id || sec.title} eyebrow={sec.eyebrow || ""} title={sec.title || ""}>
            {(sec.fields || []).map((f, idx) => {
              if (f.type === "dockNote") {
                return (
                  <div
                    key={`dock-${idx}`}
                    className="mt-2 rounded-lg border border-slate-200 bg-white px-2 py-2 text-[11px] leading-relaxed text-slate-700"
                  >
                    <span className="font-semibold text-slate-800">Note Text:</span> {L.dockAppointmentNote}
                  </div>
                );
              }
              if (f.type === "shipToLines") {
                return (
                  <div key={`st-${idx}`}>
                    <div className="py-2 text-[12px] font-semibold text-slate-800">{f.label || "Ship To:"}</div>
                    <div className="space-y-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12px] font-medium leading-snug text-slate-900">
                      <div className="bg-amber-100/80 px-1">{fp.shipToLine1 || "—"}</div>
                      <div className="bg-amber-100/80 px-1">{fp.shipToLine2 || "—"}</div>
                      <div className="bg-amber-100/80 px-1">{fp.shipToLine3 || "—"}</div>
                    </div>
                  </div>
                );
              }
              if (f.type === "aspEmail") {
                return (
                  <div key={`em-${idx}`}>
                    <div className="mt-3 text-[11px] text-slate-600">
                      <span className="font-semibold text-slate-800">Instruction:</span> EMAIL ASP FORM to the following:
                    </div>
                    <div className="mt-1 text-[12px]">
                      <span className="font-semibold text-slate-700">Email Address:</span>{" "}
                      <a className="font-semibold text-blue-700 underline" href={`mailto:${L.aspFormEmail}`}>
                        {L.aspFormEmail}
                      </a>
                    </div>
                  </div>
                );
              }
              const v = f.key ? fp[f.key] : "";
              return (
                <FieldRow
                  key={f.key || idx}
                  label={f.label != null ? f.label : f.key}
                  value={v}
                  valueClassName={f.highlight ? "bg-amber-100/80" : undefined}
                />
              );
            })}
          </Section>
        ))}
      </div>
    </div>
  );
}

export default function PlanningWorkspaceReact() {
  const [activePlanner, setActivePlanner] = useState("demand");
  const [toasts, setToasts] = useState([]);
  const [aspMappings, setAspMappings] = useState([]);
  const [aspSettingsOpen, setAspSettingsOpen] = useState(false);

  const [dCustomer, setDCustomer] = useState("");
  const [dShipTo, setDShipTo] = useState("all");
  const [dPartSearch, setDPartSearch] = useState("");
  const [dDateFrom, setDDateFrom] = useState(fmtDate(baseDates[0]));
  const [dDateTo, setDDateTo] = useState(fmtDate(baseDates[baseDates.length - 1]));
  const [dHighlightShortage, setDHighlightShortage] = useState(true);
  const [dHighlightLow, setDHighlightLow] = useState(true);
  /** demandOnHand → API onHand (then inventoryOnHand); iqmsOnHand → API iqmsOnHand / IQMSOnHand */
  const [dQTySource, setDQTySource] = useState("iqmsOnHand");
  /** Default Each; user can switch to Pallet or clear via segmented control empty branch */
  const [dUom, setDUom] = useState("each");
  /** When false (default), hide day columns with no demand; when true, show every day between From and To. */
  const [dShowOnlyDemandDaysInRange, setDShowOnlyDemandDaysInRange] = useState(true);
  /** When true, table day columns are today through the next 59 days (60 calendar days); From/To still drive the API. */
  const [dSixtyDayOutOfStock, setDSixtyDayOutOfStock] = useState(false);
  const [demandApiRows, setDemandApiRows] = useState([]);
  const [demandApiLoading, setDemandApiLoading] = useState(false);
  const [demandApiError, setDemandApiError] = useState(null);
  /** After customer changes, first successful demand load sets From/To from API data once (manual date edits are kept). */
  const demandDatesFittedForCustomerKeyRef = useRef("");
  /** Customer display names from `Customer/getall` (Demand planner `<select>`). */
  const [demandCustomerDropdownFromApi, setDemandCustomerDropdownFromApi] = useState([]);

  const [sCustomer, setSCustomer] = useState("");
  const [sShipDate, setSShipDate] = useState(fmtDate(baseDates[0]));
  const [sShipTo, setSShipTo] = useState("all");
  const [sPartSearch, setSPartSearch] = useState("");
  const [sPoSearch, setSPoSearch] = useState("");
  const [sSourceFilter, setSSourceFilter] = useState("all");
  const [sDestinationFilter, setSDestinationFilter] = useState("all");
  const [sSelectedDueDates, setSSelectedDueDates] = useState([]);
  const [sActiveLoad, setSActiveLoad] = useState(0);
  const [sNumLoads, setSNumLoads] = useState(0);
  const [sShippingBoard, setSShippingBoard] = useState({});
  const [shippingApiRows, setShippingApiRows] = useState([]);
  const [shippingApiLoading, setShippingApiLoading] = useState(false);
  const [shippingApiError, setShippingApiError] = useState(null);
  /** `{ name, value }` from `Customer/getall` for Shipping planner `<select>` (`value` = code or `__name:…`). */
  const [shippingPlannerCustomerOptions, setShippingPlannerCustomerOptions] = useState([]);
  /** After first `Customer/getall` attempt (success or failure) planner demand/shipping detail APIs may run. */
  const [plannerCustomerListReady, setPlannerCustomerListReady] = useState(false);
  const [sAspMappingProfile, setSAspMappingProfile] = useState("");
  const [sLoadBoardModalOpen, setSLoadBoardModalOpen] = useState(false);
  const [sLoadBoardTabIndex, setSLoadBoardTabIndex] = useState(0);
  const [sLoadBoardSchema, setSLoadBoardSchema] = useState(null);
  const [sLoadBoardHeaderRows, setSLoadBoardHeaderRows] = useState(null);
  /** Parsed shipmentForm from customer API and/or loadboard-schema (null = no configured form). */
  const [sLoadBoardShipmentFormLayout, setSLoadBoardShipmentFormLayout] = useState(null);
  /** False while Load Board modal is resolving customer/schema ASP layout; then true (even if layout stayed null). */
  const [sLoadBoardShipmentFormFetchDone, setSLoadBoardShipmentFormFetchDone] = useState(false);
  const [sAspFormsUploading, setSAspFormsUploading] = useState(false);
  const [sLoadToShipmentLoading, setSLoadToShipmentLoading] = useState(false);
  const [sPalletMode, setSPalletMode] = useState(false);
  /** Pending truck-overflow confirm: apply shipment qty only after user clicks OK in dialog. */
  const [sTruckOverflowPending, setSTruckOverflowPending] = useState(null);
  /** Load board modal line overrides: `${loadIndex}::${rowKey}::${fieldKey}` → display value. */
  const [sLoadBoardCellOverrides, setSLoadBoardCellOverrides] = useState({});
  const sLoadBoardCellOverridesRef = useRef({});
  useEffect(() => {
    sLoadBoardCellOverridesRef.current = sLoadBoardCellOverrides;
  }, [sLoadBoardCellOverrides]);
  /** Server-assigned CP ASP numbers per shipment column (load index → ASPNum). */
  const [sLoadBoardSavedAspByLoadIndex, setSLoadBoardSavedAspByLoadIndex] = useState({});

  const [mComponentType, setMComponentType] = useState("MATERIAL");
  const [mComponentTypes, setMComponentTypes] = useState([]);
  const [mComponentTypesLoading, setMComponentTypesLoading] = useState(false);
  const [mComponentTypesLoaded, setMComponentTypesLoaded] = useState(false);
  const [mComponentTypesError, setMComponentTypesError] = useState(null);
  const [mWorkspaceRows, setMWorkspaceRows] = useState([]);
  const [mWorkspaceLoading, setMWorkspaceLoading] = useState(false);
  const [mWorkspaceError, setMWorkspaceError] = useState(null);
  const [mCustomer, setMCustomer] = useState("");
  const [mMaterial, setMMaterial] = useState("all");
  const [mPartSearch, setMPartSearch] = useState("");
  const [mDateFrom, setMDateFrom] = useState(fmtDate(baseDates[0]));
  const [mDateTo, setMDateTo] = useState(fmtDate(baseDates[baseDates.length - 1]));
  const [mStatus, setMStatus] = useState("all");
  const [mModalData, setMModalData] = useState(null);
  const [mModalSearch, setMModalSearch] = useState("");

  useEffect(() => {
    ensureSpvpAspScript().catch(() => {});
  }, []);

  const addToast = (message, variant = "default") => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, { id, message, variant }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((item) => item.id !== id));
    }, 3200);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await CustomerService.getAllCustomersList();
        if (cancelled) return;
        const st = res?.status ?? res?.Status;
        if (st != null && st !== 200 && st !== "200") {
          const msg = res?.errorMessage?.message || res?.message || res?.Message || "Failed to load customers.";
          addToast(String(msg), "error");
          setDemandCustomerDropdownFromApi([]);
          setShippingPlannerCustomerOptions([]);
          return;
        }
        const { demandNames, shippingOptions } = buildPlannerCustomerListsFromGetAllResponse(res);
        setDemandCustomerDropdownFromApi(demandNames);
        setShippingPlannerCustomerOptions(shippingOptions);
      } catch (e) {
        if (!cancelled) {
          addToast(e?.message || "Failed to load customers.", "error");
          setDemandCustomerDropdownFromApi([]);
          setShippingPlannerCustomerOptions([]);
        }
      } finally {
        if (!cancelled) setPlannerCustomerListReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally once on mount: master customer list for planner dropdowns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMComponentTypesLoading(true);
      setMComponentTypesLoaded(false);
      setMComponentTypesError(null);
      try {
        const res = await PlannerService.getMaterialPlannerComponentTypes();
        if (cancelled) return;
        const st = res?.status ?? res?.Status;
        if (st != null && st !== 200 && st !== "200") {
          const msg = res?.errorMessage?.message || res?.message || res?.Message || "Failed to load component types.";
          setMComponentTypes([]);
          setMComponentTypesError(String(msg));
          addToast(String(msg), "error");
          return;
        }
        const types = normalizeMaterialPlannerComponentTypesResponse(res);
        setMComponentTypes(types);
        setMComponentType(types.find((type) => type.toUpperCase() === "MATERIAL") || types[0] || "MATERIAL");
      } catch (e) {
        if (!cancelled) {
          setMComponentTypes([]);
          setMComponentTypesError(e?.message || "Failed to load component types.");
          addToast(e?.message || "Failed to load component types.", "error");
        }
      } finally {
        if (!cancelled) {
          setMComponentTypesLoading(false);
          setMComponentTypesLoaded(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally once on mount: component type master list for Material Planner.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!mComponentTypesLoaded) return;
    const materialType = String(mComponentType || "MATERIAL").trim() || "MATERIAL";
    let cancelled = false;
    (async () => {
      setMWorkspaceLoading(true);
      setMWorkspaceError(null);
      try {
        const res = await PlannerService.getMaterialPlannerWorkspace(materialType);
        if (cancelled) return;
        const st = res?.status ?? res?.Status;
        if (st != null && st !== 200 && st !== "200") {
          const msg = res?.errorMessage?.message || res?.message || res?.Message || "Failed to load material planner workspace.";
          setMWorkspaceRows([]);
          setMWorkspaceError(String(msg));
          addToast(String(msg), "error");
          return;
        }
        setMWorkspaceRows(normalizeMaterialPlannerWorkspaceRowsResponse(res));
      } catch (e) {
        if (!cancelled) {
          setMWorkspaceRows([]);
          setMWorkspaceError(e?.message || "Failed to load material planner workspace.");
          addToast(e?.message || "Failed to load material planner workspace.", "error");
        }
      } finally {
        if (!cancelled) setMWorkspaceLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Refetch workspace rows whenever the selected ComponentType changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mComponentTypesLoaded, mComponentType]);

  const refreshAspMappingsList = useCallback(() => {
    (async () => {
      // Prefer backend mappings when a customer is selected; fallback to localStorage.
      if (sCustomer) {
        try {
          const res = await SpvpAspService.getAspMappingStore({
            customerCode: sCustomer,
            shipToCode: sShipTo && sShipTo !== "all" ? sShipTo : null,
          });
          const storeJson = res?.response?.storeJson ?? res?.response?.StoreJson ?? res?.storeJson ?? res?.StoreJson ?? "";
          if (storeJson) {
            const parsed = JSON.parse(storeJson);
            const profiles = Array.isArray(parsed?.profiles) ? parsed.profiles : [];
            saveAspStore({ version: 1, profiles });
            setAspMappings([...profiles].sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))));
            return;
          }

          // Backend reachable but no mappings for this customer/ship-to.
          const msg = res?.message ?? res?.Message;
          if (msg && String(msg).toLowerCase().includes("no asp")) {
            addToast(String(msg), "warn");
          }
        } catch {
          // ignore and fallback
        }
      }
      setAspMappings(loadAspMappings().sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""))));
    })();
  }, [sCustomer, sShipTo]);

  const syncAspStoreToBackend = useCallback(() => {
    if (!sCustomer) return;
    (async () => {
      try {
        const store = loadAspStore();
        await SpvpAspService.saveAspMappingStore({
          customerCode: sCustomer,
          shipToCode: sShipTo && sShipTo !== "all" ? sShipTo : null,
          storeVersion: store?.version ?? 1,
          storeJson: JSON.stringify(store),
        });
      } catch {
        // Best-effort sync; UI can still rely on localStorage if API is unavailable.
      }
    })();
  }, [sCustomer, sShipTo]);

  useEffect(() => {
    refreshAspMappingsList();
    window.addEventListener("storage", refreshAspMappingsList);
    return () => window.removeEventListener("storage", refreshAspMappingsList);
  }, [refreshAspMappingsList]);

  useEffect(() => {
    if (!sLoadBoardModalOpen) return;
    let cancelled = false;
    (async () => {
      if (!cancelled) setSLoadBoardShipmentFormFetchDone(false);
      try {
        if (!sCustomer) {
          if (!cancelled) {
            setSLoadBoardHeaderRows(null);
            setSLoadBoardShipmentFormLayout(getDefaultLoadBoardShipmentFormLayoutRaw());
            setSLoadBoardSchema(DEFAULT_SUPPLIER_SHIP_PLAN_LOAD_BOARD_COLUMNS);
            setSLoadBoardShipmentFormFetchDone(true);
          }
          return;
        }

        const customerCodeForApi = resolvePlannerCustomerCodeForApi(sCustomer, shippingApiRows);
        if (!customerCodeForApi) {
          if (String(sCustomer).startsWith("__name:")) {
            const local = loadLoadBoardSchemaFromLocalStorage(sCustomer, sShipTo);
            if (!cancelled && local?.columns?.length) {
              setSLoadBoardSchema(local.columns);
              setSLoadBoardHeaderRows(local.headerRows?.length ? local.headerRows : null);
              setSLoadBoardShipmentFormLayout(local.shipmentForm ?? getDefaultLoadBoardShipmentFormLayoutRaw());
              if (!cancelled) setSLoadBoardShipmentFormFetchDone(true);
              return;
            }
          }
          if (!cancelled) {
            setSLoadBoardHeaderRows(null);
            setSLoadBoardShipmentFormLayout(getDefaultLoadBoardShipmentFormLayoutRaw());
            setSLoadBoardSchema(DEFAULT_SUPPLIER_SHIP_PLAN_LOAD_BOARD_COLUMNS);
            setSLoadBoardShipmentFormFetchDone(true);
          }
          return;
        }

        const schemaRes = await SpvpAspService.getLoadBoardSchema({
          customerCode: customerCodeForApi,
          shipToCode: sShipTo && sShipTo !== "all" ? sShipTo : null,
        }).catch(() => null);

        let customerAspConfig = null;
        if (LOAD_BOARD_USE_CUSTOMER_ASP_CONFIG_API) {
          const customerRes = await SpvpAspService.getCustomerAspLoadBoardConfig(customerCodeForApi).catch(() => null);
          customerAspConfig = customerRes ? parseShipmentFormFromCustomerAspApiResponse(customerRes) : null;
        }

        const schemaJson =
          schemaRes?.response?.schemaJson ??
          schemaRes?.response?.SchemaJson ??
          schemaRes?.schemaJson ??
          schemaRes?.SchemaJson ??
          "";
        const customerAspConfigFromApi = LOAD_BOARD_USE_CUSTOMER_ASP_CONFIG_API ? customerAspConfig : null;

        let schemaColumns = [];
        let schemaHeaderRows = null;
        let shipmentFormFromSchema = null;
        if (schemaJson) {
          try {
            const parsed = JSON.parse(schemaJson);
            schemaColumns = Array.isArray(parsed?.columns) ? parsed.columns : [];
            schemaHeaderRows = Array.isArray(parsed?.headerRows)
              ? parsed.headerRows
              : Array.isArray(parsed?.header)
                ? parsed.header
                : null;
            shipmentFormFromSchema = parsed?.shipmentForm ?? parsed?.ShipmentForm ?? null;
          } catch {
            schemaColumns = [];
          }
        }

        const customerColumns = Array.isArray(customerAspConfigFromApi?.columns) ? customerAspConfigFromApi.columns : [];
        const customerHeaderRows = Array.isArray(customerAspConfigFromApi?.headerRows)
          ? customerAspConfigFromApi.headerRows
          : Array.isArray(customerAspConfigFromApi?.header)
            ? customerAspConfigFromApi.header
            : null;

        const mergedColumns = schemaColumns.length ? schemaColumns : customerColumns;
        const mergedHeaderRows =
          schemaHeaderRows && schemaHeaderRows.length
            ? schemaHeaderRows
            : customerHeaderRows && customerHeaderRows.length
              ? customerHeaderRows
              : null;

        const defaultLayout = getDefaultLoadBoardShipmentFormLayoutRaw();
        const mergedRaw =
          customerAspConfigFromApi && shipmentFormFromSchema
            ? { ...shipmentFormFromSchema, ...customerAspConfigFromApi }
            : shipmentFormFromSchema ?? customerAspConfigFromApi ?? defaultLayout;

        const hasCustomerAspPayload =
          customerAspConfigFromApi &&
          typeof customerAspConfigFromApi === "object" &&
          (Array.isArray(customerAspConfigFromApi.sections) && customerAspConfigFromApi.sections.length > 0 ||
            customerColumns.length > 0 ||
            (customerHeaderRows && customerHeaderRows.length > 0) ||
            customerAspConfigFromApi.supplierDefaults != null);

        if (!schemaJson && !hasCustomerAspPayload) {
          if (!cancelled) setSLoadBoardHeaderRows(null);
          if (!cancelled) setSLoadBoardShipmentFormLayout(defaultLayout);
          if (!cancelled) setSLoadBoardSchema(DEFAULT_SUPPLIER_SHIP_PLAN_LOAD_BOARD_COLUMNS);

          const msg = schemaRes?.message ?? schemaRes?.Message;
          if (msg && String(msg).toLowerCase().includes("no load board schema")) {
            addToast(`${msg} — showing default supplier ship plan columns until a schema is saved.`, "default");
          }
          if (!cancelled) setSLoadBoardShipmentFormFetchDone(true);
          return;
        }

        if (!cancelled) setSLoadBoardSchema(mergedColumns.length ? mergedColumns : DEFAULT_SUPPLIER_SHIP_PLAN_LOAD_BOARD_COLUMNS);
        if (!cancelled) setSLoadBoardHeaderRows(mergedHeaderRows && mergedHeaderRows.length ? mergedHeaderRows : null);
        if (!cancelled) setSLoadBoardShipmentFormLayout(mergedRaw ?? defaultLayout);
        if (!cancelled) setSLoadBoardShipmentFormFetchDone(true);
      } catch {
        if (!cancelled) setSLoadBoardSchema(DEFAULT_SUPPLIER_SHIP_PLAN_LOAD_BOARD_COLUMNS);
        if (!cancelled) setSLoadBoardHeaderRows(null);
        if (!cancelled) setSLoadBoardShipmentFormLayout(getDefaultLoadBoardShipmentFormLayoutRaw());
        if (!cancelled) setSLoadBoardShipmentFormFetchDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sLoadBoardModalOpen, sCustomer, sShipTo, shippingApiRows]);

  useEffect(() => {
    if (sLoadBoardModalOpen && sNumLoads < 1) setSLoadBoardModalOpen(false);
  }, [sLoadBoardModalOpen, sNumLoads]);

  useEffect(() => {
    if (activePlanner !== "demand") return;
    if (!plannerCustomerListReady) return;
    const customerName = dCustomer ? String(dCustomer).trim() : "";
    const customerKey = customerName || "__ALL__";
    let cancelled = false;
    (async () => {
      setDemandApiLoading(true);
      setDemandApiError(null);
      try {
        const res = await PlannerService.getCustomerDemandDetail({
          customer: customerName || undefined,
          dueDateFrom: dDateFrom,
          dueDateTo: dDateTo,
        });
        const raw = res?.response ?? res?.Response;
        const list = Array.isArray(raw) ? raw : [];
        const range = getDemandDueDateRangeFromApiRows(list);
        if (!cancelled) {
          setDemandApiRows(list);
          if (range && demandDatesFittedForCustomerKeyRef.current !== customerKey) {
            demandDatesFittedForCustomerKeyRef.current = customerKey;
            setDDateFrom(range.fromIso);
            setDDateTo(range.toIso);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setDemandApiRows([]);
          setDemandApiError(e?.message || "Failed to load customer demand.");
        }
      } finally {
        if (!cancelled) setDemandApiLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activePlanner, plannerCustomerListReady, dCustomer, dDateFrom, dDateTo]);

  const shippingDemandDateRange = useMemo(() => {
    const anchor = sShipDate && /^\d{4}-\d{2}-\d{2}$/.test(sShipDate) ? new Date(`${sShipDate}T12:00:00`) : new Date(`${fmtDate(baseDates[0])}T12:00:00`);
    const from = addDays(anchor, -30);
    const to = addDays(anchor, 60);
    return { fromIso: fmtDate(from), toIso: fmtDate(to) };
  }, [sShipDate]);

  const plannerSubtitle = {
    demand: "Customer demand by part and due date — matrix view with frozen dimensions.",
    shipping: "Operational shipment board — assign parts into loads; slicers and actions mirror Excel workflows.",
    material: "Material requirements vs on-hand and replenishment — supports shortage and safety signals.",
  }[activePlanner];

  const demandCustomerOptions = demandCustomerDropdownFromApi;

  const demandCustomerSelectOptions = useMemo(
    () => [
      { value: "", label: plannerCustomerListReady ? "All customers" : "Loading customers…" },
      ...demandCustomerOptions.map((name) => ({ value: name, label: name })),
    ],
    [plannerCustomerListReady, demandCustomerOptions],
  );

  const demandCustomerSelectValue = useMemo(() => {
    const opts = demandCustomerSelectOptions;
    if (!dCustomer) return opts[0] || null;
    return opts.find((o) => o.value === dCustomer) || { value: dCustomer, label: dCustomer };
  }, [dCustomer, demandCustomerSelectOptions]);

  const materialComponentTypeSelectOptions = useMemo(() => {
    const types = mComponentTypes.length ? mComponentTypes : ["MATERIAL"];
    return types.map((type) => ({ value: type, label: type }));
  }, [mComponentTypes]);

  const materialComponentTypeSelectValue = useMemo(() => {
    const opts = materialComponentTypeSelectOptions;
    return opts.find((o) => o.value === mComponentType) || { value: mComponentType || "MATERIAL", label: mComponentType || "MATERIAL" };
  }, [mComponentType, materialComponentTypeSelectOptions]);

  const materialCustomerSelectOptions = demandCustomerSelectOptions;

  const materialCustomerSelectValue = useMemo(() => {
    const opts = materialCustomerSelectOptions;
    if (!mCustomer) return opts[0] || null;
    return opts.find((o) => o.value === mCustomer) || { value: mCustomer, label: mCustomer };
  }, [mCustomer, materialCustomerSelectOptions]);

  const demandShipToOptions = useMemo(() => {
    const set = new Set();
    let rows = Array.isArray(demandApiRows) ? demandApiRows : [];
    const selectedName = dCustomer ? String(dCustomer).trim() : "";
    if (selectedName) {
      rows = rows.filter((r) => String(pickDemandRow(r, "customerName", "CustomerName") || "").trim() === selectedName);
    }
    rows.forEach((r) => {
      const label = shipToLabelFromDemandRow(r);
      if (label && label !== "—") set.add(label);
    });
    return ["", ...[...set].sort((a, b) => a.localeCompare(b))];
  }, [demandApiRows, dCustomer]);

  useEffect(() => {
    setDShipTo("all");
  }, [dCustomer]);

  useEffect(() => {
    demandDatesFittedForCustomerKeyRef.current = "";
  }, [dCustomer]);

  const demandApiFilteredRows = useMemo(() => {
    let rows = Array.isArray(demandApiRows) ? [...demandApiRows] : [];
    const q = dPartSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const pn = String(pickDemandPartNumber(r) || "").toLowerCase();
        const desc = String(pickDemandRow(r, "description", "Description") || "").toLowerCase();
        return pn.includes(q) || desc.includes(q);
      });
    }
    if (dShipTo && dShipTo !== "all") {
      const st = dShipTo.toLowerCase();
      rows = rows.filter((r) => {
        const blob = [
          pickDemandRow(r, "attn", "ATTN"),
          pickDemandRow(r, "addr1", "ADDR1"),
          pickDemandRow(r, "city", "CITY"),
          pickDemandRow(r, "state", "STATE"),
        ]
          .join(" ")
          .toLowerCase();
        return blob.includes(st);
      });
    }
    return rows;
  }, [demandApiRows, dPartSearch, dShipTo]);

  // Demand matrix columns from From/To filters (API query range).
  const demandMatrixDatesFromFilters = useMemo(() => {
    const start = new Date(`${dDateFrom}T12:00:00`);
    const end = new Date(`${dDateTo}T12:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
    const days = differenceInCalendarDays(end, start);
    const safeDays = Number.isFinite(days) ? Math.max(0, Math.min(days, 370)) : 0; // guard against runaway ranges
    return Array.from({ length: safeDays + 1 }, (_, i) => addDays(start, i));
  }, [dDateFrom, dDateTo]);

  /** Effective column date range: optional rolling 60-day window from today, else From/To. */
  const demandMatrixDatesRange = useMemo(() => {
    if (!dSixtyDayOutOfStock) return demandMatrixDatesFromFilters;
    const start = new Date();
    start.setHours(12, 0, 0, 0);
    return Array.from({ length: 60 }, (_, i) => addDays(start, i));
  }, [dSixtyDayOutOfStock, demandMatrixDatesFromFilters]);

  const demandMatrixRows = useMemo(() => {
    /** @type {Map<string, any>} */
    const map = new Map();

    demandApiFilteredRows.forEach((row) => {
      const customerName = pickDemandRow(row, "customerName", "CustomerName") || "";
      const partNumber = pickDemandPartNumber(row);
      const key = `${customerName}||${partNumber}`;
      if (!map.has(key)) {
        const desc = pickDemandRow(row, "description", "Description") || "";
        const status = pickDemandRow(row, "status", "Status") || "";
        const onHand = getQTyNumeric(row, dQTySource);
        map.set(key, {
          key,
          customerName,
          partNumber,
          desc,
          status,
          onHand,
          perPallet: Number(pickDemandRow(row, "partsonaPallet", "PartsonaPallet")) || null,
          qtyByIso: {}, // eaches
        });
      }

      const agg = map.get(key);
      const rowOnHand = getQTyNumeric(row, dQTySource);
      if (rowOnHand != null && Number.isFinite(rowOnHand)) {
        if (agg.onHand == null || !Number.isFinite(agg.onHand)) agg.onHand = rowOnHand;
        else agg.onHand = Math.max(agg.onHand, rowOnHand);
      }
      const dueIso = parseDemandDueDateToIsoYmd(pickDemandDueDateRaw(row));
      if (!dueIso) return;

      const qty = Number(pickDemandRow(row, "quantity", "Quantity"));
      if (!Number.isFinite(qty) || qty === 0) return;

      agg.qtyByIso[dueIso] = (agg.qtyByIso[dueIso] || 0) + qty;
    });

    const rows = [...map.values()];

    // Sort: by customer (when all customers), then part number.
    rows.sort((a, b) => {
      const c = String(a.customerName || "").localeCompare(String(b.customerName || ""));
      if (c !== 0) return c;
      return String(a.partNumber || "").localeCompare(String(b.partNumber || ""));
    });

    return rows;
  }, [demandApiFilteredRows, dQTySource]);

  /** Hide day columns where no part has demand (keeps From/To for API; table only shows dates with data). */
  const demandMatrixDatesVisible = useMemo(() => {
    if (!demandMatrixRows.length || !demandMatrixDatesRange.length) return demandMatrixDatesRange;
    const isosWithQty = new Set();
    for (const part of demandMatrixRows) {
      const qb = part.qtyByIso || {};
      for (const iso of Object.keys(qb)) {
        if (Number(qb[iso]) !== 0) isosWithQty.add(iso);
      }
    }
    return demandMatrixDatesRange.filter((d) => isosWithQty.has(fmtDate(d)));
  }, [demandMatrixRows, demandMatrixDatesRange]);

  const demandMatrixDatesForTable = useMemo(
    () => (dShowOnlyDemandDaysInRange ? demandMatrixDatesVisible : demandMatrixDatesRange),
    [dShowOnlyDemandDaysInRange, demandMatrixDatesRange, demandMatrixDatesVisible],
  );

  /** Demand table rows: when “60 Days Out Of Stock” is on, only parts projected short in that window. */
  const demandMatrixRowsForTable = useMemo(() => {
    if (!dSixtyDayOutOfStock) return demandMatrixRows;
    return demandMatrixRows.filter((part) => demandPartHasShortageInWindow(part, demandMatrixDatesForTable));
  }, [dSixtyDayOutOfStock, demandMatrixRows, demandMatrixDatesForTable]);

  const shippingCacheKey = `${sCustomer}|${sShipTo || "all"}`;

  const shippingSelectedCustomerName = useMemo(() => {
    if (!sCustomer) return "";
    if (String(sCustomer).startsWith("__name:")) return String(sCustomer).slice("__name:".length);
    const demo = String(customers[sCustomer]?.name || "").trim();
    if (demo) return demo;
    const hit = shippingPlannerCustomerOptions.find((o) => o.value === sCustomer);
    return hit ? String(hit.name || "").trim() : "";
  }, [sCustomer, shippingPlannerCustomerOptions]);

  useEffect(() => {
    if (activePlanner !== "shipping") return;
    if (!plannerCustomerListReady) return;
    if (!String(sCustomer || "").trim()) {
      setShippingApiLoading(false);
      setShippingApiError(null);
      setShippingApiRows([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setShippingApiLoading(true);
      setShippingApiError(null);
      try {
        const customerName = shippingSelectedCustomerName || undefined;
        const res = await PlannerService.getCustomerDemandDetailShippingPlaner({
          customer: customerName,
          dueDateFrom: shippingDemandDateRange.fromIso,
          dueDateTo: shippingDemandDateRange.toIso,
        });
        const raw = res?.response ?? res?.Response;
        const list = Array.isArray(raw) ? raw : [];
        if (!cancelled) setShippingApiRows(list);
      } catch (e) {
        if (!cancelled) {
          setShippingApiRows([]);
          setShippingApiError(e?.message || "Failed to load shipping demand detail.");
        }
      } finally {
        if (!cancelled) setShippingApiLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    activePlanner,
    plannerCustomerListReady,
    sCustomer,
    shippingSelectedCustomerName,
    shippingDemandDateRange.fromIso,
    shippingDemandDateRange.toIso,
  ]);

  const shippingCustomerOptions = shippingPlannerCustomerOptions;

  const shippingCustomerSelectOptions = useMemo(
    () => [
      { value: "", label: plannerCustomerListReady ? "Select Customer" : "Loading customers…" },
      ...shippingCustomerOptions.map((o) => ({ value: o.value, label: o.name })),
    ],
    [plannerCustomerListReady, shippingCustomerOptions],
  );

  const shippingCustomerSelectValue = useMemo(() => {
    const opts = shippingCustomerSelectOptions;
    if (!String(sCustomer || "").trim()) return opts[0] || null;
    return (
      opts.find((o) => o.value === sCustomer) || {
        value: sCustomer,
        label: shippingSelectedCustomerName || String(sCustomer),
      }
    );
  }, [sCustomer, shippingCustomerSelectOptions, shippingSelectedCustomerName]);

  const shippingShipToOptions = useMemo(() => {
    const set = new Set();
    let rows = Array.isArray(shippingApiRows) ? shippingApiRows : [];
    const selectedName = shippingSelectedCustomerName;
    if (selectedName) {
      rows = rows.filter((r) => String(pickDemandRow(r, "customerName", "CustomerName") || "").trim() === selectedName);
    }
    rows.forEach((r) => {
      const label = shipToLabelFromDemandRow(r);
      if (label && label !== "—") set.add(label);
    });
    (customers[sCustomer]?.shipTos || []).forEach((st) => {
      if (st) set.add(st);
    });
    return ["", ...[...set].sort((a, b) => a.localeCompare(b))];
  }, [shippingApiRows, shippingSelectedCustomerName, sCustomer]);

  useEffect(() => {
    setSShipTo("all");
    setSPoSearch("");
    setSSourceFilter("all");
    setSDestinationFilter("all");
    setSSelectedDueDates([]);
    setSActiveLoad(0);
    setSNumLoads(0);
  }, [sCustomer]);

  useEffect(() => {
    if (activePlanner !== "shipping") return;
    const customerName = shippingSelectedCustomerName;
    const nextRows = buildShippingRowsFromDemandDetail(shippingApiRows, {
      customerName,
      shipTo: sShipTo || "all",
    });
    setSShippingBoard((prev) => ({
      ...prev,
      [shippingCacheKey]: nextRows,
    }));
    setSNumLoads(0);
    setSActiveLoad(0);
    setSSelectedDueDates([]);
  }, [activePlanner, shippingApiRows, shippingSelectedCustomerName, sShipTo, shippingCacheKey]);

  const masterShippingRows = sShippingBoard[shippingCacheKey] || [];
  const shippingSourceOptions = useMemo(() => [...new Set(masterShippingRows.map((row) => row.source))], [masterShippingRows]);
  const shippingDestinationOptions = useMemo(() => [...new Set(masterShippingRows.map((row) => row.destination))], [masterShippingRows]);

  const filteredShippingRows = useMemo(() => {
    let rows = [...masterShippingRows];
    const query = sPartSearch.trim().toLowerCase();
    const poQuery = sPoSearch.trim().toLowerCase();

    if (sSourceFilter !== "all") rows = rows.filter((row) => row.source === sSourceFilter);
    if (sDestinationFilter !== "all") rows = rows.filter((row) => row.destination === sDestinationFilter);
    if (query) rows = rows.filter((row) => row.sku.toLowerCase().includes(query));
    if (poQuery) rows = rows.filter((row) => String(row.po || "").toLowerCase().includes(poQuery));
    if (sSelectedDueDates.length) {
      rows = rows.filter((row) => sSelectedDueDates.some((due) => (row.qtyByDueDate[due] || 0) > 0));
    }
    return rows;
  }, [masterShippingRows, sPartSearch, sPoSearch, sSourceFilter, sDestinationFilter, sSelectedDueDates]);

  const dueColumns = useMemo(() => {
    if (!sSelectedDueDates.length) return [];
    return sSelectedDueDates.filter((iso) => filteredShippingRows.some((row) => (row.qtyByDueDate[iso] || 0) > 0));
  }, [filteredShippingRows, sSelectedDueDates]);

  /** True when any visible row is over-assigned (grand total minus shipment qtys is below zero). Active shipment header turns red. */
  const shippingHasNegativeUnassigned = useMemo(
    () =>
      filteredShippingRows.some((row) => {
        const assignedSum = row.loads.reduce((sum, value) => sum + (value || 0), 0);
        const displayGrandTotal = dueColumns.length ? sumForDates(row, dueColumns, "qtyByDueDate") : row.grandTotal;
        return displayGrandTotal - assignedSum < 0;
      }),
    [filteredShippingRows, dueColumns],
  );

  /** Per shipment column: any row qty in that column exceeds Pallet Qty × 52. */
  const shippingLoadExceedsTruck = useMemo(() => {
    const flags = Array.from({ length: sNumLoads }, () => false);
    filteredShippingRows.forEach((row) => {
      for (let i = 0; i < sNumLoads; i += 1) {
        if (!flags[i] && shipmentQtyExceedsTruckLimit(row, row.loads?.[i] || 0)) {
          flags[i] = true;
        }
      }
    });
    return flags;
  }, [filteredShippingRows, sNumLoads]);

  const shippingActiveShipmentHeaderIsRed = useCallback(
    (loadIndex) =>
      shippingLoadExceedsTruck[loadIndex] || (loadIndex === sActiveLoad && shippingHasNegativeUnassigned),
    [shippingLoadExceedsTruck, sActiveLoad, shippingHasNegativeUnassigned],
  );

  const allDueDatesForSlicer = useMemo(() => {
    const dueDates = new Set();
    masterShippingRows.forEach((row) => {
      Object.entries(row.qtyByDueDate || {}).forEach(([date, qty]) => {
        if (qty > 0) dueDates.add(date);
      });
      if (row.dueDate) dueDates.add(row.dueDate);
    });
    const apiRows = filterRawShippingDemandRows(shippingApiRows, {
      customerName: shippingSelectedCustomerName,
      shipTo: sShipTo || "all",
    });
    apiRows.forEach((r) => {
      const iso = parseDemandDueDateToIsoYmd(pickDemandDueDateRaw(r));
      if (iso) dueDates.add(iso);
    });
    return [...dueDates].sort();
  }, [masterShippingRows, shippingApiRows, shippingSelectedCustomerName, sShipTo]);

  const shippingDueDateSelectOptions = useMemo(
    () => allDueDatesForSlicer.map((iso) => ({ value: iso, label: fmtDueLabel(iso) })),
    [allDueDatesForSlicer],
  );

  const shippingDueDateSelectValue = useMemo(
    () => sSelectedDueDates.map((iso) => ({ value: iso, label: fmtDueLabel(iso) })),
    [sSelectedDueDates],
  );

  const materialOptions = useMemo(() => {
    const byItem = new Map();
    mWorkspaceRows.forEach((row) => {
      const id = String(pickDemandRow(row, "componentItemNo", "ComponentItemNo") || "").trim();
      if (!id || byItem.has(id)) return;
      byItem.set(id, {
        id,
        name: String(pickDemandRow(row, "componentDescription", "ComponentDescription") || "").trim(),
      });
    });
    return [...byItem.values()].sort((a, b) => a.id.localeCompare(b.id));
  }, [mWorkspaceRows]);

  useEffect(() => {
    setMMaterial("all");
  }, [mCustomer, mComponentType]);

  const materialRange = useMemo(() => getDateRangeIndices(mDateFrom, mDateTo), [mDateFrom, mDateTo]);

  const materialRows = useMemo(() => {
    let rows = Array.isArray(mWorkspaceRows) ? [...mWorkspaceRows] : [];

    if (mMaterial !== "all") {
      rows = rows.filter((row) => String(pickDemandRow(row, "componentItemNo", "ComponentItemNo") || "").trim() === mMaterial);
    }

    const query = mPartSearch.trim().toLowerCase();
    if (query) {
      rows = rows.filter((row) =>
        materialPartDetailColumns.some((column) =>
          String(pickDemandRow(row, ...column.keys) ?? "")
            .toLowerCase()
            .includes(query),
        ),
      );
    }

    const byMaterial = new Map();
    rows.forEach((row) => {
      const id = String(pickDemandRow(row, "componentItemNo", "ComponentItemNo") || "").trim();
      if (!id) return;
      if (!byMaterial.has(id)) {
        byMaterial.set(id, {
          id,
          name: String(pickDemandRow(row, "componentDescription", "ComponentDescription") || "").trim(),
          componentType: String(pickDemandRow(row, "componentType", "ComponentType") || "").trim(),
          opClass: String(pickDemandRow(row, "opClass", "OpClass") || "").trim(),
          onHand: pickDemandRow(row, "onHand", "OnHand"),
          ptsPer: pickDemandRow(row, "ptsPer", "PtsPer"),
          totalMaterialPiece: pickDemandRow(row, "totalMaterialPiece", "TotalMaterialPiece"),
          totalMaterialPieceFallback: 0,
          palletDims: String(pickDemandRow(row, "palletDims", "PalletDims") || "").trim(),
          req: Array.from({ length: baseDates.length }, () => 0),
          incoming: Array.from({ length: baseDates.length }, () => 0),
          parts: [],
          partNumbers: new Set(),
        });
      }
      const group = byMaterial.get(id);
      group.parts.push(row);
      const partNumber = String(pickDemandRow(row, "partNumber", "PartNumber") || "").trim();
      if (partNumber) group.partNumbers.add(partNumber);
      const materialPerPiece = Number(pickDemandRow(row, "materialPerPiece", "MaterialPerPiece"));
      if (Number.isFinite(materialPerPiece)) group.totalMaterialPieceFallback += materialPerPiece;
      const dueIso = parseDemandDueDateToIsoYmd(pickDemandRow(row, "dueDate", "DueDate"));
      const dateIdx = dueIso ? baseDates.findIndex((d) => fmtDate(d) === dueIso) : -1;
      const requiredQty = Number(
        pickDemandRow(
          row,
          "materialRequiredQty",
          "MaterialRequiredQty",
          "materialRequirement",
          "MaterialRequirement",
          "requiredQty",
          "RequiredQty",
          "dateQuantity",
          "DateQuantity",
        ),
      );
      if (dateIdx >= 0 && Number.isFinite(requiredQty)) {
        group.req[dateIdx] = (group.req[dateIdx] || 0) + requiredQty;
      }
    });

    return [...byMaterial.values()]
      .map((group) => ({
        ...group,
        partCount: group.partNumbers.size || group.parts.length,
        totalMaterialPiece:
          group.totalMaterialPiece !== "" && group.totalMaterialPiece != null
            ? group.totalMaterialPiece
            : Number(group.totalMaterialPieceFallback.toFixed(6)),
      }))
      .filter((group) => {
        const total = group.req.reduce((sum, value) => sum + (Number(value) || 0), 0);
        const onHand = Number(group.onHand);
        if (mStatus === "shortage") return Number.isFinite(onHand) && total > onHand;
        if (mStatus === "safety") return Number.isFinite(onHand) && total > onHand * 0.7;
        if (mStatus === "replenish") return group.incoming.some((value) => Number(value) > 0);
        return true;
      })
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [mWorkspaceRows, mMaterial, mPartSearch, mStatus]);

  const materialDateIndices = useMemo(
    () => materialDateIndicesWithData(materialRows, materialRange.fromIdx, materialRange.toIdx),
    [materialRows, materialRange],
  );

  const modalFilteredParts = useMemo(() => {
    if (!mModalData) return [];
    const query = mModalSearch.trim().toLowerCase();
    const parts = Array.isArray(mModalData.parts) ? mModalData.parts : [];
    if (!query) return parts;
    return parts.filter((part) =>
      materialPartDetailColumns.some((column) =>
        String(pickDemandRow(part, ...column.keys) ?? "")
          .toLowerCase()
          .includes(query),
      ),
    );
  }, [mModalData, mModalSearch]);

  const resetDemandFilters = () => {
    setDCustomer("");
    setDShipTo("all");
    setDPartSearch("");
    setDDateFrom(fmtDate(baseDates[0]));
    setDDateTo(fmtDate(baseDates[baseDates.length - 1]));
    setDHighlightShortage(true);
    setDHighlightLow(true);
    setDQTySource("iqmsOnHand");
    setDUom("each");
    setDShowOnlyDemandDaysInRange(true);
    setDSixtyDayOutOfStock(false);
    setDemandApiRows([]);
    setDemandApiError(null);
  };

  const addShipment = () => {
    setSNumLoads((prev) => prev + 1);
    setSShippingBoard((prev) => {
      const rows = prev[shippingCacheKey] || masterShippingRows;
      const nextLoadCount = sNumLoads + 1;
      const nextRows = rows.map((row) => ({ ...row, loads: [...row.loads, 0] }));
      return { ...prev, [shippingCacheKey]: nextRows };
    });
    setSActiveLoad(sNumLoads);
  };

  const resetShipments = () => {
    setSShippingBoard((prev) => ({
      ...prev,
      [shippingCacheKey]: (prev[shippingCacheKey] || []).map((row) => ({
        ...row,
        loads: [],
        remainingByDueDate: { ...row.qtyByDueDate },
      })),
    }));
    setSNumLoads(0);
    setSActiveLoad(0);
    addToast("All shipment assignments cleared.", "success");
  };

  const handleAddToShipment = useCallback(() => {
    // Wire to: add selected / focused lines into the active shipment
  }, []);

  const handlePallet = useCallback(() => {
    setSPalletMode((prev) => {
      const next = !prev;
      addToast(
        next
          ? "Pallet mode: Pallet Inv, due Ship qty, Total remaining, Grand total, Unassigned, and Shipment columns show as pallets (÷ Pallet Qty)."
          : "Pallet mode disabled.",
      );
      return next;
    });
  }, []);

  const assignDueBucketToActiveShipment = (rowKey, dueIso) => {
    if (!sNumLoads) {
      addToast("Add a shipment first, then click a shipment header or quantity cell.", "error");
      return;
    }

    const rows = sShippingBoard[shippingCacheKey] || masterShippingRows;
    const row = rows.find((r) => r.rowKey === rowKey);
    if (!row) return;

    const available = row.remainingByDueDate?.[dueIso] ?? row.qtyByDueDate?.[dueIso] ?? 0;
    if (available <= 0) return;

    const nextQty = (row.loads?.[sActiveLoad] || 0) + available;
    if (shipmentQtyExceedsTruckLimit(row, nextQty) && nextQty !== (row.loads?.[sActiveLoad] || 0)) {
      setSTruckOverflowPending({
        kind: "dueBucket",
        rowKey,
        dueIso,
        loadIndex: sActiveLoad,
        shipmentLabel: `Shipment ${sActiveLoad + 1}`,
      });
      return;
    }

    applyDueBucketToActiveShipment(rowKey, dueIso, sActiveLoad);
  };

  const applyDueBucketToActiveShipment = (rowKey, dueIso, loadIndex) => {
    setSShippingBoard((prev) => {
      const nextRows = (prev[shippingCacheKey] || []).map((r) => {
        if (r.rowKey !== rowKey) return r;
        const avail = r.remainingByDueDate?.[dueIso] ?? r.qtyByDueDate?.[dueIso] ?? 0;
        if (avail <= 0) return r;
        const loads = [...r.loads];
        while (loads.length <= loadIndex) loads.push(0);
        loads[loadIndex] = (loads[loadIndex] || 0) + avail;
        return {
          ...r,
          loads,
          remainingByDueDate: { ...r.remainingByDueDate, [dueIso]: 0 },
        };
      });
      return { ...prev, [shippingCacheKey]: nextRows };
    });

    addToast(`Added remaining quantity from ${fmtDueLabel(dueIso)} to Shipment ${loadIndex + 1}.`, "success");
  };

  const applyShipmentCellValue = (rowKey, loadIndex, value) => {
    setSShippingBoard((prev) => {
      const nextRows = (prev[shippingCacheKey] || []).map((r) => {
        if (r.rowKey !== rowKey) return r;
        const loads = [...r.loads];
        while (loads.length <= loadIndex) loads.push(0);
        loads[loadIndex] = value;
        return { ...r, loads };
      });
      return { ...prev, [shippingCacheKey]: nextRows };
    });
  };

  const updateShipmentCell = (rowKey, loadIndex, rawValue) => {
    const rows = sShippingBoard[shippingCacheKey] || masterShippingRows;
    const row = rows.find((r) => r.rowKey === rowKey);
    const value = shippingShipmentLoadInputToEach(rawValue, row, sPalletMode);
    const prevQty = row?.loads?.[loadIndex] || 0;
    if (row && value !== prevQty && shipmentQtyExceedsTruckLimit(row, value)) {
      setSTruckOverflowPending({
        kind: "cell",
        rowKey,
        loadIndex,
        value,
        shipmentLabel: `Shipment ${loadIndex + 1}`,
      });
      return;
    }

    applyShipmentCellValue(rowKey, loadIndex, value);
  };

  const confirmTruckOverflowPending = () => {
    const pending = sTruckOverflowPending;
    if (!pending) return;
    setSTruckOverflowPending(null);
    setSActiveLoad(pending.loadIndex);
    if (pending.kind === "cell") {
      applyShipmentCellValue(pending.rowKey, pending.loadIndex, pending.value);
    } else {
      applyDueBucketToActiveShipment(pending.rowKey, pending.dueIso, pending.loadIndex);
    }
  };

  const cancelTruckOverflowPending = () => {
    setSTruckOverflowPending(null);
  };

  const demandSummary = useMemo(() => {
    if (!plannerCustomerListReady) return "Loading customer list…";
    if (demandApiLoading) return "Loading demand from server…";
    if (demandApiError) return demandApiError;
    if (!demandMatrixRows.length) {
      return "No demand lines from the server for this filter.";
    }
    if (dSixtyDayOutOfStock && !demandMatrixRowsForTable.length) {
      return "No parts projected out of stock in the next 60 days (with current on-hand and filters).";
    }
    const scope = dCustomer ? String(dCustomer).trim() || "Selected customer" : "All customers";
    const shipLabel = dShipTo && dShipTo !== "all" ? ` · ${dShipTo}` : "";
    const uomLabel = dUom === "each" ? "each" : dUom === "pallet" ? "pallet" : "not selected";
    const sixtyHint = dSixtyDayOutOfStock ? " · 60-day shortage view" : "";
    return `${demandMatrixRowsForTable.length} part(s) · ${demandMatrixDatesForTable.length} day column(s) · ${scope}${shipLabel} · UOM: ${uomLabel}${sixtyHint}`;
  }, [
    plannerCustomerListReady,
    dCustomer,
    demandApiLoading,
    demandApiError,
    demandMatrixRows,
    demandMatrixRowsForTable,
    demandMatrixDatesForTable.length,
    dShipTo,
    dUom,
    dSixtyDayOutOfStock,
  ]);

  const shippingSummary = useMemo(() => {
    if (!plannerCustomerListReady) return "Loading customer list…";
    if (shippingApiLoading) return "Loading shipping demand from server…";
    if (shippingApiError) return shippingApiError;
    if (!String(sCustomer || "").trim() && !filteredShippingRows.length) {
      return "Select a customer to load shipping lines.";
    }
    const scope = shippingSelectedCustomerName || "All customers";
    const dueHint = dueColumns.length ? ` · Due columns: ${dueColumns.map(fmtDueLabel).join(", ")}` : "";
    const shipHint = sNumLoads > 0 ? ` · Active: Shipment ${sActiveLoad + 1}` : " · No shipments";
    return `${filteredShippingRows.length} line(s) · Ship date ${sShipDate} · ${scope} · ${sShipTo === "all" ? "All locations" : sShipTo}${dueHint}${shipHint}`;
  }, [
    plannerCustomerListReady,
    shippingApiLoading,
    shippingApiError,
    shippingSelectedCustomerName,
    sCustomer,
    filteredShippingRows.length,
    dueColumns,
    sNumLoads,
    sActiveLoad,
    sShipDate,
    sShipTo,
  ]);

  const effectiveShipmentLoadIndex = Math.min(Math.max(0, sActiveLoad), Math.max(0, sNumLoads - 1));
  const loadBoardViewLoadIndex = sLoadBoardModalOpen
    ? Math.min(Math.max(0, sLoadBoardTabIndex), Math.max(0, sNumLoads - 1))
    : effectiveShipmentLoadIndex;

  const loadBoardModalTableRows = useMemo(
    () => shippingRowsWithQtyOnLoad(filteredShippingRows, loadBoardViewLoadIndex),
    [filteredShippingRows, loadBoardViewLoadIndex],
  );

  const effectiveLoadBoardShipmentFormLayout = useMemo(
    () => sLoadBoardShipmentFormLayout ?? getDefaultLoadBoardShipmentFormLayoutRaw(),
    [sLoadBoardShipmentFormLayout],
  );

  const normalizedLoadBoardShipmentFormLayout = useMemo(
    () => normalizeShipmentFormLayoutFromApi(effectiveLoadBoardShipmentFormLayout),
    [effectiveLoadBoardShipmentFormLayout],
  );

  const loadBoardShipmentFormProps = useMemo(
    () =>
      computeLoadBoardShipmentFormPropsForIndex(
        loadBoardViewLoadIndex,
        {
          filteredShippingRows,
          shippingApiRows,
          shippingSelectedCustomerName,
          sShipTo,
          sShipDate,
          sCustomer,
        },
        {
          supplierDefaults:
            normalizedLoadBoardShipmentFormLayout?.supplierDefaults ?? DEFAULT_SUPPLIER_SHIP_FROM,
          savedAspByLoadIndex: sLoadBoardSavedAspByLoadIndex,
        },
      ),
    [
      loadBoardViewLoadIndex,
      filteredShippingRows,
      shippingApiRows,
      shippingSelectedCustomerName,
      sShipTo,
      sShipDate,
      sCustomer,
      normalizedLoadBoardShipmentFormLayout,
      sLoadBoardSavedAspByLoadIndex,
    ],
  );

  const openShippingLoadBoardModal = () => {
    if (sNumLoads < 1) {
      addToast("Add at least one shipment column before opening the load board.", "error");
      return;
    }
    setSLoadBoardSchema(null);
    setSLoadBoardHeaderRows(null);
    setSLoadBoardShipmentFormLayout(null);
    setSLoadBoardShipmentFormFetchDone(false);
    setSLoadBoardTabIndex(Math.min(Math.max(0, sActiveLoad), Math.max(0, sNumLoads - 1)));
    setSLoadBoardCellOverrides({});
    sLoadBoardCellOverridesRef.current = {};
    setSLoadBoardModalOpen(true);
  };

  useEffect(() => {
    if (!sLoadBoardModalOpen) return;
    setSLoadBoardTabIndex((t) => Math.min(t, Math.max(0, sNumLoads - 1)));
  }, [sNumLoads, sLoadBoardModalOpen]);

  const getLoadBoardExcelWorkbookForLoadIndex = useCallback(
    (loadIdx) => {
    const formProps = computeLoadBoardShipmentFormPropsForIndex(
      loadIdx,
      {
        filteredShippingRows,
        shippingApiRows,
        shippingSelectedCustomerName,
        sShipTo,
        sShipDate,
        sCustomer,
      },
      {
        supplierDefaults:
          normalizedLoadBoardShipmentFormLayout?.supplierDefaults ?? DEFAULT_SUPPLIER_SHIP_FROM,
      },
    );
    const cust = customers[sCustomer];
    const shipToLabel = sShipTo === "all" ? "All locations" : sShipTo;
    const shipmentFormAoa = buildLoadBoardShipmentFormExcelAoa(formProps, effectiveLoadBoardShipmentFormLayout);
    const exportContextRows = [
      ["", "", "", "", "", ""],
      [
        "Planner context",
        shippingSelectedCustomerName || cust?.name || sCustomer || "All customers",
        "Ship date",
        sShipDate || "—",
        "Ship-To filter",
        shipToLabel || "—",
      ],
      ["Shipment column", `Shipment ${loadIdx + 1}`, "Exported", new Date().toLocaleString(), "", ""],
    ];
    const metaRows = [...shipmentFormAoa, ...exportContextRows];
    const rowsForShipment = shippingRowsWithQtyOnLoad(filteredShippingRows, loadIdx);
    const dueHeaders = dueColumns.map((iso) => `Due ${fmtDueLabel(iso)} (remaining)`);
    const headerRow = [
      "Line #",
      "Part Number",
      "Description",
      "PO",
      "Pallet Inv",
      "Box Qty",
      "Pallet Qty",
      "Release ID",
      "Grand Total" + (sPalletMode ? " (pallets)" : ""),
      "Unassigned" + (sPalletMode ? " (pallets)" : ""),
      ...dueHeaders,
      ...(dueColumns.length > 0 ? ["Due total (remaining)"] : []),
      `Shipment ${loadIdx + 1} qty`,
      "Source",
      "Destination",
      "Est. boxes (shipment qty ÷ box qty)",
      "UOM",
    ];
    const dataRows = rowsForShipment.map((row, i) => {
      const assignedSum = row.loads.reduce((sum, value) => sum + (value || 0), 0);
      const displayGrandTotal = dueColumns.length ? sumForDates(row, dueColumns, "qtyByDueDate") : row.grandTotal;
      const unassigned = displayGrandTotal - assignedSum;
      const shipmentQty = row.loads[loadIdx] || 0;
      const estBoxes = formatShipmentUnitRatio(shipmentQty, row.boxQty);
      const dues = dueColumns.map((due) => row.remainingByDueDate?.[due] ?? row.qtyByDueDate?.[due] ?? 0);
      const dueTot = dueColumns.length > 0 ? sumForDates(row, dueColumns, "remainingByDueDate") : "";
      return [
        i + 1,
        row.sku,
        row.desc,
        row.po,
        shippingPalletInvDisplay(row, sPalletMode),
        row.boxQty,
        row.partsonaPallet !== "" && row.partsonaPallet != null ? row.partsonaPallet : "—",
        row.releaseId,
        shippingLineQtyAsPalletsDisplay(displayGrandTotal, row, sPalletMode),
        shippingLineQtyAsPalletsDisplay(unassigned, row, sPalletMode),
        ...dues,
        ...(dueColumns.length > 0 ? [dueTot] : []),
        shipmentQty,
        row.source,
        row.destination,
        estBoxes,
        "EA",
      ];
    });
    const aspLineHeaders = [
      "Line #",
      "Part Number",
      "Description",
      "PO Number",
      "Number of Boxes",
      "Qty Per Box",
      "Number of Pallet(s)",
      "Planned Ship Quantity",
      "UOM",
      "Mixed Pallet Y/N",
      "Dimensions L x W x H",
      "Total Ship Weight",
      "Total Cube",
      "Defer? (Y/N)",
      "ShipmentReleaseId",
    ];
    const aspLineRows = rowsForShipment.map((row, i) => {
      const shipmentQty = row.loads[loadIdx] || 0;
      const estBoxes = formatShipmentUnitRatio(shipmentQty, row.boxQty);
      const estPallets = formatShipmentUnitRatio(shipmentQty, row.partsonaPallet);
      return [
        i + 1,
        row.sku,
        row.desc,
        row.po,
        estBoxes,
        row.boxQty,
        estPallets,
        shipmentQty,
        "EA",
        "N",
        loadBoardDimensionsOrDefault(row.palletDimensions),
        computeLoadBoardTotalShipWeightEach(shipmentQty, row),
        "",
        "",
        row.releaseId || "",
      ];
    });
    const stamp = sShipDate && /^\d{4}-\d{2}-\d{2}$/.test(sShipDate) ? sShipDate.replace(/-/g, "") : new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const fileName = `${stamp}_${sanitizeFilenamePart(`Shipment_${loadIdx + 1}_LoadBoard`)}.xlsx`;
    const aspSheetAoa = [...metaRows, ["", "", "", "", "", ""], aspLineHeaders, ...aspLineRows];
    const wb = buildLoadBoardTableWorkbook({
      metaRows,
      headerRow,
      dataRows,
      extraSheets: [{ name: "ASP line items", aoa: aspSheetAoa }],
    });
    return { wb, fileName };
  },
  [
    customers,
    sCustomer,
    sShipTo,
    sShipDate,
    dueColumns,
    fmtDueLabel,
    filteredShippingRows,
    sumForDates,
    shippingApiRows,
    shippingSelectedCustomerName,
    normalizedLoadBoardShipmentFormLayout,
    effectiveLoadBoardShipmentFormLayout,
    sPalletMode,
  ],
  );

  const handleUploadAllLoadBoardExcels = useCallback(async () => {
    if (sNumLoads < 1) return;

    let mappingProfile = aspMappings.find((m) => String(m.id) === String(sAspMappingProfile));
    if (!mappingProfile && aspMappings.length === 1) {
      mappingProfile = aspMappings[0];
    }

    setSAspFormsUploading(true);
    addToast(`Building ${sNumLoads} ASP form(s) from COMMON template…`);
    try {
      if (mappingProfile) {
        await ensureSpvpAspScript();
      }

      const templateRes = await SpvpAspService.downloadCommonAspTemplate();
      if (!templateRes || templateRes.status !== 200) {
        throw new Error(
          templateRes?.errorMessage?.message ||
            templateRes?.message ||
            "Failed to download COMMON_ASP_Form.xlsx from OneDrive.",
        );
      }
      const templatePayload = templateRes.response || templateRes.Response || {};
      const templateBase64 = templatePayload.fileBase64 || templatePayload.FileBase64;
      if (!templateBase64) {
        throw new Error("COMMON ASP template download did not return fileBase64.");
      }

      // Keep original template bytes — do NOT round-trip through SheetJS (it strips logo/borders/styles).
      const templateBytes = base64ToUint8Array(templateBase64);

      // Always fill from Load board (label-based); ASP mapping optional override.
      if (!mappingProfile) {
        addToast("Filling COMMON template from Load board values (no ASP mapping selected).", "info");
      }

      const customerCode =
        resolvePlannerCustomerCodeForApi(sCustomer, shippingApiRows) ||
        String(sCustomer || "").trim();
      const customerName =
        shippingSelectedCustomerName ||
        customers[sCustomer]?.name ||
        customerCode ||
        "";

      for (let i = 0; i < sNumLoads; i++) {
        const formProps = computeLoadBoardShipmentFormPropsForIndex(
          i,
          {
            filteredShippingRows,
            shippingApiRows,
            shippingSelectedCustomerName,
            sShipTo,
            sShipDate,
            sCustomer,
          },
          {
            supplierDefaults:
              normalizedLoadBoardShipmentFormLayout?.supplierDefaults ?? DEFAULT_SUPPLIER_SHIP_FROM,
            savedAspByLoadIndex: sLoadBoardSavedAspByLoadIndex,
          },
        );

        const destination =
          sDestinationFilter !== "all"
            ? sDestinationFilter
            : [formProps.shipToLine1, formProps.shipToLine2, formProps.shipToLine3]
                .filter(Boolean)
                .join(", ") || (sShipTo !== "all" ? sShipTo : "");

        const lineRows = buildAspLineRowsForFill({
          loadIdx: i,
          filteredShippingRows,
          dueColumns,
          palletMode: sPalletMode,
          cellOverrides: sLoadBoardCellOverridesRef.current,
        });
        if (!lineRows.length) {
          throw new Error(`Shipment ${i + 1} has no assigned quantities. Assign qty before Load ASP forms.`);
        }

        // Always fill a COPY of COMMON template with Load board values (in memory).
        // Master COMMON_ASP_Form.xlsx is never overwritten.
        const shipmentOnly = buildAspShipmentOnlyFromFormProps(formProps, {
          shipDate: sShipDate,
          customerCode,
        });
        const filledBytes = await fillAspTemplatePreservingStyles(
          templateBytes,
          mappingProfile || null,
          lineRows,
          shipmentOnly,
        );
        const fileBase64 = uint8ArrayToBase64(filledBytes);

        const fileName = buildLoadedAspFormFileName({
          customerCode,
          customerName,
          shipmentNumber: formProps.shipmentNumber,
          shipDate: sShipDate,
          loadIndex: i,
        });

        const response = await SpvpAspService.generateAndUploadAspForm({
          customerCode: customerCode || null,
          customerName: customerName || null,
          destination: destination || null,
          shipmentNumber: formProps.shipmentNumber || null,
          shipDate: sShipDate || null,
          fileName,
          fileBase64,
          outputFolderName: LOADED_ASP_FORMS_FOLDER,
          neverOverwriteCommonTemplate: true,
        });

        if (!response || response.status !== 200) {
          const errMsg =
            response?.errorMessage?.message ||
            response?.message ||
            `Upload failed for Shipment ${i + 1}.`;
          if (/locked|resourceLocked|notAllowed/i.test(String(errMsg))) {
            throw new Error(
              `${errMsg} Close the file in Excel/OneDrive (or use a new filename), then try again.`,
            );
          }
          throw new Error(errMsg);
        }
      }
      addToast(
        `Saved ${sNumLoads} ASP form(s) to ${LOADED_ASP_FORMS_FOLDER} with Load board values (common template unchanged).`,
        "success",
      );
    } catch (e) {
      addToast(e?.message || "ASP forms upload failed.", "error");
    } finally {
      setSAspFormsUploading(false);
    }
  }, [
    sNumLoads,
    addToast,
    aspMappings,
    sAspMappingProfile,
    sCustomer,
    shippingApiRows,
    shippingSelectedCustomerName,
    customers,
    filteredShippingRows,
    sShipTo,
    sShipDate,
    sDestinationFilter,
    normalizedLoadBoardShipmentFormLayout,
    sLoadBoardSavedAspByLoadIndex,
    dueColumns,
    sPalletMode,
  ]);

  const handleLoadToShipment = useCallback(async () => {
    if (sNumLoads < 1) {
      addToast("Add at least one shipment column before using Load to Shipment.", "error");
      return;
    }
    const loadIdx = loadBoardViewLoadIndex;
    const rawCustomer = String(sCustomer || "").trim();
    let customerNum = resolvePlannerCustomerIdForUpsert(sCustomer, shippingSelectedCustomerName, shippingApiRows);
    if (!customerNum && rawCustomer && !rawCustomer.startsWith("__name:")) {
      customerNum = rawCustomer;
    }
    const customerName = resolvePlannerCustomerNameForUpsert(sCustomer, shippingSelectedCustomerName, shippingApiRows);
    if (!customerNum) {
      addToast("Select a customer with a recognized customer id before loading to shipment.", "error");
      return;
    }
    const hasAnyQty = filteredShippingRows.some((r) => (r.loads?.[loadIdx] || 0) > 0);
    if (!hasAnyQty) {
      addToast("Assign at least one quantity to this shipment column before loading to shipment.", "error");
      return;
    }

    const formProps = computeLoadBoardShipmentFormPropsForIndex(
      loadIdx,
      {
        filteredShippingRows,
        shippingApiRows,
        shippingSelectedCustomerName,
        sShipTo,
        sShipDate,
        sCustomer,
      },
      {
        supplierDefaults:
          normalizedLoadBoardShipmentFormLayout?.supplierDefaults ?? DEFAULT_SUPPLIER_SHIP_FROM,
      },
    );
    const shipDateYmdFallback = demandDateToYmd(sShipDate);
    const savedAspNum = String(sLoadBoardSavedAspByLoadIndex[loadIdx] || "").trim();
    const headerObj = buildPlannerUpsertShippingHeaderObject(formProps, {
      customerNum,
      customerName,
      shipDateYmdFallback,
      aspNumForUpsert: savedAspNum,
    });
    const itemsArr = buildPlannerUpsertShippingItemsFromLoadBoard({
      loadIdx,
      rows: filteredShippingRows,
      cellOverrides: sLoadBoardCellOverridesRef.current,
      dueColumns,
      palletMode: sPalletMode,
      shippingApiRows,
    });
    const demandMatchErrors = itemsArr
      .map((item) => item?._demandMatchError)
      .filter((msg) => String(msg || "").trim());
    if (demandMatchErrors.length) {
      const extra = demandMatchErrors.length > 1 ? ` (+${demandMatchErrors.length - 1} more line(s))` : "";
      addToast(`${demandMatchErrors[0]}${extra}`, "error");
      return;
    }
    const itemsForApi = itemsArr.map(stripLoadBoardUpsertItemMeta);
    const stamp =
      sShipDate && /^\d{4}-\d{2}-\d{2}$/.test(sShipDate)
        ? sShipDate.replace(/-/g, "")
        : new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const sourceFileName = `${stamp}_${sanitizeFilenamePart(`Shipment_${loadIdx + 1}_LoadBoard`)}.xlsx`;
    const requestedAspNum = savedAspNum;

    setSLoadToShipmentLoading(true);
    addToast(`Saving Shipment ${loadIdx + 1} to server…`);
    try {
      const response = await PlannerService.upsertShipmentsAndItems({
        shippingJson: JSON.stringify(headerObj),
        shippingItemsJson: JSON.stringify(itemsForApi),
        updateFullHeader: true,
        softDeleteMissingItems: false,
        requestedAspNum,
        sourceFileName,
      });
      const st = response?.status ?? response?.Status;
      const isOk = st == null || st === 200 || st === "200";

      if (!isOk) {
        const errPayload = response?.errorMessage ?? response?.ErrorMessage;
        const errMsg =
          (typeof errPayload === "object" && errPayload?.message) ||
          (typeof errPayload === "string" ? errPayload : "") ||
          response?.message ||
          response?.Message ||
          "Load to Shipment failed.";
        throw new Error(String(errMsg).trim());
      }

      const upsertResult = pickUpsertShipmentResult(response);
      const assignedAspNum = pickAspNumFromUpsertResult(upsertResult) || savedAspNum;
      if (assignedAspNum) {
        setSLoadBoardSavedAspByLoadIndex((prev) => ({ ...prev, [loadIdx]: assignedAspNum }));
      }

      const successPayload = response?.errorMessage ?? response?.ErrorMessage;
      const apiSuccessMsg = String(
        response?.message ||
          response?.Message ||
          (typeof successPayload === "object" && successPayload?.message) ||
          (typeof successPayload === "string" ? successPayload : "") ||
          "",
      ).trim();

      addToast(
        apiSuccessMsg ||
          (assignedAspNum
            ? `Shipment ${loadIdx + 1} saved (ASP ${assignedAspNum}).`
            : `Shipment ${loadIdx + 1} saved.`),
        "success",
      );
    } catch (e) {
      addToast(e?.message || "Load to Shipment failed.", "error");
    } finally {
      setSLoadToShipmentLoading(false);
    }
  }, [
    sNumLoads,
    loadBoardViewLoadIndex,
    sCustomer,
    shippingApiRows,
    filteredShippingRows,
    shippingSelectedCustomerName,
    sShipTo,
    sShipDate,
    normalizedLoadBoardShipmentFormLayout,
    sLoadBoardSavedAspByLoadIndex,
    sLoadBoardCellOverrides,
    dueColumns,
    sPalletMode,
    addToast,
  ]);

  const resolveLoadBoardHeaderValue = (fieldKey) => {
    const k = String(fieldKey || "").trim();
    if (!k) return "";
    if (k === "customerName") return shippingSelectedCustomerName || customers?.[sCustomer]?.name || sCustomer || "";
    if (k === "customerCode") return sCustomer || "";
    if (k === "shipDate") return sShipDate || "";
    if (k === "shipTo") return sShipTo === "all" ? "All locations" : sShipTo || "";
    if (k === "shipmentNumber") return `Shipment ${loadBoardViewLoadIndex + 1}`;
    if (k === "exportedOn") return new Date().toLocaleString();
    return "";
  };

  const resolveLoadBoardCell = (col, row, rowIndex) => {
    const key = String(col?.fieldKey || col?.key || "").trim();
    return resolveLoadBoardCellValue({
      loadIdx: loadBoardViewLoadIndex,
      row,
      rowIndex,
      fieldKey: key,
      dueColumns,
      palletMode: sPalletMode,
      cellOverrides: {},
    });
  };

  const resolveLoadBoardDisplayCell = (col, row, rowIndex) => {
    const key = String(col?.fieldKey || col?.key || "").trim();
    if (!key) return "";
    if (key === "totalShipWeight") return resolveLoadBoardCell(col, row, rowIndex);
    return resolveLoadBoardCellValue({
      loadIdx: loadBoardViewLoadIndex,
      row,
      rowIndex,
      fieldKey: key,
      dueColumns,
      palletMode: sPalletMode,
      cellOverrides: sLoadBoardCellOverrides,
    });
  };

  const updateLoadBoardCell = (rowKey, fieldKey, rawValue) => {
    const key = canonicalLoadBoardFieldKey(fieldKey);
    if (!key || LOAD_BOARD_READONLY_FIELD_KEYS.has(key)) return;
    if (key === "plannedShipQuantity") {
      updateShipmentCell(rowKey, loadBoardViewLoadIndex, rawValue);
      return;
    }
    const overrideKey = `${loadBoardViewLoadIndex}::${rowKey}::${key}`;
    setSLoadBoardCellOverrides((prev) => {
      const next = { ...prev, [overrideKey]: rawValue };
      sLoadBoardCellOverridesRef.current = next;
      return next;
    });
  };

  const loadBoardTableColumns = useMemo(() => {
    const cols =
      Array.isArray(sLoadBoardSchema) && sLoadBoardSchema.length
        ? sLoadBoardSchema
        : DEFAULT_SUPPLIER_SHIP_PLAN_LOAD_BOARD_COLUMNS;
    return filterLoadBoardTableColumns(cols);
  }, [sLoadBoardSchema]);

  const materialSummary = useMemo(() => {
    if (mComponentTypesLoading) return "Loading component types…";
    if (mComponentTypesError) return mComponentTypesError;
    if (mWorkspaceLoading) return `Loading ${mComponentType || "MATERIAL"} workspace data…`;
    if (mWorkspaceError) return mWorkspaceError;
    if (!materialRows.length) return "No material lines for this selection.";
    const customerLabel = mCustomer || "All customers";
    return `${materialRows.length} material line(s) · ${materialDateIndices.length} day column(s) · ${mComponentType || "MATERIAL"} · ${customerLabel}`;
  }, [materialRows.length, materialDateIndices.length, mCustomer, mComponentType, mComponentTypesLoading, mComponentTypesError, mWorkspaceLoading, mWorkspaceError]);

  return (
    <div style={{ height: 'auto' }}>
      <div className="page-wrapper" style={{ height: 'auto' }}>
        <div
          className="content planning-workspace-shell"
          style={{
            height: 'auto',
            display: 'block',
          }}
        >
          <div className="min-h-screen bg-slate-100 px-4 py-6 text-slate-900 md:px-6 lg:px-8">
            <div className="mx-auto max-w-[1960px] space-y-4">
        <PanelCard>
          <div className="flex flex-col gap-4 px-5 py-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold tracking-tight">Planning Workspace</h1>
              <p className="max-w-3xl text-sm text-slate-500">{plannerSubtitle}</p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600">
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.16)]" />
                Live planning
              </div>
              <div className="inline-flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-slate-50 p-1.5">
                <PlannerTabButton active={activePlanner === "demand"} label="Demand Planner" icon={Factory} onClick={() => setActivePlanner("demand")} />
                <PlannerTabButton active={activePlanner === "shipping"} label="Shipping Planner" icon={Package2} onClick={() => setActivePlanner("shipping")} />
                <PlannerTabButton active={activePlanner === "material"} label="Material Planner" icon={Boxes} onClick={() => setActivePlanner("material")} />
              </div>
            </div>
          </div>
        </PanelCard>

        {activePlanner === "demand" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <PanelCard>
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex flex-wrap items-end gap-3">
                  <Field label="Customer">
                    <Select
                      instanceId="planner-demand-customer"
                      className="min-w-[220px] max-w-[min(100%,320px)]"
                      classNamePrefix="planner-rs-demand-cust"
                      options={demandCustomerSelectOptions}
                      value={demandCustomerSelectValue}
                      onChange={(opt) => setDCustomer(opt?.value ?? "")}
                      isDisabled={!plannerCustomerListReady}
                      isSearchable
                      isClearable={false}
                      placeholder={plannerCustomerListReady ? "All customers" : "Loading customers…"}
                      styles={PLANNER_CUSTOMER_SELECT_STYLES}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      aria-label="Customer"
                    />
                  </Field>
                  <Field label="Part #">
                    <input value={dPartSearch} onChange={(e) => setDPartSearch(e.target.value)} placeholder="Search part #, description…" className="input-field w-[220px]" />
                  </Field>
                  <Field label="From">
                    <input type="date" value={dDateFrom} onChange={(e) => setDDateFrom(e.target.value)} className="input-field w-[150px]" />
                  </Field>
                  <Field label="To">
                    <input type="date" value={dDateTo} onChange={(e) => setDDateTo(e.target.value)} className="input-field w-[150px]" />
                  </Field>
                  <Field label="Ship-To">
                    <select value={dShipTo} onChange={(e) => setDShipTo(e.target.value)} className="input-field min-w-[180px]">
                      {demandShipToOptions.map((shipTo) => (
                        <option key={shipTo || "all"} value={shipTo || "all"}>
                          {shipTo || "All locations"}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <div className="ml-auto flex flex-wrap items-center gap-2">
                    <Segmented
                      value={dQTySource}
                      onChange={setDQTySource}
                      options={[
                        { value: "demandOnHand", label: "On Hand Qty" },
                        { value: "iqmsOnHand", label: "IQMS On Hand Qty" },
                      ]}
                    />
                    <Segmented
                      value={dUom ?? ""}
                      onChange={(v) => setDUom(v === "" ? null : v)}
                      options={[{ value: "each", label: "Each" }, { value: "pallet", label: "Pallet" }]}
                    />
                    <button type="button" onClick={resetDemandFilters} className="secondary-btn">Reset filters</button>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <ToggleButton active={dHighlightShortage} onClick={() => setDHighlightShortage((v) => !v)}>Highlight shortages</ToggleButton>
                  <ToggleButton active={dHighlightLow} onClick={() => setDHighlightLow((v) => !v)}>Low on-hand</ToggleButton>
                  <ToggleButton active={dSixtyDayOutOfStock} onClick={() => setDSixtyDayOutOfStock((v) => !v)}>
                    60 Days Out Of Stock
                  </ToggleButton>
                </div>
                <p className="text-xs text-slate-500">
                  Customer list loads from the server first; demand loads immediately for “All customers” (first option), or pick one customer to filter. When <strong>Show only Days in Range</strong> is on (default), days with no demand are hidden; turn it off to show every calendar day between From and To. <strong>60 Days Out Of Stock</strong> limits day columns to the next 60 days and lists only parts whose projected on-hand (by column order) goes below zero in that window. Widen From/To if demand falls outside that window.
                </p>
              </div>
            </PanelCard>

            <PanelCard>
              <ToolbarSummary
                summary={demandSummary}
                center={
                  <ToggleButton
                    active={dShowOnlyDemandDaysInRange}
                    onClick={() => setDShowOnlyDemandDaysInRange((v) => !v)}
                  >
                    Show only Days in Range
                  </ToggleButton>
                }
                legend={[
                  { label: "Demand", className: "bg-blue-100" },
                  { label: "Covered", className: "bg-emerald-100" },
                  { label: "Shortage", className: "bg-red-100" },
                ]}
              />
              <div className="max-h-[min(68vh,calc(100vh-16rem))] overflow-y-auto overflow-x-auto">
                {!plannerCustomerListReady ? (
                  <div className="flex flex-col items-center justify-center gap-3 py-20 text-slate-600">
                    <RefreshCw className="h-8 w-8 shrink-0 animate-spin text-slate-500" aria-hidden />
                    <p className="text-sm font-medium text-slate-700">Loading customers…</p>
                    <p className="max-w-md text-center text-xs text-slate-500">Fetching the customer list for the dropdown. Demand lines load after you select a customer.</p>
                  </div>
                ) : demandApiLoading ? (
                  <div className="flex items-center justify-center gap-2 py-16 text-slate-600">
                    <RefreshCw className="h-5 w-5 animate-spin" />
                    <span>Loading demand…</span>
                  </div>
                ) : demandApiError ? (
                  <EmptyState title="Could not load demand" description={demandApiError} />
                ) : !demandMatrixRows.length ? (
                  <EmptyState
                    title="No demand lines to show"
                    description="Widen the From/To dates, clear part search, or adjust Ship-To. No lines matched this filter."
                  />
                ) : !demandMatrixRowsForTable.length ? (
                  <EmptyState
                    title="No projected stock-outs in this window"
                    description="60 Days Out Of Stock is on: only parts whose running balance (on-hand minus cumulative demand, left to right) goes below zero in the visible day columns are shown. Try turning 60 Days off, widening From/To, or changing the on-hand source."
                  />
                ) : (
                  <table className="min-w-full table-fixed border-separate border-spacing-0 text-sm">
                    <thead>
                      <tr>
                        <StickyHeader left={0} width={DM_COL_PART}>Part Number</StickyHeader>
                        <StickyHeader left={DM_COL_PART} width={DM_COL_DESC}>Description</StickyHeader>
                        <StickyHeader left={DM_LEFT_STATUS} width={DM_COL_STATUS}>Status</StickyHeader>
                        <StickyHeader left={DM_LEFT_ONHAND} width={DM_COL_ONHAND} align="right">
                          {dQTySource === "iqmsOnHand" ? "IQMS On Hand Qty" : "On Hand Qty"}
                        </StickyHeader>
                        {demandMatrixDatesForTable.map((d) => {
                          const iso = fmtDate(d);
                          return (
                            <th
                              key={iso}
                              className="sticky top-0 z-20 min-w-[80px] border-b border-r border-slate-200 bg-slate-100 px-3 py-2 text-right text-[11px] font-bold uppercase tracking-[0.04em] text-slate-500"
                            >
                              {fmtHeader(d)}
                            </th>
                          );
                        })}
                        <th className="sticky top-0 z-20 min-w-[96px] border-b border-r border-slate-200 bg-slate-200 px-3 py-2 text-right text-[11px] font-bold uppercase tracking-[0.04em] text-slate-700">Grand Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {demandMatrixRowsForTable.map((part, rowIndex) => {
                        const partLabel = String(part.partNumber || "—");
                        const alt = rowIndex % 2 === 1;

                        const onHand = part.onHand;
                        let cumulativeEach = 0;
                        let grandTotalEach = 0;

                        demandMatrixDatesForTable.forEach((d) => {
                          const iso = fmtDate(d);
                          grandTotalEach += Number(part.qtyByIso?.[iso] || 0);
                        });

                        const lineShort =
                          dHighlightShortage &&
                          onHand !== null &&
                          Number.isFinite(onHand) &&
                          grandTotalEach > onHand;

                        return (
                          <tr key={`${part.key}-${rowIndex}`} className={alt ? "bg-slate-200" : "bg-white"}>
                            <StickyCell
                              alternateRow={alt}
                              className="z-[5] truncate border-b border-r border-slate-200 px-2 py-2 font-mono text-xs font-medium"
                              style={{ left: 0, width: DM_COL_PART, minWidth: DM_COL_PART, maxWidth: DM_COL_PART }}
                              title={partLabel}
                            >
                              {partLabel}
                            </StickyCell>
                            <StickyCell
                              alternateRow={alt}
                              className="z-[5] truncate border-b border-r border-slate-200 px-2 py-2 text-left text-xs"
                              style={{ left: DM_COL_PART, width: DM_COL_DESC, minWidth: DM_COL_DESC, maxWidth: DM_COL_DESC }}
                              title={part.desc ? String(part.desc) : ""}
                            >
                              {part.desc || "—"}
                            </StickyCell>
                            <StickyCell
                              alternateRow={alt}
                              className={classNames(
                                "z-[5] border-b border-r border-slate-200 px-2 py-2 text-left text-xs font-medium",
                                getPartStatusClass(part.status),
                              )}
                              style={{ left: DM_LEFT_STATUS, minWidth: DM_COL_STATUS }}
                            >
                              {part.status || "—"}
                            </StickyCell>
                            <StickyCell
                              alternateRow={alt}
                              className={classNames(
                                "z-[5] border-b border-r border-slate-200 px-2 py-2 text-right text-xs",
                                dHighlightLow && onHand !== null && onHand <= CRITICAL_STOCK && "bg-red-100 font-bold text-red-700",
                                dHighlightLow &&
                                  onHand !== null &&
                                  onHand > CRITICAL_STOCK &&
                                  onHand < LOW_STOCK &&
                                  "bg-amber-100 font-semibold text-amber-700",
                              )}
                              style={{ left: DM_LEFT_ONHAND, minWidth: DM_COL_ONHAND }}
                              title={
                                dQTySource === "demandOnHand"
                                  ? "onHand from API (falls back to inventoryOnHand if missing)"
                                  : "iqmsOnHand / IQMSOnHand from API"
                              }
                            >
                              {onHand !== null && Number.isFinite(onHand) ? onHand : "—"}
                            </StickyCell>

                            {demandMatrixDatesForTable.map((d) => {
                              const iso = fmtDate(d);
                              const qtyEach = Number(part.qtyByIso?.[iso] || 0);
                              cumulativeEach += qtyEach;
                              const projected = onHand !== null && Number.isFinite(onHand) ? onHand - cumulativeEach : null;

                              if (!dUom) {
                                return (
                                  <td
                                    key={`${part.key}-${iso}-nouom`}
                                    className={classNames(
                                      "border-b border-r border-slate-200 px-3 py-2 text-right text-xs text-slate-400",
                                      alt ? "bg-slate-200" : "bg-slate-50",
                                    )}
                                    title="Select Each or Pallet to show quantities"
                                  >
                                    —
                                  </td>
                                );
                              }

                              const cellShort = dHighlightShortage && projected !== null && projected < 0 && qtyEach > 0;
                              const cellAvail =
                                dHighlightShortage &&
                                projected !== null &&
                                projected >= 0 &&
                                qtyEach > 0 &&
                                cumulativeEach <= onHand;

                              let displayText = "—";
                              let titleDetail = "";
                              if (qtyEach !== 0) {
                                if (dUom === "each") {
                                  displayText = String(qtyEach);
                                  titleDetail = `Each · Qty ${qtyEach}`;
                                } else {
                                  const perPallet = Number(part.perPallet);
                                  if (Number.isFinite(perPallet) && perPallet > 0) {
                                    const pallets = qtyEach / perPallet;
                                    displayText = String(Number.isInteger(pallets) ? pallets : Number(pallets.toFixed(2)));
                                    titleDetail = `Pallet · Qty ${qtyEach} ÷ PartsonaPallet ${perPallet} = ${displayText}`;
                                  } else {
                                    displayText = "—";
                                    titleDetail = "PartsonaPallet missing — cannot show pallets";
                                  }
                                }
                              }

                              return (
                                <td
                                  key={`${part.key}-${iso}`}
                                  className={classNames(
                                    "border-b border-r border-slate-200 px-3 py-2 text-right text-xs",
                                    qtyEach === 0 && (alt ? "bg-slate-200 text-slate-500" : "bg-slate-50 text-slate-400"),
                                    qtyEach !== 0 && displayText !== "—" && !cellShort && !cellAvail && "bg-blue-50 text-slate-800",
                                    cellAvail && "bg-emerald-50 text-emerald-800",
                                    cellShort && "bg-red-50 font-semibold text-red-700",
                                  )}
                                  title={
                                    qtyEach === 0
                                      ? ""
                                      : `${titleDetail}${onHand !== null && Number.isFinite(onHand) ? ` · Projected ${projected}` : ""}`
                                  }
                                >
                                  {displayText}
                                </td>
                              );
                            })}

                            <td
                              className={classNames(
                                "border-b border-r border-slate-200 px-3 py-2 text-right font-semibold",
                                alt ? "bg-slate-300" : "bg-slate-200",
                                dHighlightShortage && onHand !== null && Number.isFinite(onHand) && grandTotalEach > onHand && "bg-red-50 text-red-700",
                              )}
                              title="Sum of visible dates (same UOM as columns)"
                            >
                              {!dUom
                                ? "—"
                                : dUom === "each"
                                  ? grandTotalEach
                                  : (() => {
                                      const perPallet = Number(part.perPallet);
                                      if (Number.isFinite(perPallet) && perPallet > 0) {
                                        const v = grandTotalEach / perPallet;
                                        return Number.isInteger(v) ? v : Number(v.toFixed(2));
                                      }
                                      return "—";
                                    })()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </PanelCard>
          </motion.div>
        )}

        {activePlanner === "shipping" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
            <PanelCard>
              <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                <div className="flex flex-wrap items-end gap-3">
                  <Field label="Customer">
                    <Select
                      instanceId="planner-shipping-customer"
                      className="min-w-[220px] max-w-[min(100%,320px)]"
                      classNamePrefix="planner-rs-ship-cust"
                      options={shippingCustomerSelectOptions}
                      value={shippingCustomerSelectValue}
                      onChange={(opt) => setSCustomer(opt?.value ?? "")}
                      isDisabled={!plannerCustomerListReady}
                      isSearchable
                      isClearable={false}
                      placeholder={plannerCustomerListReady ? "Select Customer" : "Loading customers…"}
                      styles={PLANNER_CUSTOMER_SELECT_STYLES}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      aria-label="Customer"
                    />
                  </Field>
                  <Field label="Ship-To">
                    <select value={sShipTo} onChange={(e) => setSShipTo(e.target.value)} className="input-field min-w-[180px]">
                      {shippingShipToOptions.map((shipTo) => (
                        <option key={shipTo || "all"} value={shipTo || "all"}>
                          {shipTo || "All locations"}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Part #">
                    <input value={sPartSearch} onChange={(e) => setSPartSearch(e.target.value)} placeholder="Filter parts…" className="input-field w-[190px]" />
                  </Field>
                  <Field label="PO Number">
                    <input value={sPoSearch} onChange={(e) => setSPoSearch(e.target.value)} placeholder="Filter PO…" className="input-field w-[160px]" />
                  </Field>
                  <Field label="Source">
                    <select value={sSourceFilter} onChange={(e) => setSSourceFilter(e.target.value)} className="input-field min-w-[130px]">
                      <option value="all">All sources</option>
                      {shippingSourceOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  </Field>
                  <Field label="Due date">
                    <Select
                      instanceId="planner-shipping-due-dates"
                      className="min-w-[200px] max-w-[min(100%,320px)]"
                      classNamePrefix="planner-rs-ship-due"
                      isMulti
                      isClearable
                      closeMenuOnSelect={false}
                      options={shippingDueDateSelectOptions}
                      value={shippingDueDateSelectValue}
                      onChange={(opts) => {
                        const next = Array.isArray(opts) ? opts.map((o) => o.value).filter(Boolean) : [];
                        setSSelectedDueDates([...next].sort());
                      }}
                      placeholder="All dates"
                      styles={PLANNER_CUSTOMER_SELECT_STYLES}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      aria-label="Due date"
                      isDisabled={!shippingDueDateSelectOptions.length}
                    />
                  </Field>
                  <Field label="Destination">
                    <select value={sDestinationFilter} onChange={(e) => setSDestinationFilter(e.target.value)} className="input-field min-w-[130px]">
                      <option value="all">All destinations</option>
                      {shippingDestinationOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                    </select>
                  </Field>
                  <Field label="Pallet">
                    <button
                      type="button"
                      onClick={handlePallet}
                      className={classNames(
                        "h-[38px] min-w-[88px] rounded-[0.85rem] px-[0.95rem] text-[0.8125rem] font-bold transition-all duration-150",
                        sPalletMode
                          ? "border border-blue-700 bg-blue-600 text-white shadow-sm hover:bg-blue-700"
                          : "secondary-btn",
                      )}
                    >
                      Pallet
                    </button>
                  </Field>
                  <div className="ml-auto flex items-end gap-2">
                    <Field label="ASP">
                      <button type="button" onClick={() => setAspSettingsOpen(true)} className="secondary-btn h-[38px]">
                        ASP Settings
                      </button>
                    </Field>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-end gap-3 px-4 py-3">
                <Field label="ASP mapping">
                  <select value={sAspMappingProfile} onChange={(e) => setSAspMappingProfile(e.target.value)} className="input-field min-w-[220px]">
                    <option value="">— Select ASP mapping —</option>
                    {aspMappings.map((mapping) => (
                      <option key={mapping.id || mapping.name} value={mapping.id}>{mapping.name || mapping.id}</option>
                    ))}
                  </select>
                </Field>
                <button type="button" onClick={addShipment} className="primary-btn">Add Shipment</button>
                <button type="button" onClick={resetShipments} className="accent-btn">Reset Shipments</button>
                <button type="button" onClick={openShippingLoadBoardModal} className="accent-btn">Load to Form</button>
                <button type="button" onClick={handleAddToShipment} className="accent-btn">Add to Shipment</button>
                <span className="ml-auto text-xs text-slate-500">Select due date(s) above → click a due qty to fill the highlighted shipment.</span>
              </div>
            </PanelCard>

            <PanelCard>
                <ToolbarSummary
                  summary={shippingSummary}
                  legend={[
                    { label: "Due qty remaining", className: "bg-blue-100" },
                    { label: "Selected date total", className: "bg-slate-200" },
                    { label: "Shipment qty", className: "bg-sky-100" },
                    { label: "Unassigned", className: "bg-amber-100" },
                    ...(sPalletMode ? [{ label: "Grand / Unassigned = pallets", className: "bg-violet-100" }] : []),
                  ]}
                />
                <div className="max-h-[min(68vh,calc(100vh-16rem))] overflow-y-auto overflow-x-auto">
                  {!plannerCustomerListReady ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-20 text-slate-600">
                      <RefreshCw className="h-8 w-8 shrink-0 animate-spin text-slate-500" aria-hidden />
                      <p className="text-sm font-medium text-slate-700">Loading customers…</p>
                      <p className="max-w-md text-center text-xs text-slate-500">Fetching the customer list for the dropdown. Shipping lines load after you select a customer.</p>
                    </div>
                  ) : shippingApiLoading ? (
                    <div className="flex items-center justify-center gap-2 py-16 text-slate-600">
                      <RefreshCw className="h-5 w-5 animate-spin" />
                      <span>Loading shipping lines…</span>
                    </div>
                  ) : shippingApiError ? (
                    <EmptyState title="Could not load shipping demand" description={shippingApiError} />
                  ) : !filteredShippingRows.length ? (
                    <EmptyState
                      title={String(sCustomer || "").trim() ? "No shipping lines for this filter" : "Select a customer"}
                      description={
                        String(sCustomer || "").trim()
                          ? "Clear part search, widen Source / Destination / Due date filters, or pick a different ship-to."
                          : "Select a customer to load data."
                      }
                    />
                  ) : (
                    <table className="w-max min-w-full border-separate border-spacing-0 text-sm">
                      <thead>
                        <tr>
                          {[
                            ["Part Number", 0, 110, "left"],
                            ["Description", 110, 180, "left"],
                            ["PO", 290, 80, "left"],
                            ["Pallet Inv", 370, 90, "right"],
                            ["Box Qty", 460, 70, "right"],
                            ["Pallet Qty", 530, 90, "right"],
                            ["Release ID", 620, 110, "left"],
                            [sPalletMode ? "Grand total (plt)" : "Grand Total", 730, 90, "right"],
                            [sPalletMode ? "Unassigned (plt)" : "Unassigned", 820, 90, "right"],
                          ].map(([label, left, width, align]) => (
                            <StickyHeader key={String(label)} left={left} width={width} align={align}>
                              {label}
                            </StickyHeader>
                          ))}
                          {dueColumns.map((due) => (
                            <th key={due} className="sticky top-0 z-20 min-w-[98px] border-b border-r border-slate-200 bg-slate-100 px-3 py-2 text-center text-[11px] font-bold uppercase tracking-[0.04em] text-slate-600">
                              <div className="text-[11px] font-bold normal-case text-slate-800">{fmtDueLabel(due)}</div>
                              <div className="mt-1 text-[9px] uppercase tracking-[0.08em] text-slate-500">
                                {sPalletMode ? "Ship qty (plt)" : "Ship qty"}
                              </div>
                            </th>
                          ))}
                          {dueColumns.length > 0 && (
                            <th className="sticky top-0 z-20 min-w-[88px] border-b border-r border-slate-200 bg-slate-200 px-3 py-2 text-center text-[10px] font-bold uppercase tracking-[0.04em] text-slate-700">
                              <div>Total</div>
                              <div className="mt-1 text-[9px] text-slate-500">{sPalletMode ? "Remaining (plt)" : "Remaining"}</div>
                            </th>
                          )}
                          {Array.from({ length: sNumLoads }).map((_, loadIndex) => (
                            <th
                              key={`load-${loadIndex}`}
                              onClick={() => setSActiveLoad(loadIndex)}
                              className={classNames(
                                "sticky top-0 z-20 min-w-[110px] cursor-pointer border-b border-r px-3 py-2 text-center text-[11px] font-bold uppercase tracking-[0.04em]",
                                loadIndex === sActiveLoad
                                  ? shippingActiveShipmentHeaderIsRed(loadIndex)
                                    ? "bg-red-600 text-white hover:bg-red-700"
                                    : "bg-blue-700 text-white hover:bg-blue-800"
                                  : shippingActiveShipmentHeaderIsRed(loadIndex)
                                    ? "bg-red-200 text-red-900 hover:bg-red-300"
                                    : loadIndex % 4 === 0
                                      ? "bg-blue-100 text-blue-800"
                                      : loadIndex % 4 === 1
                                        ? "bg-violet-100 text-violet-800"
                                        : loadIndex % 4 === 2
                                          ? "bg-emerald-100 text-emerald-800"
                                          : "bg-orange-100 text-orange-800",
                              )}
                            >
                              Shipment {loadIndex + 1}{sPalletMode ? " (plt)" : ""}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredShippingRows.map((row, rowIndex) => {
                          const alt = rowIndex % 2 === 1;
                          const assignedSum = row.loads.reduce((sum, value) => sum + (value || 0), 0);
                          const displayGrandTotal = dueColumns.length ? sumForDates(row, dueColumns, "qtyByDueDate") : row.grandTotal;
                          const unassigned = displayGrandTotal - assignedSum;
                          return (
                            <tr key={`${row.rowKey}-${rowIndex}`} className={alt ? "bg-slate-200" : "bg-white"}>
                              <StickyCell alternateRow={alt} className="border-b border-r border-slate-200 px-3 py-2 font-mono text-xs font-medium" style={{ left: 0, minWidth: 110 }}>{row.sku}</StickyCell>
                              <StickyCell alternateRow={alt} className="border-b border-r border-slate-200 px-3 py-2 text-left text-xs" style={{ left: 110, minWidth: 180 }}>{row.desc}</StickyCell>
                              <StickyCell alternateRow={alt} className="border-b border-r border-slate-200 px-3 py-2 text-xs" style={{ left: 290, minWidth: 80 }}>{row.po}</StickyCell>
                              <StickyCell alternateRow={alt} className="border-b border-r border-slate-200 px-3 py-2 text-right text-xs" style={{ left: 370, minWidth: 90 }}>
                                {shippingPalletInvDisplay(row, sPalletMode)}
                              </StickyCell>
                              <StickyCell alternateRow={alt} className="border-b border-r border-slate-200 px-3 py-2 text-right text-xs" style={{ left: 460, minWidth: 70 }}>{row.boxQty}</StickyCell>
                              <StickyCell alternateRow={alt} className="border-b border-r border-slate-200 px-3 py-2 text-right text-xs" style={{ left: 530, minWidth: 90 }}>
                                {row.partsonaPallet !== "" && row.partsonaPallet != null ? row.partsonaPallet : "—"}
                              </StickyCell>
                              <StickyCell alternateRow={alt} className="border-b border-r border-slate-200 px-3 py-2 font-mono text-[11px]" style={{ left: 620, minWidth: 110 }}>{row.releaseId}</StickyCell>
                              <StickyCell
                                alternateRow={alt}
                                className={classNames(
                                  "border-b border-r border-slate-200 px-3 py-2 text-right font-semibold",
                                  sPalletMode && "bg-violet-50/90",
                                )}
                                style={{ left: 730, minWidth: 90 }}
                              >
                                {shippingLineQtyAsPalletsDisplay(displayGrandTotal, row, sPalletMode)}
                              </StickyCell>
                              <StickyCell
                                alternateRow={alt}
                                className={classNames(
                                  "border-b border-r border-slate-200 px-3 py-2 text-right font-bold",
                                  sPalletMode && "bg-violet-50/90",
                                  unassigned > 0 ? "text-red-700" : unassigned < 0 ? "text-red-700" : "text-slate-900",
                                  !sPalletMode && unassigned > 0 ? "bg-amber-50" : !sPalletMode && unassigned < 0 ? "bg-red-50" : !sPalletMode ? "bg-amber-50" : "",
                                )}
                                style={{ left: 820, minWidth: 90 }}
                              >
                                {shippingDueShipQtyDisplay(unassigned, row, sPalletMode)}
                              </StickyCell>

                              {dueColumns.map((due) => {
                                const remaining = row.remainingByDueDate?.[due] ?? row.qtyByDueDate?.[due] ?? 0;
                                return (
                                  <td
                                    key={`${row.rowKey}-${due}`}
                                    onClick={() => assignDueBucketToActiveShipment(row.rowKey, due)}
                                    className={classNames(
                                      "cursor-pointer border-b border-r border-slate-200 px-3 py-2 text-center font-semibold transition",
                                      remaining > 0
                                        ? "bg-blue-50 text-blue-900 hover:bg-blue-100"
                                        : alt
                                          ? "bg-slate-200 text-slate-500"
                                          : "bg-slate-50 text-slate-400",
                                    )}
                                  >
                                    {shippingDueShipQtyDisplay(remaining, row, sPalletMode)}
                                  </td>
                                );
                              })}

                              {dueColumns.length > 0 && (
                                <td
                                  className={classNames(
                                    "border-b border-r border-slate-200 px-3 py-2 text-center font-bold",
                                    alt ? "bg-slate-300" : "bg-slate-200",
                                  )}
                                >
                                  {shippingDueTotalRemainingDisplay(row, dueColumns, sPalletMode)}
                                </td>
                              )}

                              {Array.from({ length: sNumLoads }).map((_, loadIndex) => (
                                <td
                                  key={`${row.rowKey}-load-${loadIndex}`}
                                  className={classNames(
                                    "border-b border-r border-slate-200 px-2 py-1",
                                    loadIndex === sActiveLoad ? "bg-blue-50" : alt ? "bg-slate-200" : "bg-slate-100",
                                  )}
                                >
                                  <input
                                    type="number"
                                    min="0"
                                    value={shippingShipmentLoadDisplay(row.loads[loadIndex], row, sPalletMode)}
                                    onFocus={() => setSActiveLoad(loadIndex)}
                                    onChange={(e) => updateShipmentCell(row.rowKey, loadIndex, e.target.value)}
                                    className="w-24 rounded-lg border border-slate-200 bg-white px-2 py-1 text-right text-sm outline-none ring-0 transition focus:border-blue-500"
                                  />
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </PanelCard>
          </motion.div>
        )}

        {activePlanner === "material" && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
            <PanelCard>
              <div className="bg-slate-50 px-4 py-3">
                <div className="flex flex-wrap items-end gap-3">
                  <Field label="ComponentType">
                    <Select
                      instanceId="planner-material-component-type"
                      className="min-w-[220px] max-w-[min(100%,320px)]"
                      classNamePrefix="planner-rs-mat-component"
                      options={materialComponentTypeSelectOptions}
                      value={materialComponentTypeSelectValue}
                      onChange={(opt) => setMComponentType(opt?.value || "MATERIAL")}
                      isDisabled={mComponentTypesLoading}
                      isSearchable
                      isClearable={false}
                      placeholder={mComponentTypesLoading ? "Loading component types…" : "Select ComponentType"}
                      styles={PLANNER_CUSTOMER_SELECT_STYLES}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      aria-label="ComponentType"
                    />
                  </Field>
                  <Field label="Material">
                    <select value={mMaterial} onChange={(e) => setMMaterial(e.target.value)} className="input-field min-w-[220px]">
                      <option value="all">All materials</option>
                      {materialOptions.map((material) => <option key={material.id} value={material.id}>{material.id} — {material.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Customer">
                    <Select
                      instanceId="planner-material-customer"
                      className="min-w-[220px] max-w-[min(100%,320px)]"
                      classNamePrefix="planner-rs-mat-cust"
                      options={materialCustomerSelectOptions}
                      value={materialCustomerSelectValue}
                      onChange={(opt) => setMCustomer(opt?.value ?? "")}
                      isDisabled={!plannerCustomerListReady}
                      isSearchable
                      isClearable={false}
                      placeholder={plannerCustomerListReady ? "All customers" : "Loading customers…"}
                      styles={PLANNER_CUSTOMER_SELECT_STYLES}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      aria-label="Customer"
                    />
                  </Field>
                  <Field label="Part #">
                    <input value={mPartSearch} onChange={(e) => setMPartSearch(e.target.value)} placeholder="Filter by part…" className="input-field w-[210px]" />
                  </Field>
                  <Field label="From">
                    <input type="date" value={mDateFrom} onChange={(e) => setMDateFrom(e.target.value)} className="input-field w-[150px]" />
                  </Field>
                  <Field label="To">
                    <input type="date" value={mDateTo} onChange={(e) => setMDateTo(e.target.value)} className="input-field w-[150px]" />
                  </Field>
                  <Field label="Status">
                    <select value={mStatus} onChange={(e) => setMStatus(e.target.value)} className="input-field min-w-[160px]">
                      <option value="all">All</option>
                      <option value="shortage">Shortage risk</option>
                      <option value="replenish">Incoming PO</option>
                      <option value="safety">Below safety stock</option>
                    </select>
                  </Field>
                  <div className="ml-auto flex flex-wrap items-center gap-2">
                    <Segmented
                      value="iqmsOnHand"
                      onChange={() => {}}
                      options={[
                        { value: "demandOnHand", label: "On Hand Qty" },
                        { value: "iqmsOnHand", label: "IQMS On Hand Qty" },
                      ]}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setMComponentType(mComponentTypes.find((type) => type.toUpperCase() === "MATERIAL") || "MATERIAL");
                        setMCustomer("");
                        setMMaterial("all");
                        setMPartSearch("");
                        setMStatus("all");
                        setMDateFrom(fmtDate(baseDates[0]));
                        setMDateTo(fmtDate(baseDates.length ? baseDates[baseDates.length - 1] : new Date()));
                      }}
                      className="secondary-btn"
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </PanelCard>

            <PanelCard>
              <ToolbarSummary summary={materialSummary} legend={[
                { label: "Requirement", className: "bg-blue-100" },
                { label: "Incoming", className: "bg-sky-100" },
                { label: "Safety risk", className: "bg-amber-100" },
                { label: "Shortage", className: "bg-red-100" },
              ]} />
              <div className="max-h-[min(68vh,calc(100vh-16rem))] overflow-y-auto overflow-x-auto">
                {mWorkspaceLoading ? (
                  <div className="flex min-h-[160px] items-center justify-center text-sm text-slate-500">
                    Loading material planner workspace…
                  </div>
                ) : !materialRows.length ? (
                  <EmptyState title="Material planner" description="No workspace rows found for the selected ComponentType." />
                ) : (
                  <table className="min-w-full border-separate border-spacing-0 text-sm">
                    <thead>
                      <tr>
                        <StickyHeader left={0} width={168}>Material</StickyHeader>
                        <StickyHeader left={168} width={104} align="right">On Hand</StickyHeader>
                        <StickyHeader left={272} width={160}>Linked Parts</StickyHeader>
                        {materialDateIndices.map((idx) => (
                          <th key={idx} className="sticky top-0 z-10 min-w-[80px] border-b border-r border-slate-200 bg-slate-100 px-3 py-2 text-right text-[11px] font-bold uppercase tracking-[0.04em] text-slate-500">{fmtHeader(baseDates[idx])}</th>
                        ))}
                        <th className="sticky top-0 z-10 min-w-[96px] border-b border-r border-slate-200 bg-slate-200 px-3 py-2 text-right text-[11px] font-bold uppercase tracking-[0.04em] text-slate-700">Grand Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {materialRows.map((material, rowIndex) => {
                        const alt = rowIndex % 2 === 1;
                        let total = 0;
                        for (let i = materialRange.fromIdx; i <= materialRange.toIdx; i += 1) total += Number(material.req[i] || 0);
                        const onHand = Number(material.onHand);
                        const projectedShort = Number.isFinite(onHand) && total > onHand;
                        const belowSafety = Number.isFinite(onHand) && total > onHand * 0.7;
                        let cumulative = 0;
                        return (
                          <tr key={`${material.id}-${rowIndex}`} className={alt ? "bg-slate-200" : "bg-white"}>
                            <StickyCell alternateRow={alt} className="border-b border-r border-slate-200 px-3 py-2 font-mono text-xs font-medium" style={{ left: 0, minWidth: 168 }}>
                              {material.id}
                              {material.name ? <span className="block font-sans text-[11px] font-normal text-slate-500">{material.name}</span> : null}
                            </StickyCell>
                            <StickyCell
                              alternateRow={alt}
                              className={classNames("border-b border-r border-slate-200 px-3 py-2 text-right text-xs", projectedShort ? "bg-red-100 font-bold text-red-700" : belowSafety ? "bg-amber-100 font-semibold text-amber-700" : "")}
                              style={{ left: 168, minWidth: 104 }}
                            >
                              {fmtMaterialNumber(material.onHand)}
                            </StickyCell>
                            <StickyCell alternateRow={alt} className="border-b border-r border-slate-200 px-3 py-2" style={{ left: 272, minWidth: 160 }}>
                              <button
                                type="button"
                                onClick={() => {
                                  setMModalData(material);
                                  setMModalSearch("");
                                }}
                                className="rounded-lg border border-slate-300 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                              >
                                {material.partCount === 0 ? "No parts" : `${material.partCount} part${material.partCount === 1 ? "" : "s"}`}
                              </button>
                            </StickyCell>
                            {materialDateIndices.map((idx) => {
                              const requirement = Number(material.req[idx] || 0);
                              const incoming = Number(material.incoming[idx] || 0);
                              cumulative += requirement;
                              const after = Number.isFinite(onHand) ? onHand - cumulative + incoming : null;
                              return (
                                <td
                                  key={`${material.id}-${idx}`}
                                  className={classNames(
                                    "border-b border-r border-slate-200 px-3 py-2 text-right",
                                    requirement === 0 && incoming === 0 && (alt ? "bg-slate-200 text-slate-400" : "bg-slate-50 text-slate-400"),
                                    incoming > 0 && requirement === 0 && "bg-sky-100 text-sky-900",
                                    after !== null && after < 0 && "bg-red-50 font-semibold text-red-700",
                                    after !== null && after >= 0 && incoming > 0 && requirement > 0 && "bg-sky-50 text-sky-900",
                                    after !== null && after >= 0 && belowSafety && cumulative > onHand * 0.7 && requirement > 0 && "bg-amber-50 font-semibold text-amber-800",
                                    after !== null && after >= 0 && incoming === 0 && !(belowSafety && cumulative > onHand * 0.7) && requirement > 0 && "bg-blue-50 text-slate-800",
                                  )}
                                >
                                  {incoming > 0 && requirement === 0 ? `+${fmtMaterialNumber(incoming)}` : requirement ? fmtMaterialNumber(requirement) : "—"}
                                </td>
                              );
                            })}
                            <td className={classNames("border-b border-r border-slate-200 px-3 py-2 text-right font-semibold", alt ? "bg-slate-200" : "bg-slate-100", projectedShort && "bg-red-50 text-red-700")}>{total ? fmtMaterialNumber(total) : "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </PanelCard>
          </motion.div>
        )}
      </div>

      <Modal
        open={Boolean(mModalData)}
        title={mModalData?.id || "Linked parts"}
        subtitle={mModalData ? `${mModalData.name || ""} · ${mModalData.componentType || mComponentType || "MATERIAL"}` : ""}
        onClose={() => setMModalData(null)}
      >
        <div className="mb-4 max-w-sm">
          <label className="mb-1 block text-xs font-bold uppercase tracking-[0.08em] text-slate-500">Part number</label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={mModalSearch} onChange={(e) => setMModalSearch(e.target.value)} placeholder="Search part number in this list…" className="input-field w-full pl-9" />
          </div>
        </div>
        <div className="overflow-auto rounded-2xl border border-slate-200">
          <table className="min-w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                {linkedPartColumns.map((column) => (
                  <th key={column.key} className="sticky top-0 z-10 border-b border-r border-slate-200 bg-slate-100 px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.04em] text-slate-500">
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {modalFilteredParts.length === 0 ? (
                <tr>
                  <td colSpan={linkedPartColumns.length} className="px-4 py-8 text-center text-slate-500">
                    {mModalData?.parts?.length ? "No part numbers match your search." : "No linked parts."}
                  </td>
                </tr>
              ) : (
                modalFilteredParts.map((part, index) => (
                  <tr key={`${pickDemandRow(part, "id", "Id") ?? index}-${index}`} className={index % 2 ? "bg-slate-200" : "bg-white"}>
                    {linkedPartColumns.map((column) => {
                      const mappedPart = {
                        partNumber: pickDemandRow(part, "partNumber", "PartNumber"),
                        shotWeight: pickDemandRow(part, "materialPerPiece", "MaterialPerPiece"),
                        status: pickDemandRow(part, "componentType", "ComponentType"),
                        wtNumber: "",
                        cycleTime: "",
                        plannedQty: pickDemandRow(part, "quantity", "Quantity"),
                        totalMaterialRequired: pickDemandRow(part, "materialRequiredQty", "MaterialRequiredQty", "materialRequirement", "MaterialRequirement"),
                        plannedStartDate: pickDemandRow(part, "dueDate", "DueDate"),
                        plannedEndDate: "",
                      };
                      const value = mappedPart[column.key];
                      const displayValue = ["shotWeight", "plannedQty", "totalMaterialRequired"].includes(column.key)
                        ? fmtMaterialNumber(value)
                        : value === "" || value == null
                          ? "—"
                          : String(value);
                      return (
                        <td key={column.key} className="border-b border-r border-slate-200 px-3 py-2 font-mono text-[12px] text-slate-700">
                          {displayValue}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Modal>

      <Modal
        open={sLoadBoardModalOpen}
        panelClassName="max-w-[min(96rem,calc(100vw-2rem))]"
        title="Load board"
        subtitle={`${shippingSelectedCustomerName || "All customers"} · Ship date ${sShipDate} · ${sShipTo === "all" ? "All locations" : sShipTo} · ${sNumLoads} shipment column(s) — each tab shows the shipment form and line table for that column only (parts with quantity on that shipment).`}
        onClose={() => setSLoadBoardModalOpen(false)}
      >
        <div className="flex h-[min(72vh,720px)] max-h-[min(72vh,720px)] flex-col gap-3 overflow-hidden sm:gap-4">
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="min-w-0 text-sm text-slate-600">
              Load ASP forms downloads COMMON_ASP_Form.xlsx, fills it with this Load board’s values, then saves a new file to Loaded_ASP_Forms_Raintels (common template is never overwritten).
            </p>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleUploadAllLoadBoardExcels}
              disabled={
                sAspFormsUploading ||
                sLoadToShipmentLoading ||
                !sLoadBoardShipmentFormFetchDone ||
                !normalizedLoadBoardShipmentFormLayout
              }
              title={
                !normalizedLoadBoardShipmentFormLayout && sLoadBoardShipmentFormFetchDone
                  ? "Configure a customer ASP form before uploading ASP workbooks."
                  : undefined
              }
              className="primary-btn inline-flex shrink-0 items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Upload className="h-4 w-4" />
              {sAspFormsUploading ? "Uploading…" : "Load ASP forms"}
            </button>
            <button
              type="button"
              onClick={handleLoadToShipment}
              disabled={
                sAspFormsUploading ||
                sLoadToShipmentLoading ||
                !sLoadBoardShipmentFormFetchDone ||
                sNumLoads < 1
              }
              title={
                !sLoadBoardShipmentFormFetchDone
                  ? "Loading shipment form configuration…"
                  : sNumLoads < 1
                    ? "Add at least one shipment column first."
                    : "Save this shipment column to the shipment module (upsertShipmentsAndItems)."
              }
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border border-emerald-800 bg-emerald-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sLoadToShipmentLoading ? "Saving…" : "Load to Shipment"}
            </button>
            </div>
          </div>

          {sNumLoads > 0 && (
            <div className="flex shrink-0 flex-wrap gap-1 border-b border-slate-200 pb-0">
              {Array.from({ length: sNumLoads }, (_, i) => (
                <button
                  key={`lb-tab-${i}`}
                  type="button"
                  onClick={() => setSLoadBoardTabIndex(i)}
                  className={classNames(
                    "rounded-t-lg px-3 py-2 text-sm font-semibold transition-colors",
                    loadBoardViewLoadIndex === i
                      ? "bg-blue-800 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                  )}
                >
                  Shipment {i + 1}
                </button>
              ))}
            </div>
          )}

          <div className="max-h-[min(26vh,240px)] min-h-0 shrink-0 overflow-y-auto overflow-x-hidden rounded-xl border border-slate-200 bg-slate-50/60 p-2 shadow-inner sm:max-h-[min(28vh,260px)]">
            {!sLoadBoardShipmentFormFetchDone ? (
              <div className="flex min-h-[120px] items-center justify-center px-3 text-center text-sm text-slate-500">
                Loading shipment form configuration…
              </div>
            ) : (
              <>
                <LoadBoardShipmentFormHeader formProps={loadBoardShipmentFormProps} layout={effectiveLoadBoardShipmentFormLayout} />
                {Array.isArray(sLoadBoardHeaderRows) && sLoadBoardHeaderRows.length > 0 && (
                  <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
                    <div className="space-y-2">
                      {sLoadBoardHeaderRows.map((row, idx) => {
                        const cells = Array.isArray(row) ? row : [row];
                        return (
                          <div key={`lb-h-${idx}`} className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                            {cells.map((cell, j) => {
                              if (cell == null) return null;
                              if (typeof cell === "string") {
                                return (
                                  <div key={`lb-hc-${idx}-${j}`} className="text-sm text-slate-700">
                                    {cell}
                                  </div>
                                );
                              }
                              if (typeof cell === "object") {
                                const label = cell.label != null ? String(cell.label) : "";
                                const value =
                                  cell.value != null
                                    ? String(cell.value)
                                    : cell.fieldKey
                                      ? String(resolveLoadBoardHeaderValue(cell.fieldKey))
                                      : "";
                                return (
                                  <div key={`lb-hc-${idx}-${j}`} className="flex items-baseline justify-between gap-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                                    <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500">{label || " "}</div>
                                    <div className="text-sm font-semibold text-slate-900">{value || "—"}</div>
                                  </div>
                                );
                              }
                              return null;
                            })}
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-2 text-xs text-slate-500">Excel header (from schema) shown above the table.</div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-slate-200 bg-white">
            {!filteredShippingRows.length ? (
              <div className="p-8 text-center text-sm text-slate-500">No lines match the current filters. Adjust filters or due-date slicers, then open again.</div>
            ) : !loadBoardModalTableRows.length ? (
              <div className="p-8 text-center text-sm text-slate-500">
                No parts with quantity on Shipment {loadBoardViewLoadIndex + 1}. Assign quantities to this shipment column in the planner grid below, or switch to another tab.
              </div>
            ) : (
              <table className="w-max min-w-full border-separate border-spacing-0 text-sm">
                <thead>
                  <tr>
                    {loadBoardTableColumns.map((col, idx) => {
                      const label = String(col?.label || col?.header || col?.fieldKey || col?.key || `Column ${idx + 1}`);
                      const align = col?.align === "right" ? "right" : col?.align === "center" ? "center" : "left";
                      const group = col?.group ? String(col.group) : "";
                      const isApproval = group.toUpperCase() === "APPROVAL";
                      return (
                        <th
                          key={`${label}-${idx}`}
                          className={classNames(
                            "sticky top-0 z-[1] min-w-[96px] border-b border-r border-slate-200 px-2 py-2",
                            isApproval ? "bg-slate-200" : "bg-slate-100",
                          )}
                        >
                          {group ? (
                            <div className={classNames("mb-1 text-[9px] font-extrabold uppercase tracking-[0.12em] text-slate-500", isApproval && "text-slate-700")}>
                              {group}
                            </div>
                          ) : null}
                          <div
                            className={classNames(
                              "text-[11px] font-bold uppercase tracking-[0.04em] text-slate-700",
                              align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left",
                            )}
                          >
                            {label}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {loadBoardModalTableRows.map((row, rowIndex) => {
                    return (
                      <tr key={`lb-${row.rowKey}-${rowIndex}`} className={rowIndex % 2 ? "bg-slate-200" : "bg-white"}>
                        {loadBoardTableColumns.map((col, idx) => {
                          const align = col?.align === "right" ? "right" : col?.align === "center" ? "center" : "left";
                          const group = col?.group ? String(col.group) : "";
                          const isApproval = group.toUpperCase() === "APPROVAL";
                          const fieldKey = String(col?.fieldKey || col?.key || "").trim();
                          const isReadOnly = !fieldKey || LOAD_BOARD_READONLY_FIELD_KEYS.has(fieldKey);
                          const v = resolveLoadBoardDisplayCell(col, row, rowIndex);
                          const displayText = v === "" || v == null ? "—" : String(v);
                          return (
                            <td
                              key={`lb-dyn-${row.rowKey}-${rowIndex}-${idx}`}
                              className={classNames(
                                "border-b border-r border-slate-200 px-2 py-1 text-xs",
                                align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left",
                                isApproval && "bg-slate-50",
                              )}
                            >
                              {isReadOnly ? (
                                displayText
                              ) : (
                                <input
                                  type="text"
                                  value={displayText === "—" ? "" : displayText}
                                  onChange={(e) => updateLoadBoardCell(row.rowKey, fieldKey, e.target.value)}
                                  onBlur={(e) => updateLoadBoardCell(row.rowKey, fieldKey, e.target.value)}
                                  className={classNames(
                                    "w-full min-w-[72px] rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs outline-none ring-0 transition focus:border-blue-500",
                                    align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left",
                                  )}
                                />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Modal>

      <AspSettingsModal
        open={aspSettingsOpen}
        onClose={() => setAspSettingsOpen(false)}
        onStoreChanged={() => {
          refreshAspMappingsList();
          syncAspStoreToBackend();
        }}
        toast={(message, variant) =>
          addToast(
            message,
            variant === "error" ? "error" : variant === "warn" ? "warn" : "success",
          )
        }
        plannerCustomerKey={activePlanner === "shipping" ? sCustomer : ""}
        plannerShipTo={activePlanner === "shipping" ? sShipTo : "all"}
      />

      <ShipmentTruckOverflowDialog
        open={Boolean(sTruckOverflowPending)}
        shipmentLabel={sTruckOverflowPending?.shipmentLabel}
        onCancel={cancelTruckOverflowPending}
        onConfirm={confirmTruckOverflowPending}
      />

      <ToastStack toasts={toasts} onDismiss={(id) => setToasts((prev) => prev.filter((item) => item.id !== id))} />

      <style>{`
        .input-field {
          height: 38px;
          border-radius: 0.85rem;
          border: 1px solid rgb(203 213 225);
          background: white;
          padding: 0 0.9rem;
          font-size: 0.875rem;
          color: rgb(15 23 42);
          outline: none;
          transition: box-shadow .15s ease, border-color .15s ease;
        }
        .input-field:focus {
          border-color: rgb(37 99 235);
          box-shadow: 0 0 0 3px rgba(37,99,235,0.12);
        }
        .primary-btn, .secondary-btn, .accent-btn {
          height: 38px;
          border-radius: 0.85rem;
          padding: 0 0.95rem;
          font-size: 0.8125rem;
          font-weight: 700;
          transition: all .15s ease;
        }
        .primary-btn {
          background: rgb(29 78 216);
          color: white;
        }
        .primary-btn:hover {
          background: rgb(30 64 175);
        }
        .secondary-btn {
          border: 1px solid rgb(203 213 225);
          background: white;
          color: rgb(71 85 105);
        }
        .secondary-btn:hover {
          background: rgb(248 250 252);
          color: rgb(15 23 42);
        }
        .accent-btn {
          border: 1px solid rgb(226 232 240);
          background: linear-gradient(180deg, #fafbfc 0%, #f1f5f9 100%);
          color: rgb(30 41 59);
        }
        .accent-btn:hover {
          background: rgb(248 250 252);
        }
      `}</style>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5 text-xs font-bold uppercase tracking-[0.08em] text-slate-500">
      <span>{label}</span>
      {children}
    </label>
  );
}

function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex rounded-2xl border border-slate-200 bg-white p-1">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={classNames(
            "rounded-xl px-3 py-2 text-xs font-semibold transition",
            value === option.value ? "bg-blue-700 text-white" : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

function ToggleButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "rounded-xl border px-3 py-2 text-xs font-semibold transition",
        active ? "border-blue-700 bg-blue-700 text-white" : "border-blue-200 bg-sky-50 text-blue-900 hover:border-blue-300 hover:bg-sky-100"
      )}
    >
      {children}
    </button>
  );
}

function ToolbarSummary({ summary, legend = [], center }) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 bg-gradient-to-b from-slate-50 to-slate-100 px-4 py-3">
      <p className="min-w-0 shrink-0 text-sm text-slate-600">{summary}</p>
      <div className="flex min-w-0 flex-1 flex-wrap items-center justify-center gap-2">{center}</div>
      <div className="flex shrink-0 flex-wrap items-center gap-4 text-xs text-slate-500">
        {legend.map((item) => (
          <span key={item.label} className="inline-flex items-center gap-2">
            <i className={classNames("h-3.5 w-3.5 rounded border border-slate-200", item.className)} />
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ title, description }) {
  return (
    <div className="px-6 py-14 text-center text-sm text-slate-500">
      <strong className="mb-2 block text-base font-semibold text-slate-900">{title}</strong>
      <p>{description}</p>
    </div>
  );
}

function StickyHeader({ left, width, align = "left", children }) {
  return (
    <th
      className={classNames(
        "sticky top-0 z-20 border-b border-r border-slate-200 bg-slate-200 px-2 py-2 text-[11px] font-bold uppercase tracking-[0.04em] text-slate-600",
        align === "right" ? "text-right" : "text-left"
      )}
      style={width != null ? { left, minWidth: width, width } : { left }}
    >
      {children}
    </th>
  );
}
